import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { clsx } from "clsx";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import {
  AlertTriangle, ArrowDown, ArrowUp, BookOpen, CheckCircle2, ChevronDown, ChevronRight, ChevronUp, ChevronsUpDown, CircleDot, Crosshair, Download, ExternalLink,
  FileSearch, FileSignature, FileSpreadsheet, FileStack, FileUp, Filter, FileText, Gavel, Globe, HardHat, History, Info, KeyRound, Link2, LandPlot, type LucideIcon, MapPin, Megaphone, MessageSquare, Paperclip, Pencil, RefreshCw, Route, Ruler,
  Check, Send, ShieldCheck, Sparkles, Table2, Trash2, Unlink, Upload, UserPlus, Users, X,
} from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { Z7_CONSENT_FORMS } from "./z7/z7types";
import { groupOwnersForDisplay } from "../ownerDisplay";
import { MpzpAdvicePanel } from "./mpzp/MpzpAdvicePanel";
import { MpzpParcelOrdersSection } from "./mpzp/MpzpParcelOrdersSection";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import {
  useAccessBasisTypes,
  useGeneratedDocs,
  useLinkableOrders,
  useOffices,
  useSelectableOffices,
  useOrderLinkMutations,
  useOwnerAccessMutations,
  useOwnerMutations,
  useAttachableLetters,
  useNotificationMutations,
  useParcel,
  useParcelMutations,
  useParcelNoteMutations,
  useParcelNotes,
  useParcelRouteSegments,
  useOwnerMismatches,
  useParcels,
  useProceedings,
  useMyProjectGate,
  useProjectEvents,
  useRegulatoryChecklist,
  useTaskPolling,
  useWpwHandovers,
  useWpwHandoverMutations,
  useWypisy,
  useWypisMutations,
  useParcelOutdatedOwners,
} from "../hooks";
import { ParcelStatusMatrixTable } from "./ParcelStatusMatrixTable";
import { checkLandClassUnderDevices, fetchAdminData, fetchLandClasses, fetchOwnershipCodes, importOwnersCsv, importOwnersCsvBulk, startGlobalParcelsXlsx } from "../api";
import { triggerBrowserDownload } from "@/utils/download";
import { isRegulacjaOperator } from "../operatorTemplates";
import { GenerateConsentModal } from "./GenerateConsentModal";
import { GenerateRegulatoryApplicationModal } from "./GenerateRegulatoryApplicationModal";
import { RegulatoryAttachmentsPanel } from "./RegulatoryAttachmentsPanel";
import { KwAnalysisModal } from "./KwAnalysisModal";
import { OwnerMailBlockControl, ParcelMailBlockControl, WpwMailLettersPanel } from "./WpwMailLettersPanel";
import { OfficeFormModal } from "@/features/paper_send/pages/OfficesPage";
import { ORDER_STATUS_LABELS, type OrderStatus } from "@/features/orders/types";
import {
  ORDER_TYPE_LABEL,
  OWNER_ROLE_OPTIONS,
  PARCEL_ACCESS_MODE_BADGE,
  PARCEL_ACCESS_MODE_OPTIONS,
  WPW_ORDER_TYPE_OPTIONS,
  type KwRoadRow,
  type OwnerAccess,
  type OwnerRole,
  type Parcel,
  type ParcelAccessMode,
  type ParcelAccessSummary,
  type ParcelExportResult,
  type OdrolnienieOverride,
  type ParcelNote,
  type ParcelOdrolnienie,
  type ParcelOpenProceeding,
  type ParcelOwner,
  type ParcelStatus,
  type PowerProject,
  type Proceeding,
  type AttachableLetter,
  type ProceedingItem,
  type WpwHandover,
  type WpwHandoverFile,
  type WypisDetachSummary,
  type WypisExtractionProposal,
  type WypisFile,
} from "../types";
import { taskErrorTail } from "./wypisy/ExtractionNotes";
import { ExtractionReviewModal } from "./wypisy/ExtractionReviewModal";
import { OwnerOfficePickerModal, type PickedOffice } from "./wypisy/OwnerOfficePickerModal";
import { apiErr, fmtArea, fmtDate, fmtDateTime, isPerpetualUsufructCode, isProjectParcel } from "../utils";
import {
  ApplicationStatusBadge,
  AttachmentLinks,
  DISPATCH_LABEL,
  EmptyState,
  KpaBadge,
  MultiFileInput,
  OutcomeBadge,
  OwnerAccessBadge,
  PaperDispatchLine,
  PrintOrderLine,
  ParcelStatusBadge,
  ProceedingScopeBadge,
  ProceedingStatusBadge,
  StaleBadge,
  TableShell,
  Th,
  ownerAccessMark,
  parcelDotColor,
  usePersistedFlag,
} from "./ui";
import { ParcelRouteMiniMap } from "./ParcelRouteMiniMap";
import { SuggestStreetModal } from "./SuggestStreetModal";
import { ProjectEventList } from "./ProgressTimelineTab";
import { WpwMassLinks } from "./WpwMassLinks";
import { OwnerTitleDocuments } from "./OwnerTitleDocuments";
import { JstAssignModal } from "./JstAssignModal";
import { LandClassSourceModal } from "./LandClassSourceModal";
import { DeviceFootprintRatesPanel } from "./DeviceFootprintRatesPanel";
import { WpwConversationModal, WpwStatusBadge } from "./WpwConversationModal";
import { WpwFilesInline } from "./WpwHandoverPanel";

// WPW declared statuses worth surfacing in the list/map: the negotiator is mid-conversation
// ("w trakcie" / "zebrano w terenie" — the scan still to come) or hit a wall ("problem").
// Acquired/idle don't need a table flag.
const WPW_FLAG_STATUSES = new Set<WpwHandover["declared_status"]>([
  "in_progress", "collected_in_field", "problem_refusal", "problem_impossible",
]);

const STATUS_OPTIONS: ParcelStatus[] = [
  "not_started", "in_progress", "action_required", "application_sent",
  "negotiator_sent", "ok", "problem",
];
const STATUS_LABEL: Record<ParcelStatus, string> = {
  not_started: "Nie rozpoczęto",
  in_progress: "W trakcie",
  action_required: "W trakcie – wymagane działanie",
  application_sent: "Wniosek wysłany",
  negotiator_sent: "Negocjator wysłany",
  ok: "Uzgodniona",
  problem: "Problem",
};

// Requisition (Zapotrzebowanie) status → chip colour, for the WPW section list.
const REQ_STATUS_COLOR: Record<string, string> = {
  draft: "#6b7280",
  submitted: "#d97706",
  approved: "#059669",
  rejected: "#dc2626",
};

// WPW order status → chip colour, mirroring the Badge palette in OrdersListPage so the
// linked-order chip reads the same here as on the orders list.
const ORDER_STATUS_COLOR: Record<string, string> = {
  sent: "#2563eb",
  accepted: "#059669",
  rejected: "#dc2626",
  in_progress: "#ea580c",
  in_progress_partial: "#d97706",
  submitted_for_acceptance: "#7c3aed",
  accepted_final: "#059669",
  closed: "#6b7280",
  cancelled: "#6b7280",
};

// Deleting a parcel cascades to its owners, their access titles and any order links
// (map objects survive — only their parcel link is cleared). Warn about that, and that
// a route-derived parcel can reappear on the next DXF recompute.
function confirmParcelDelete(p: Parcel): boolean {
  const extras: string[] = [];
  if (p.owners_total > 0) extras.push(`właścicieli (${p.owners_total}) wraz z tytułami dostępu`);
  if (p.linked_orders.length > 0) extras.push(`powiązania z zamówieniami (${p.linked_orders.length})`);
  const tail = extras.length ? ` Usunięte zostaną również: ${extras.join(" i ")}.` : "";
  const routeWarn =
    !p.is_manual && p.in_current_route
      ? " Uwaga: działka leży na trasie i może pojawić się ponownie po przeliczeniu DXF."
      : "";
  return window.confirm(`Usunąć działkę „${p.teryt}"?${tail}${routeWarn}`);
}

// ── „wykorzystywana w projekcie" — automat + ręczne rozstrzygnięcie ──────────
// Automat czyta trasę i obiekty z DXF-a (trasa przez działkę, obiekt na działce lub przy
// jej granicy, linia w osi granicy; linia wzdłuż granicy po stronie sąsiada trzyma działkę
// w projekcie, ale jej nie wykorzystuje),
// a te nigdy nie pokrywają się z ewidencją co do centymetra: raz
// wciągnie sąsiadkę spod granicy, raz nie złapie działki, którą projekt realnie zajmuje.
// Ręczne oznaczenie bije automat w obie strony i przeżywa przeliczenie działek.
// Zdjęcie działki z projektu ma skutki (wypada z domyślnego filtra listy, z postępu
// i z mapy), więc pytamy o potwierdzenie; oznaczenie jako wykorzystywana i powrót do
// automatu idą bez pytania.
const USAGE_TITLE_AUTO =
  "Wykorzystanie liczone automatycznie: trasa przebiega przez działkę, biegnie w osi jej "
  + "granicy (do 10 cm) albo stoi na niej (lub przy jej granicy) obiekt. Linia trasy "
  + "wzdłuż granicy, ale po stronie sąsiada, nie liczy się jako wykorzystanie.";
const USAGE_TITLE_MANUAL = "Ustawione ręcznie — automat (trasa/obiekty) tego nie zmieni.";

function confirmParcelUnused(p: Parcel): boolean {
  return window.confirm(
    `Oznaczyć działkę „${p.teryt}" jako NIEWYKORZYSTYWANĄ w projekcie?\n\n`
    + "Zniknie z domyślnego widoku listy działek, przestanie się liczyć do postępu "
    + "projektu i nie będzie pokazywana na mapie jako działka projektu.\n"
    + "Oznaczenie przeżyje przeliczenie działek z DXF — cofniesz je tym samym przyciskiem.",
  );
}

// Wariant do wiersza tabeli: jeden mały znacznik-przycisk. „niewykorzystywana" widać
// zawsze (to wyjątek), „używana" dopiero pod kursorem, żeby nie zaśmiecać listy, na
// której i tak domyślnie są same działki wykorzystywane.
function ParcelUsageMark({ parcel: p, pending, onSet }: {
  parcel: Parcel;
  pending: boolean;
  onSet: (value: boolean | null) => void;
}) {
  const manual = p.usage_override !== null;
  const cls = "inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide px-1 py-px border transition-colors disabled:opacity-50";
  if (!p.is_used) {
    return (
      <button
        type="button"
        disabled={pending}
        onClick={(e) => { e.stopPropagation(); onSet(true); }}
        title={`${manual ? USAGE_TITLE_MANUAL : "Trasa nie przebiega przez działkę i nie stoi na niej żaden obiekt — działka zamówiona w wypisie „na zapas”."}\nKliknij, aby oznaczyć jako wykorzystywaną w projekcie.`}
        className={clsx(cls, "border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]")}
      >
        niewykorzystywana{manual && " ✎"}
      </button>
    );
  }
  return (
    <button
      type="button"
      disabled={pending}
      onClick={(e) => { e.stopPropagation(); if (confirmParcelUnused(p)) onSet(false); }}
      title={`${manual ? USAGE_TITLE_MANUAL : USAGE_TITLE_AUTO}\nKliknij, aby oznaczyć jako NIEwykorzystywaną w projekcie.`}
      className={clsx(
        cls,
        "border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-amber-400 hover:text-amber-700",
        manual ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus:opacity-100",
      )}
    >
      używana{manual && " ✎"}
    </button>
  );
}

// Wariant do karty działki: checkbox + znacznik auto/ręcznie + powrót do automatu
// (tri-stan: true / false ręcznie, null = wynik z trasy i obiektów).
function ParcelUsageControl({ parcel: p, pending, onSet }: {
  parcel: Parcel;
  pending: boolean;
  onSet: (value: boolean | null) => void;
}) {
  const manual = p.usage_override !== null;
  return (
    <div className="flex flex-col gap-1">
      <label
        className={clsx("flex items-start gap-2 text-xs cursor-pointer", pending && "opacity-60")}
        title={manual ? USAGE_TITLE_MANUAL : USAGE_TITLE_AUTO}
      >
        <input
          type="checkbox"
          className="mt-0.5 accent-[var(--color-primary)]"
          checked={p.is_used}
          disabled={pending}
          onChange={(e) => {
            if (e.target.checked) onSet(true);
            else if (confirmParcelUnused(p)) onSet(false);
          }}
        />
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="font-medium">Wykorzystywana w projekcie</span>
          <span
            className={clsx(
              "text-[9px] font-mono uppercase tracking-wide px-1 py-px border",
              manual
                ? "border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
                : "border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
            )}
          >
            {manual ? "ręcznie" : "auto · z trasy"}
          </span>
          {manual && (
            <button
              type="button"
              className="section-label text-[var(--color-primary)] hover:underline"
              disabled={pending}
              onClick={(e) => { e.preventDefault(); onSet(null); }}
            >
              przywróć auto
            </button>
          )}
        </span>
      </label>
      <span className="pl-6 text-[11px] text-[var(--color-text-muted)]">
        {manual
          ? "Ustawione ręcznie — „przywróć auto” wraca do wyniku z trasy i obiektów na działce."
          : "Wynik automatyczny (trasa / obiekty na działce); odznaczenie nadpisze go ręcznie."}
      </span>
    </div>
  );
}

// Działka drogowa: domyślnie wg wpisów drogowych z KW, koordynator może to przykryć
// („księga mówi droga, a to pole"). Od drogi zależy skrót automatu: droga + ponad 50%
// udziałów = „Uzgodniona". Wpisy z księgi zostają widoczne zawsze — przy „nie droga"
// przekreślone, po powrocie do „droga" wracają bez ponownej analizy KW.
const ROAD_KW_LABEL = (r: KwRoadRow) =>
  `${r.section ? `[${r.section}] ` : ""}${r.kind}${r.description ? ` — ${r.description}` : ""}`;

export function ParcelRoadControl({ parcel: p, pending, onSet }: {
  parcel: Parcel;
  pending: boolean;
  onSet: (value: boolean | null, note: string) => void;
}) {
  const road = p.road;
  if (!road) return null;
  const canSet = !!p.can_set_road;
  const manual = road.source === "manual";
  const kwKnown = p.kw_summary.state === "ok" || p.kw_summary.state === "issues";
  const ask = (value: boolean) => {
    const note = window.prompt(
      `Oznaczyć działkę „${p.teryt}" jako ${value ? "DROGĘ" : "NIE drogę"}?\n\n`
      + (value
        ? "Przy drodze status „Uzgodniona” wystarczy przy ponad 50% udziałów z pozyskanym tytułem."
        : "Działka przestanie korzystać ze skrótu „droga + ponad 50% udziałów” — "
          + "do uzgodnienia potrzebny będzie komplet tytułów.")
      + "\n\nUzasadnienie (opcjonalnie):",
      "",
    );
    if (note !== null) onSet(value, note.trim());
  };
  const byWhom = [road.override_by_name, road.override_at ? fmtDate(road.override_at) : ""]
    .filter(Boolean)
    .join(", ");
  const explain = manual
    ? `Decyzja koordynatora${byWhom ? ` (${byWhom})` : ""}: `
      + (road.is_road
        ? `droga${road.kw_is_road ? "" : " — księga tego nie wskazuje"}.`
        : `nie droga${road.kw_is_road ? " mimo wpisu w KW" : ""}.`)
    : road.kw_is_road
      ? "Wg księgi wieczystej. Przy drodze „Uzgodniona” wystarczy przy ponad 50% udziałów."
      : kwKnown
        ? "Księga wieczysta nie wskazuje drogi."
        : "Brak analizy KW — nie wiadomo, czy to droga.";
  return (
    <div className="flex flex-col gap-1">
      <label
        className={clsx("flex items-start gap-2 text-xs", canSet ? "cursor-pointer" : "opacity-60", pending && "opacity-60")}
        title={canSet
          ? "Zaznaczenie / odznaczenie przykrywa werdykt z księgi wieczystej. Wpisy z KW zostają."
          : "O tym, czy działka jest drogą, rozstrzyga koordynator przedsięwzięcia albo dyrektor."}
      >
        <input
          type="checkbox"
          className="mt-0.5 accent-[var(--color-primary)]"
          checked={road.is_road}
          disabled={!canSet || pending}
          onChange={(e) => ask(e.target.checked)}
        />
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="font-medium">Działka drogowa</span>
          <span
            className={clsx(
              "text-[9px] font-mono uppercase tracking-wide px-1 py-px border",
              manual
                ? "border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
                : "border-[var(--color-border)] text-[var(--color-text-muted)]",
            )}
          >
            {manual ? "decyzja koordynatora" : "wg KW"}
          </span>
          {road.is_road && road.internal && (
            <span className="text-[9px] uppercase tracking-wide px-1 py-px border border-red-300 bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300">
              wewnętrzna (KW)
            </span>
          )}
          {manual && canSet && (
            <button
              type="button"
              className="section-label text-[var(--color-primary)] hover:underline"
              disabled={pending}
              onClick={(e) => { e.preventDefault(); onSet(null, ""); }}
            >
              przywróć wg KW
            </button>
          )}
        </span>
      </label>
      <span className="pl-6 text-[11px] text-[var(--color-text-muted)]">
        {explain}
        {manual && road.note ? ` Uzasadnienie: ${road.note}` : ""}
      </span>
      {road.entries.length > 0 && (
        <ul
          className={clsx(
            "pl-6 text-[11px] list-none",
            road.is_road ? "text-[var(--color-text)]" : "line-through text-[var(--color-text-muted)]",
          )}
          title={road.is_road ? "Wpisy o drogach w księdze wieczystej" : "Wpisy o drogach w KW — pominięte decyzją koordynatora"}
        >
          {road.entries.map((r, i) => (
            <li key={i} className={r.internal && road.is_road ? "text-red-700 dark:text-red-300" : undefined}>
              {ROAD_KW_LABEL(r)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── drobne klocki układu: grupa paska opcji, nazwana grupa w szufladzie ─────
function ToolbarGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5 px-3 py-2 border-r border-[var(--color-border)] last:border-r-0">
      <span className="section-label">{label}</span>
      <div className="flex flex-wrap items-center gap-1.5">{children}</div>
    </div>
  );
}

/** Nazwana grupa danych w szufladzie działki / karcie właściciela — nagłówek jak
 *  SectionCard, ciasny korpus. `bodyClassName="p-0"` dla siatki komórek meta. */
function DrawerGroup({
  title, icon: Icon, action, children, className, bodyClassName,
}: {
  title: string;
  icon?: LucideIcon;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={clsx("card flex flex-col", className)}>
      <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
        <h4 className="inline-flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--color-text-secondary)]">
          {Icon && <Icon size={12} />}
          {title}
        </h4>
        {action}
      </div>
      <div className={clsx("flex flex-col gap-2 p-3", bodyClassName)}>{children}</div>
    </section>
  );
}

const plSprawy = (n: number) =>
  n === 1 ? "sprawa" : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? "sprawy" : "spraw";
const plUwagi = (n: number) =>
  n === 1 ? "uwaga" : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? "uwagi" : "uwag";

/** Gruby czerwony „!" przy statusie działki: są na niej NIEZAKOŃCZONE sprawy (status
 *  liczy się z tytułów dostępu, więc działka bywa „Uzgodniona" mimo wiszącego ULICP).
 *  Link prowadzi do zakładki Sprawy z podświetloną sprawą; dymek wylicza wszystkie. */
function OpenCasesLink({
  cases, pathname, className,
}: {
  cases?: ParcelOpenProceeding[];
  pathname: string;
  className?: string;
}) {
  if (!cases || cases.length === 0) return null;
  const title =
    `Niezakończone sprawy (${cases.length}):\n` +
    cases.map((c) => `• ${c.label}`).join("\n") +
    "\n\nKliknij → zakładka Sprawy z podświetloną sprawą.";
  return (
    <Link
      to={`${pathname}?tab=correspondence&proceeding=${cases[0].id}`}
      onClick={(e) => e.stopPropagation()}
      title={title}
      aria-label={title}
      className={clsx(
        "inline-flex items-center gap-1 font-mono text-[11px] font-bold text-red-600 hover:underline whitespace-nowrap",
        className,
      )}
    >
      <span className="text-[15px] font-black leading-none">!</span>
      {cases.length} {plSprawy(cases.length)}
    </Link>
  );
}

// ── per-owner access title ("Tytuł dostępu") ─────────────────────────────────
// The access title is per-owner: it carries the route version it was obtained for,
// shows a "nieaktualna wersja" badge when SECURED on a non-current version, and offers
// "Zaakceptuj uzgodnienie dla aktualnej trasy". Office-owners are resolved through OFFICE
// proceedings (art.124) — the title is still per-owner, set when the decyzja secures them.

// Same code as backend services.OWNER_CONSENT_BASIS_CODE („Uzgodnienie pisemne właściciela").
const OWNER_CONSENT_BASIS_CODE = "zgoda_wlasciciela";

type OwnerPrefill = Pick<ParcelOwner,
  "street" | "building_no" | "postal_code" | "city" | "phone" | "email" | "ownership_share"
>;

function OwnerCard({
  owner,
  siblings,
  connectionId,
  regulacja,
  onCopyFrom,
  onAssignOffice,
}: {
  owner: ParcelOwner;
  /** Wszyscy właściciele tej działki — do łączenia w małżeństwo (jeden podpis). */
  siblings: ParcelOwner[];
  connectionId: string;
  /** Projekt TAURON-owy — tylko tam wniosek regulacyjny (OMI) ma sens. */
  regulacja: boolean;
  onCopyFrom: (prefill: OwnerPrefill) => void;
  /** Instytucja wpisana samą nazwą (bez rekordu urzędu) — skrót do dopasowania urzędu. */
  onAssignOffice?: () => void;
}) {
  // Per-owner: the negotiator is "in field" for THIS owner only (an active WPW handover they
  // started) — a co-owner's WPW must not flag this one as "w trakcie / negocjator w terenie".
  const inField = owner.in_field;
  const access = owner.access;
  // Poprawka danych urzędu WPROST z karty właściciela — literówkę w nazwie, zły adres czy
  // numer konta widać właśnie tutaj, a nie w rejestrze. Bez tego skrótu jedynym wyjściem
  // było założenie duplikatu urzędu, bo do rejestru trzeba było umieć trafić (i mieć DLC_02).
  const [editingOffice, setEditingOffice] = useState(false);
  const { data: allOffices } = useOffices();
  const officeRecord = owner.office ? (allOffices ?? []).find((o) => o.id === owner.office) : undefined;
  const m = useOwnerAccessMutations(connectionId);
  const hm = useWpwHandoverMutations();
  const owners = useOwnerMutations(owner.parcel, connectionId);
  // Wspólność małżeńska (jeden podpis): współmałżonek = inny właściciel tej działki z tą
  // samą niepustą marital_group. Ręczna poprawka źle odczytanego wypisu (osoba z
  // sąsiedniej pozycji Lp zgarnięta do małżeństwa, para rozłączona) bez ponownej ekstrakcji;
  // udział samodzielny obok wspólności (techniczny duplikat osoby) to pole own_share.
  const spouses = siblings.filter(
    (s) => s.id !== owner.id && !!owner.marital_group && s.marital_group === owner.marital_group,
  );
  const spouseOptions = siblings.filter(
    (s) => s.id !== owner.id && s.kind === "person" && !s.office && !s.marital_group,
  );
  const [spouseChoice, setSpouseChoice] = useState("");
  const joinError = (owners.joinMarital.error as
    | { response?: { data?: { errors?: { spouse?: string[] } } } }
    | null)?.response?.data?.errors?.spouse?.[0];
  const { data: bases } = useAccessBasisTypes(true);
  const [securing, setSecuring] = useState(false);
  const [basisId, setBasisId] = useState("");
  // Forma zgody ENERGA (Załącznik 7) — domyślka z mapowania wybranej podstawy,
  // zapisywana na tytule; ewidencja Z7 czyta ją bez ponownego pytania usera.
  const [z7Form, setZ7Form] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  // „Dodaj uzgodnienie" starts from the owner's written consent (the usual basis) — the
  // picker has no empty option, so the form must never sit on "" once bases load.
  const defaultBasis = (bases ?? []).find((b) => b.code === OWNER_CONSENT_BASIS_CODE) ?? bases?.[0];
  useEffect(() => {
    if (securing && !basisId && defaultBasis) {
      setBasisId(defaultBasis.id);
      setZ7Form(defaultBasis.z7_consent_form ?? "");
    }
  }, [securing, basisId, defaultBasis]);
  const [addingDocs, setAddingDocs] = useState(false);
  const [extraFiles, setExtraFiles] = useState<File[]>([]);
  const [editing, setEditing] = useState(false);
  const [ef, setEf] = useState({
    full_name: owner.full_name, street: owner.street, building_no: owner.building_no,
    postal_code: owner.postal_code, city: owner.city, pesel_nip: owner.pesel_nip,
    phone: owner.phone, email: owner.email, ownership_share: owner.ownership_share,
    own_share: owner.own_share, role: owner.role ?? "",
  });
  // Private-consent document generation (kind=person owners only): modal + the
  // latest kept render ("pobierz ostatnio wygenerowany" — only the newest survives).
  const isPrivate = owner.kind === "person";
  const [genOpen, setGenOpen] = useState(false);
  const { data: genDocs } = useGeneratedDocs("private_consent", [owner.id], isPrivate);
  const lastGen = genDocs?.[0];
  // Regulatory application (OMI) — every owner (person or institution/KOWR), ale tylko
  // w projektach TAURON. W ENERGA pokazujemy wyłącznie wtedy, gdy wniosek już powstał
  // (dostęp do pliku), bez możliwości generowania kolejnych.
  const [regOpen, setRegOpen] = useState(false);
  // Notatki o tym właścicielu — ten sam wątek co notatki działki, tu zawężony do niego.
  const [notesOpen, setNotesOpen] = useState(false);
  const { data: parcelNotes } = useParcelNotes(owner.parcel);
  const ownerNotes = useMemo(
    () => (parcelNotes ?? []).filter((n) => n.owner === owner.id),
    [parcelNotes, owner.id],
  );
  const { data: regDocs } = useGeneratedDocs("regulatory_application", [owner.id]);
  const lastReg = regDocs?.[0];
  const efField = (k: keyof typeof ef, label: string, placeholder?: string) => (
    <label className="block">
      <span className="section-label">{label}</span>
      <input
        className="input-base text-xs py-1 mt-1"
        placeholder={placeholder}
        value={ef[k]}
        onChange={(e) => setEf((s) => ({ ...s, [k]: e.target.value }))}
      />
    </label>
  );
  const saveEdit = () => {
    owners.update.mutate({ id: owner.id, payload: ef }, { onSuccess: () => setEditing(false) });
  };
  const isOffice = !!owner.office;
  // "W trakcie" is derived, not manual: a not-started title shows IN_PROGRESS once the owner
  // sits in an OPEN case on this parcel (in_open_case) or the negotiator has started in the
  // field (in_field). Same rule as the "Podstawa dostępu" column of the status matrix.
  const titleStatus = access?.status ?? "not_started";
  // A PROBLEM title (zgon, niemożliwe zebranie podpisu) with an OPEN office case (wniosek w
  // urzędzie) reads as IN_PROGRESS too — the parcel auto-status follows the application then.
  // The stored title stays PROBLEM, so closing the case without a decision brings it back.
  const displayStatus =
    (titleStatus === "not_started" && (inField || owner.in_open_case)) ||
    (titleStatus === "problem" && owner.in_open_office_case)
      ? "in_progress"
      : titleStatus;

  // Ensure an access title exists before acting on it (created lazily by the backend
  // on add, but be defensive if an older owner row lacks one).
  const ensure = (cb: (id: string) => void) => {
    if (access) return cb(access.id);
    m.create.mutate(
      { ownerId: owner.id },
      { onSuccess: (a: OwnerAccess) => cb(a.id) },
    );
  };

  const doSecure = () => {
    if (!access || !basisId) return;
    m.secure.mutate(
      { id: access.id, basis: basisId, files, z7_consent_form: z7Form },
      { onSuccess: () => { setSecuring(false); setBasisId(""); setZ7Form(""); setFiles([]); } },
    );
  };
  const addDocs = () => {
    if (extraFiles.length === 0) return;
    // ensure(): the access title is created lazily — uploading documents may be the
    // owner's very first action, before any title row exists.
    ensure((id) =>
      m.uploadFiles.mutate(
        { id, files: extraFiles },
        { onSuccess: () => { setAddingDocs(false); setExtraFiles([]); } },
      ),
    );
  };
  const reportProblem = () => {
    const note = window.prompt("Opis problemu (teren / właściciel):", "");
    if (note) ensure((id) => m.reportProblem.mutate({ id, note }));
  };
  const setStatus = (to: string) => ensure((id) => m.transition.mutate({ id, to_status: to }));

  // Dokumenty właściciela (skany tytułu + ostatnio wygenerowane pisma) — jedna zwijana
  // grupa pod kartą, żeby karta nie puchła przy każdym właścicielu.
  const [docsOpen, setDocsOpen] = useState(false);
  const docCount =
    (access?.files.length ?? 0) +
    (lastGen?.status === "success" && lastGen.file?.url ? 1 : 0) +
    (lastReg?.status === "success" && lastReg.file?.url ? 1 : 0);

  return (
    <div className="card">
      {editing ? (
        <div className="space-y-2 text-xs p-3 border-b border-[var(--color-border)]">
          {!isOffice && (
            <>
              {efField("full_name", "Nazwa / imię i nazwisko")}
              {efField("street", "Ulica")}
              <div className="grid grid-cols-2 gap-2">
                {efField("building_no", "Nr budynku / lokalu")}
                {efField("postal_code", "Kod pocztowy", "00-000")}
              </div>
              {efField("city", "Miejscowość")}
              {efField("pesel_nip", "PESEL / NIP")}
              {efField("phone", "Telefon")}
              {efField("email", "E-mail")}
            </>
          )}
          {efField("ownership_share", "Udział (np. 1/1)")}
          {/* Rzadkie, ale legalne: osoba trzyma udział samodzielnie ORAZ drugi we
              wspólności małżeńskiej (wypis wykazuje ją dwa razy) — rekord jest jeden. */}
          {(owner.marital_group || owner.own_share) &&
            efField("own_share", "Udział samodzielny — obok wspólności (np. 1/2)")}
          {!isOffice && (
            <div className="block">
              <span className="section-label">Wspólność małżeńska (jeden podpis)</span>
              {spouses.length ? (
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <span className="text-rose-700 dark:text-rose-400">
                    ⚭ z: {spouses.map((s) => s.full_name).join(", ")}
                  </span>
                  <button
                    type="button"
                    className="section-label text-[var(--color-primary)] hover:underline"
                    title="Wyłącz z małżeństwa — osobny podpis; udziały zostają do ręcznej poprawki"
                    disabled={owners.leaveMarital.isPending}
                    onClick={() => owners.leaveMarital.mutate(owner.id)}
                  >
                    Rozłącz
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 mt-1">
                  <select
                    className="input-base text-xs py-1 flex-1"
                    value={spouseChoice}
                    onChange={(e) => setSpouseChoice(e.target.value)}
                  >
                    <option value="">— brak / wybierz współmałżonka —</option>
                    {spouseOptions.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                  </select>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!spouseChoice}
                    loading={owners.joinMarital.isPending}
                    onClick={() =>
                      owners.joinMarital.mutate(
                        { id: owner.id, spouseId: spouseChoice },
                        { onSuccess: () => setSpouseChoice("") },
                      )
                    }
                  >
                    Połącz
                  </Button>
                </div>
              )}
              {owners.joinMarital.isError && (
                <span className="block text-[11px] text-red-600 mt-1">{joinError ?? "Nie udało się połączyć."}</span>
              )}
            </div>
          )}
          <label className="block">
            <span className="section-label">Rola (opcjonalnie)</span>
            <select
              className="input-base text-xs py-1 mt-1"
              value={ef.role}
              onChange={(e) => setEf((s) => ({ ...s, role: e.target.value as OwnerRole }))}
            >
              <option value="">— nie wskazano —</option>
              {OWNER_ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </label>
          <div className="flex gap-2 pt-1">
            <Button size="sm" loading={owners.update.isPending} disabled={!ef.full_name && !isOffice} onClick={saveEdit}>Zapisz</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Anuluj</Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-[var(--color-border)]">
          <div className="min-w-0 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            {isOffice && (
              <span className="text-[10px] font-mono uppercase tracking-wide px-1 py-0.5 bg-[var(--color-bg-elevated)] border border-[var(--color-border)] text-[var(--color-text-secondary)]">
                Urząd
              </span>
            )}
            <span className="font-medium text-[var(--color-text-primary)]">{owner.full_name}</span>
            {owner.ownership_share && <span className="text-[var(--color-text-muted)] font-mono text-[11px]">({owner.ownership_share})</span>}
            {owner.own_share && (
              <span
                className="text-[var(--color-text-muted)] font-mono text-[11px] cursor-help"
                title="Udział trzymany samodzielnie, obok udziału we wspólności małżeńskiej — jeden pakiet z oboma udziałami"
              >
                + {owner.own_share} samodzielnie
              </span>
            )}
            {spouses.length > 0 && (
              <span
                className="text-[10px] text-rose-700 dark:text-rose-400 cursor-help"
                title="Wspólność małżeńska według wypisu — wspólny udział, jeden podpis (zmiana: Edytuj)"
              >
                ⚭ {spouses.map((s) => s.full_name).join(", ")}
              </span>
            )}
            {owner.role_display && (
              <span className="text-[10px] uppercase tracking-wide px-1 py-0.5 bg-[var(--color-bg-elevated)] border border-[var(--color-border)] text-[var(--color-text-secondary)]">
                {owner.role_display}
              </span>
            )}
            {owner.full_address && <span className="text-[11px] text-[var(--color-text-secondary)]">{owner.full_address}</span>}
            {(owner.pesel_nip || owner.phone || owner.email) && (
              <span className="text-[11px] font-mono text-[var(--color-text-muted)]">
                {[owner.pesel_nip, owner.phone, owner.email].filter(Boolean).join(" · ")}
              </span>
            )}
            <OwnerAccessBadge status={displayStatus} isStale={access?.is_stale ?? false} />
          </div>
          <div className="flex items-center divide-x divide-[var(--color-border)] shrink-0">
            <button className="section-label text-[var(--color-primary)] hover:underline px-2 first:pl-0" onClick={() => setEditing(true)}>
              Edytuj
            </button>
            {isOffice && officeRecord && (
              <button
                className="section-label text-[var(--color-primary)] hover:underline px-2"
                title="Popraw dane urzędu we WSPÓLNYM rejestrze — zadziała wszędzie, gdzie ten urząd występuje (inne działki, wnioski, opłaty skarbowe)"
                onClick={() => setEditingOffice(true)}
              >
                Edytuj dane urzędu
              </button>
            )}
            {!isOffice && owner.kind === "company" && onAssignOffice && (
              <button
                className="section-label text-[var(--color-primary)] hover:underline px-2"
                title="Instytucja wpisana z wypisu samą nazwą — dopasuj rekord z rejestru urzędów (kategoria i ścieżka urzędowa przeliczą się same)"
                onClick={onAssignOffice}
              >
                Przypisz urząd
              </button>
            )}
            {!isOffice && (
              <button
                className="section-label text-[var(--color-primary)] hover:underline px-2"
                title="Nowy właściciel z tymi samymi danymi adresowymi, kontaktem i udziałem (formularz do poprawki)"
                onClick={() => onCopyFrom({ street: owner.street, building_no: owner.building_no, postal_code: owner.postal_code, city: owner.city, phone: owner.phone, email: owner.email, ownership_share: owner.ownership_share })}
              >
                Kopiuj do nowego
              </button>
            )}
            <button className="section-label text-[var(--color-primary)] hover:underline px-2" onClick={() => owners.remove.mutate(owner.id)}>
              Usuń
            </button>
          </div>
        </div>
      )}

      {editingOffice && officeRecord && (
        <OfficeFormModal office={officeRecord} onClose={() => setEditingOffice(false)} />
      )}

      <div className="grid gap-3 p-3 lg:grid-cols-2 items-start">
        {/* ── Działania — u góry po lewej: rozstrzygnięcia tytułu + generowanie pism ── */}
        <DrawerGroup title="Działania">
          {securing ? (
            <div className="w-full space-y-2 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-2">
              <select
                className="input-base text-[11px] py-1 w-auto"
                value={basisId}
                onChange={(e) => {
                  setBasisId(e.target.value);
                  // domyślna forma zgody ENERGA z mapowania podstawy (można zmienić niżej)
                  const b = (bases ?? []).find((x) => x.id === e.target.value);
                  setZ7Form(b?.z7_consent_form ?? "");
                }}
              >
                {(bases ?? []).map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
              </select>
              <label className="block">
                <span className="section-label">Forma zgody — Załącznik 7 ENERGA (opcjonalnie)</span>
                <select className="input-base text-[11px] py-1 w-auto mt-1" value={z7Form}
                  onChange={(e) => setZ7Form(e.target.value)}>
                  <option value="">— wg podstawy / do doprecyzowania —</option>
                  {Z7_CONSENT_FORMS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </label>
              <MultiFileInput files={files} onChange={setFiles} label="Dokumenty (podpisana zgoda / decyzja / skan)" />
              <div className="flex flex-wrap gap-1.5">
                <Button size="sm" disabled={!basisId} loading={m.secure.isPending} onClick={doSecure}>
                  <ShieldCheck size={12} /> Oznacz pozyskany
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setSecuring(false); setBasisId(""); setFiles([]); }}>Anuluj</Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-1.5">
              {access?.status !== "secured" && (
                <Button size="sm" variant="secondary" onClick={() => ensure(() => setSecuring(true))}>
                  <ShieldCheck size={12} /> Dodaj uzgodnienie
                </Button>
              )}
              {/* "W trakcie" NIE jest ręczne — wynika automatycznie ze zlecenia WPW
                  (negocjator w terenie). Tu tylko cofanie ręcznych rozstrzygnięć. */}
              {access?.status === "secured" && (
                <Button size="sm" variant="secondary" onClick={() => setStatus("not_started")}>
                  <RefreshCw size={12} /> Cofnij pozyskanie
                </Button>
              )}
              {access?.status !== "not_required" ? (
                <Button
                  size="sm"
                  variant="ghost"
                  title="Współwłaściciel zbędny / dostęp od tego właściciela niepotrzebny"
                  onClick={() => ensure((id) => m.notRequired.mutate({ id }))}
                >
                  Dostęp niewymagany
                </Button>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => setStatus("not_started")}>Przywróć wymagany</Button>
              )}
              {access?.status === "problem" ? (
                <Button size="sm" variant="ghost" onClick={() => setStatus("not_started")}>Cofnij problem</Button>
              ) : (
                <Button size="sm" variant="warning" onClick={reportProblem}>
                  <AlertTriangle size={12} /> Zgłoś problem
                </Button>
              )}
              {access?.is_stale && (
                <Button
                  size="sm"
                  variant="secondary"
                  loading={m.extendRoute.isPending}
                  onClick={() => m.extendRoute.mutate({ id: access.id })}
                  title="Ręczne rozciągnięcie tytułu na bieżącą wersję. Automatyczne porównanie przebiegu i typów jest w „Danych ogólnych” działki."
                >
                  <RefreshCw size={12} /> Zaakceptuj uzgodnienie dla aktualnej trasy
                </Button>
              )}
            </div>
          )}
          {/* ── ścieżka listowna WPW: trwała blokada automatycznej wysyłki TEMU właścicielowi ── */}
          {isPrivate && <OwnerMailBlockControl owner={owner} />}
          {/* ── generacja dokumentów ── */}
          {(isPrivate || regulacja) && (
            <div className="flex flex-wrap items-center gap-1.5 border-t border-[var(--color-border-muted)] pt-2">
              {isPrivate && (
                <Button size="sm" variant="secondary" onClick={() => setGenOpen(true)}>
                  <FileText size={12} /> Generuj zgodę prywatną
                </Button>
              )}
              {regulacja && (
                <Button size="sm" variant="secondary" onClick={() => setRegOpen(true)}>
                  <FileText size={12} /> Generuj wniosek regulacyjny
                </Button>
              )}
            </div>
          )}
          {isOffice && (
            <p className="text-[10px] font-mono text-[var(--color-text-muted)]">
              Postępowanie urzędowe (uzgodnienie lokalizacji) prowadź w zakładce „Sprawy / Korespondencja"; decyzja ustawi tytuł.
            </p>
          )}
        </DrawerGroup>

        {/* ── Tytuł dostępu (per-właściciel) ── */}
        <DrawerGroup
          title="Tytuł dostępu"
          action={
            // Only a SECURED title is "for" a version — a withdrawn one still carries the
            // version of its previous acquisition, which read as "zgoda na starą trasę".
            access?.status === "secured" && access.route_version_label ? (
              <span className={DRAWER_HDR_CHIP} title="Wersja trasy, dla której pozyskano tytuł">wersja {access.route_version_label}</span>
            ) : undefined
          }
        >
          <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-[11px]">
            <dt className="section-label self-center">Status</dt>
            <dd className="flex flex-wrap items-center gap-1.5">
              <OwnerAccessBadge status={displayStatus} isStale={access?.is_stale ?? false} />
              {access?.is_stale && <StaleBadge />}
              {displayStatus === "in_progress" && titleStatus === "not_started" && (
                <span className="text-[10px] font-mono text-[var(--color-text-muted)]">
                  {owner.in_open_case ? "(otwarta sprawa)" : "(negocjator w terenie)"}
                </span>
              )}
              {displayStatus === "in_progress" && titleStatus === "problem" && (
                <span
                  className="text-[10px] font-mono text-[var(--color-text-muted)]"
                  title="Tytuł „Problem” (np. zgon) z prowadzonym wnioskiem w urzędzie czyta się jako „W trakcie”; po zamknięciu sprawy bez rozstrzygnięcia wraca „Problem”"
                >
                  (wniosek w urzędzie)
                </span>
              )}
            </dd>
            <dt className="section-label self-center">Podstawa</dt>
            <dd className="font-mono text-[var(--color-text-secondary)]">
              {access?.status === "secured" && access.basis_label ? access.basis_label : "—"}
            </dd>
            <dt className="section-label self-center" title="Forma zgody do Załącznika 7 ENERGA (ewidencja zgód)">Forma zgody (Z7)</dt>
            <dd className="font-mono text-[var(--color-text-secondary)]">
              {access?.status === "secured" && access.z7_consent_form
                ? Z7_CONSENT_FORMS.find((f) => f.value === access.z7_consent_form)?.label ?? access.z7_consent_form
                : "—"}
            </dd>
            {access?.status === "secured" && access.source_proceeding_label && (
              <>
                <dt className="section-label self-center">Źródło</dt>
                <dd className="font-mono text-[var(--color-text-secondary)]">{access.source_proceeding_label}</dd>
              </>
            )}
          </dl>
          {access?.status === "problem" && access.problem_note && (
            <p className="text-[11px] text-red-600 flex items-center gap-1">
              <AlertTriangle size={11} /> {access.problem_note}
            </p>
          )}

        {/* Propozycja tytułu ze zwrotu WPW (przekazanie „Pozyskano zgodę pisemną”) → 1-klik. */}
        {owner.wpw_title_proposal && titleStatus !== "secured" && (
          <div className="rounded border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30 px-2 py-1.5 space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-amber-800 dark:text-amber-300">
                Tytuł do potwierdzenia — {owner.wpw_title_proposal.declared_status_label}
                {owner.wpw_title_proposal.z7_consent_form
                  ? ` · ${Z7_CONSENT_FORMS.find((f) => f.value === owner.wpw_title_proposal!.z7_consent_form)?.label ?? owner.wpw_title_proposal.z7_consent_form}`
                  : ""}
                {" "}(ze zwrotu WPW)
              </span>
              <Button
                size="sm"
                className="ml-auto"
                loading={hm.confirm.isPending}
                onClick={() =>
                  hm.confirm.mutate({
                    id: owner.wpw_title_proposal!.handover_id,
                    targetEvent: owner.wpw_title_proposal!.target_event_id,
                    decision: "confirmed",
                  })
                }
                title="Zatwierdź zgodę i przenieś do tytułu prawnego (OwnerAccess → Pozyskany); zatwierdza też wgrane porozumienie"
              >
                <CheckCircle2 size={12} /> Potwierdź → pozyskaj tytuł
              </Button>
            </div>
            {/* Skany negocjatora — żeby było widać, CO się potwierdza, bez wchodzenia w zlecenie WPW. */}
            <WpwProposalFiles files={owner.wpw_title_proposal.attachments ?? []} />
          </div>
        )}

        {/* Porozumienia wgrane przez podwykonawcę w zamówieniu WPW — do obejrzenia bez wchodzenia
            w zamówienie. Decyzja jest jedna, o całej zgodzie: „Potwierdź → pozyskaj tytuł” wyżej
            (lub „Zatwierdź i przenieś” w zamówieniu) zatwierdza też ten plik, a odrzucenie zgody
            w zamówieniu odrzuca go razem z nią. */}
        {(owner.pending_agreements ?? []).map((pa) => (
          <div
            key={pa.file.id}
            className="rounded border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30 px-2 py-1.5 space-y-1.5"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-amber-800 dark:text-amber-300 inline-flex items-center gap-1">
                <FileSignature size={11} />
                <span title="Zatwierdza się razem ze zgodą („Potwierdź → pozyskaj tytuł” / „Zatwierdź i przenieś” w zamówieniu); odrzucenie zgody w zamówieniu odrzuca też ten plik.">
                  Porozumienie do zatwierdzenia
                </span>
                {pa.order_number && (
                  <>
                    {" "}·{" "}
                    <Link to={`/orders/${pa.order_id}`} className="font-mono hover:underline" title="Otwórz zamówienie">
                      {pa.order_number}
                    </Link>
                  </>
                )}
              </span>
            </div>
            {/* Sam dokument — podgląd bez wchodzenia w zlecenie WPW. */}
            <WpwProposalFiles files={[pa.file]} />
          </div>
        ))}
        </DrawerGroup>
      </div>

      {/* ── Dokumenty (zwijane): skany tytułu + ostatnio wygenerowane pisma ── */}
      <div className="border-t border-[var(--color-border)]">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-[var(--color-bg-elevated)]">
          <button
            type="button"
            onClick={() => setDocsOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-[var(--color-text-primary)] hover:text-[var(--color-primary)] transition-colors"
          >
            {docsOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            <Paperclip size={11} /> Dokumenty <span className="font-normal text-[var(--color-text-muted)]">· {docCount}</span>
          </button>
          {!securing && !addingDocs && (
            <button
              type="button"
              className="ml-auto section-label text-[var(--color-primary)] hover:underline"
              onClick={() => { setAddingDocs(true); setDocsOpen(true); }}
            >
              + dodaj dokument
            </button>
          )}
        </div>
        {docsOpen && (
          <div className="px-3 py-2 space-y-1.5 text-[11px]">
            {access && access.files.length > 0 && (
              <OwnerTitleDocuments access={access} connectionId={connectionId} />
            )}
            {lastGen?.status === "success" && lastGen.file?.url && (
              <a
                href={lastGen.file.url}
                download={lastGen.file.original_filename}
                className="inline-flex items-center gap-1 font-mono text-[var(--color-primary)] hover:underline"
                title={`${lastGen.template_name}${lastGen.generated_at ? ` · ${fmtDateTime(lastGen.generated_at)}` : ""}`}
              >
                <Download size={12} /> {lastGen.file.original_filename}
                <span className="text-[var(--color-text-muted)]">· ostatnio wygenerowana zgoda prywatna</span>
              </a>
            )}
            {lastReg?.status === "success" && lastReg.file?.url && (
              <a
                href={lastReg.file.url}
                download={lastReg.file.original_filename}
                className="inline-flex items-center gap-1 font-mono text-[var(--color-primary)] hover:underline"
                title={`${lastReg.template_name}${lastReg.generated_at ? ` · ${fmtDateTime(lastReg.generated_at)}` : ""}`}
              >
                <Download size={12} /> {lastReg.file.original_filename}
                <span className="text-[var(--color-text-muted)]">· wniosek regulacyjny</span>
              </a>
            )}
            {docCount === 0 && !addingDocs && (
              <p className="text-[var(--color-text-muted)]">Brak dokumentów — skany podpisanej zgody dodaje się tu albo przy „Dodaj uzgodnienie".</p>
            )}
            {addingDocs && (
              <div className="space-y-1.5 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-2">
                <MultiFileInput files={extraFiles} onChange={setExtraFiles} label="Dodaj dokumenty do tytułu" />
                <div className="flex gap-1.5">
                  <Button size="sm" disabled={extraFiles.length === 0} loading={m.uploadFiles.isPending} onClick={addDocs}>Dodaj</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setAddingDocs(false); setExtraFiles([]); }}>Anuluj</Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── notatki o tym właścicielu (ten sam wątek co notatki działki) ── */}
        <div className="flex items-center gap-2 px-3 py-1.5 bg-[var(--color-bg-elevated)] border-t border-[var(--color-border-muted)]">
          <button
            type="button"
            onClick={() => setNotesOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-[var(--color-text-primary)] hover:text-[var(--color-primary)] transition-colors"
          >
            {notesOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            <MessageSquare size={11} /> Notatki <span className="font-normal text-[var(--color-text-muted)]">· {ownerNotes.length}</span>
          </button>
          {!notesOpen && ownerNotes.length > 0 && (
            <span className="min-w-0 truncate text-[11px] text-[var(--color-text-secondary)]" title={ownerNotes[0].text}>
              {ownerNotes[0].text}
            </span>
          )}
        </div>
        {notesOpen && (
          <div className="px-3 py-2">
            <ParcelNotes parcelId={owner.parcel} connectionId={connectionId} ownerId={owner.id} />
          </div>
        )}
      </div>

      {genOpen && <GenerateConsentModal owner={owner} onClose={() => setGenOpen(false)} />}
      {regOpen && regulacja && (
        <GenerateRegulatoryApplicationModal
          owner={owner}
          connectionId={connectionId}
          onClose={() => setRegOpen(false)}
        />
      )}
    </div>
  );
}

// ── notatki działki (jeden wątek: działka + „per właściciel") ─────────────────
// Notatka bez właściciela dotyczy całej działki. Notatka przypięta do właściciela
// leci do TEGO SAMEGO wątku, ale rysuje się inaczej (pasek, tło, kto/udział/tytuł
// dostępu) — żeby czytając działkę widzieć od razu, z kim była rozmowa.
function ParcelNoteCard({
  note,
  onDelete,
  deleting,
}: {
  note: ParcelNote;
  onDelete: () => void;
  deleting: boolean;
}) {
  const perOwner = !!note.owner_label;
  return (
    <div
      className={clsx(
        "p-2 border text-xs",
        perOwner
          ? "border-[var(--color-border)] border-l-2 border-l-[var(--color-primary)] bg-[var(--color-bg-elevated)]"
          : "border-[var(--color-border-muted)] bg-[var(--color-bg-surface)]",
      )}
    >
      {perOwner && (
        <div className="flex flex-wrap items-center gap-1.5 mb-1">
          <Users size={11} className="text-[var(--color-primary)] shrink-0" />
          <span className="font-medium text-[var(--color-text-primary)]">{note.owner_label}</span>
          {note.owner_share && (
            <span className="font-mono text-[10px] text-[var(--color-text-muted)]">({note.owner_share})</span>
          )}
          {note.owner_status && (
            <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
              tytuł: {note.owner_status_display}
            </span>
          )}
          {note.owner_removed && (
            <span className="font-mono text-[10px] text-amber-600" title="Właściciel został później usunięty z działki">
              (właściciel usunięty)
            </span>
          )}
        </div>
      )}
      <p className="whitespace-pre-wrap break-words text-[var(--color-text-primary)]">{note.text}</p>
      <div className="mt-1 flex items-center gap-2 font-mono text-[10px] text-[var(--color-text-muted)]">
        <span>{note.author_name ?? "—"}</span>
        <span>{fmtDateTime(note.created_at)}</span>
        {note.can_delete && (
          <button
            className="ml-auto hover:text-red-600 disabled:opacity-50"
            title="Usuń notatkę"
            disabled={deleting}
            onClick={onDelete}
          >
            <Trash2 size={11} />
          </button>
        )}
      </div>
    </div>
  );
}

function ParcelNotes({
  parcelId,
  connectionId,
  owners = [],
  ownerId,
}: {
  parcelId: string;
  connectionId: string;
  /** Właściciele działki — lista „kogo dotyczy" (pomijana w trybie jednego właściciela). */
  owners?: ParcelOwner[];
  /** Ustawione = wątek jednego właściciela (karta właściciela): bez wyboru i bez cudzych wpisów. */
  ownerId?: string;
}) {
  const { data: notes, isLoading } = useParcelNotes(parcelId);
  const m = useParcelNoteMutations(parcelId, connectionId);
  const [text, setText] = useState("");
  const [target, setTarget] = useState("");
  const single = !!ownerId;
  const visible = useMemo(
    () => (single ? (notes ?? []).filter((n) => n.owner === ownerId) : (notes ?? [])),
    [notes, ownerId, single],
  );

  const submit = () => {
    const body = text.trim();
    if (!body) return;
    m.add.mutate({ text: body, owner: ownerId ?? target ?? null }, { onSuccess: () => setText("") });
  };

  return (
    <div className="space-y-2">
      <div className="space-y-1.5">
        <textarea
          className="input-base text-xs w-full"
          rows={single ? 2 : 3}
          placeholder={single ? "Notatka o tym właścicielu…" : "Notatka do działki…"}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <div className="flex flex-wrap items-center gap-1.5">
          {!single && owners.length > 0 && (
            <label className="flex items-center gap-1.5">
              <span className="section-label">Dotyczy</span>
              <select
                className="input-base text-[11px] py-1 w-auto"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
              >
                <option value="">całej działki</option>
                {owners.map((o) => (
                  <option key={o.id} value={o.id}>{o.full_name}</option>
                ))}
              </select>
            </label>
          )}
          <Button size="sm" className="ml-auto" disabled={!text.trim()} loading={m.add.isPending} onClick={submit}>
            Dodaj notatkę
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="py-3 flex justify-center"><Spinner /></div>
      ) : visible.length === 0 ? (
        <p className="text-[11px] font-mono text-[var(--color-text-muted)]">
          {single ? "Brak notatek o tym właścicielu." : "Brak notatek."}
        </p>
      ) : (
        <div className="space-y-1.5">
          {visible.map((n) => (
            <ParcelNoteCard
              key={n.id}
              note={n}
              deleting={m.remove.isPending}
              onDelete={() => m.remove.mutate(n.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── CSV bulk owner import ────────────────────────────────────────────────────────
function OwnerCsvImport({ parcelId, connectionId }: { parcelId: string; connectionId: string }) {
  const qc = useQueryClient();
  const [taskId, setTaskId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const task = useTaskPolling(taskId);
  const taskStatus = task.data?.status;
  const busy = uploading || (!!taskId && !["success", "failure", "cancelled"].includes(taskStatus ?? ""));

  useEffect(() => {
    if (!taskId || !taskStatus) return;
    if (["success", "failure", "cancelled"].includes(taskStatus)) {
      if (taskStatus === "success") {
        qc.invalidateQueries({ queryKey: ["pp", "parcel", parcelId] });
        qc.invalidateQueries({ queryKey: ["pp", "parcels", connectionId] });
      }
    }
  }, [taskId, taskStatus, parcelId, connectionId, qc]);

  const handleFile = async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const r = await importOwnersCsv(parcelId, file);
      setTaskId(r.task_id);
    } catch {
      setError("Nie udało się przesłać pliku CSV.");
    } finally {
      setUploading(false);
    }
  };

  const reset = () => { setTaskId(null); setError(null); };

  if (taskId && taskStatus) {
    const done = ["success", "failure", "cancelled"].includes(taskStatus);
    return (
      <div className="mt-2 p-3 border border-[var(--color-border-muted)] bg-[var(--color-bg-elevated)] space-y-2 text-xs">
        <div className="flex items-center gap-2">
          {!done && <Spinner className="h-3 w-3" />}
          <span className="font-medium text-[var(--color-text-secondary)]">
            {taskStatus === "success" ? "Import zakończony" : taskStatus === "failure" ? "Import nieudany" : "Importowanie…"}
          </span>
          {done && (
            <button type="button" className="ml-auto text-[var(--color-text-muted)] hover:text-[var(--color-text)]" onClick={reset}>
              <X size={12} />
            </button>
          )}
        </div>
        {task.data?.log && (
          <pre className="text-[10px] font-mono text-[var(--color-text-muted)] whitespace-pre-wrap max-h-32 overflow-y-auto bg-[var(--color-bg-surface)] p-2 border border-[var(--color-border-muted)]">
            {task.data.log}
          </pre>
        )}
      </div>
    );
  }

  return (
    <div>
      <p className="text-[11px] text-[var(--color-text-muted)] mb-1.5">
        Jeden plik CSV z właścicielami prywatnymi TEJ działki (kolumny jak w szablonie importu).
      </p>
      {error && <p className="text-xs text-[var(--color-primary)] mb-1">{error}</p>}
      {busy ? (
        <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 px-4 py-4 flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500">
          <Spinner className="h-4 w-4" />
          <span>Przesyłanie…</span>
        </div>
      ) : (
        <FileDropZone files={[]} onFilesChange={handleFile} accept=".csv,text/csv" />
      )}
    </div>
  );
}

// ── CSV bulk owner import for the WHOLE project (one CSV, many działki) ───────────
// Twin of OwnerCsvImport, but the CSV carries an "Identyfikator działki" column so a single
// upload fans owners out to every matching parcel. Opened from a button above the parcel list.
function OwnerCsvBulkImportModal({ connectionId, onClose }: { connectionId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const task = useTaskPolling(taskId);
  const taskStatus = task.data?.status;
  const done = !!taskStatus && ["success", "failure", "cancelled"].includes(taskStatus);
  const busy = uploading || (!!taskId && !done);

  useEffect(() => {
    if (taskStatus === "success") {
      qc.invalidateQueries({ queryKey: ["pp", "parcels", connectionId] });
    }
  }, [taskStatus, connectionId, qc]);

  const submit = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const r = await importOwnersCsvBulk(connectionId, file, replaceExisting);
      setTaskId(r.task_id);
    } catch {
      setError("Nie udało się przesłać pliku CSV.");
    } finally {
      setUploading(false);
    }
  };

  const reset = () => { setTaskId(null); setFile(null); setError(null); setReplaceExisting(false); };

  return (
    <Modal open onClose={onClose} title="Dodaj zbiorczo właścicieli prywatnych (CSV)" size="md">
      <div className="space-y-3 text-sm">
        <p className="text-xs text-[var(--color-text-muted)]">
          Wgraj jeden plik CSV z kolumną <span className="font-mono">Nr działki</span> oraz{" "}
          <span className="font-mono">Nazwa / imię i nazwisko</span>. Pozostałe kolumny (
          <span className="font-mono">Ulica</span>, <span className="font-mono">Kod pocztowy</span>,{" "}
          <span className="font-mono">Udział</span> …) nazywają się tak jak pola w formularzu
          dodawania właściciela. Domyślnie import uzupełnia dane: działka spoza projektu zostanie
          utworzona (bez geometrii — można ją pobrać z ULDK później), właściciel o tym samym
          imieniu i nazwisku na działce zostanie zaktualizowany danymi z CSV (puste komórki nie
          kasują danych), a pozostali zostaną dodani.
        </p>

        {taskId && taskStatus ? (
          <div className="p-3 border border-[var(--color-border-muted)] bg-[var(--color-bg-elevated)] space-y-2 text-xs">
            <div className="flex items-center gap-2">
              {!done && <Spinner className="h-3 w-3" />}
              <span className="font-medium text-[var(--color-text-secondary)]">
                {taskStatus === "success" ? "Import zakończony" : taskStatus === "failure" ? "Import nieudany" : "Importowanie…"}
              </span>
            </div>
            {task.data?.log && (
              <pre className="text-[10px] font-mono text-[var(--color-text-muted)] whitespace-pre-wrap max-h-48 overflow-y-auto bg-[var(--color-bg-surface)] p-2 border border-[var(--color-border-muted)]">
                {task.data.log}
              </pre>
            )}
            {done && (
              <div className="flex justify-end gap-2 pt-1">
                <Button size="sm" variant="secondary" onClick={reset}>Wgraj kolejny</Button>
                <Button size="sm" onClick={onClose}>Zamknij</Button>
              </div>
            )}
          </div>
        ) : (
          <>
            {error && <p className="text-xs text-[var(--color-primary)]">{error}</p>}
            <FileDropZone
              files={file ? [file] : []}
              onFilesChange={(files) => setFile(files[0] ?? null)}
              accept=".csv,text/csv"
            />
            <label className="flex items-start gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={replaceExisting}
                onChange={(e) => setReplaceExisting(e.target.checked)}
              />
              <span>
                <span className="text-[var(--color-text-primary)]">Zamień wszystkich właścicieli prywatnych</span>
                <span className="block text-[10px] text-[var(--color-text-muted)]">
                  Dla każdej działki z pliku dotychczasowi właściciele prywatni zostaną usunięci i
                  zastąpieni tymi z CSV. Właściciele instytucjonalni (urzędy / podmioty) oraz działki
                  spoza pliku pozostają bez zmian.
                </span>
                <span className="block text-[10px] font-medium text-[var(--color-primary)] mt-0.5">
                  <AlertTriangle size={10} className="inline -mt-0.5 mr-1" />
                  Uwaga: wraz z usuwanymi właścicielami przepadają ich uzgodnienia (tytuły dostępu
                  wraz z dokumentami). Jeśli chcesz tylko poprawić dane, zostaw tę opcję wyłączoną —
                  domyślny import aktualizuje właścicieli bez ruszania uzgodnień.
                </span>
              </span>
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <Button size="sm" variant="ghost" onClick={onClose} disabled={busy}>Anuluj</Button>
              <Button size="sm" loading={busy} disabled={!file || busy} onClick={submit}>Prześlij</Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ── add owner: private person / private company / urząd (offices registry) ──────
// Procedura dostępu jest identyczna dla osoby i podmiotu prywatnego — liczy się tylko
// rozróżnienie prywatny vs urząd (po office_id). Stąd dwie opcje, nie trzy.
type OwnerKindUI = "private" | "office";

function OwnerAddForm({ parcelId, connectionId, onDone, prefill }: { parcelId: string; connectionId: string; onDone: () => void; prefill?: OwnerPrefill }) {
  const owners = useOwnerMutations(parcelId, connectionId);
  const [kind, setKind] = useState<OwnerKindUI>("private");
  const [officeId, setOfficeId] = useState("");
  const { data: offices } = useSelectableOffices(officeId);
  const [addingOffice, setAddingOffice] = useState(false);
  // Poprawka wybranego wpisu bez opuszczania formularza — inaczej „zły adres" kończył się
  // dodaniem kolejnego urzędu o tej samej nazwie.
  const [fixingOffice, setFixingOffice] = useState(false);
  const [share, setShare] = useState(prefill?.ownership_share ?? "");
  // Rola względem działki — opcjonalna dla obu rodzajów wpisu (prywatny / urząd).
  const [role, setRole] = useState<OwnerRole>("");
  const emptyEntity = {
    full_name: "", street: prefill?.street ?? "", building_no: prefill?.building_no ?? "",
    postal_code: prefill?.postal_code ?? "", city: prefill?.city ?? "",
    pesel_nip: "", phone: prefill?.phone ?? "", email: prefill?.email ?? "",
    ownership_share: prefill?.ownership_share ?? "",
  };
  const [pf, setPf] = useState(emptyEntity);

  const field = (k: keyof typeof pf, label: string, placeholder?: string) => (
    <label className="block">
      <span className="section-label">{label}</span>
      <input
        className="input-base text-xs py-1 mt-1"
        placeholder={placeholder}
        value={pf[k]}
        onChange={(e) => setPf((s) => ({ ...s, [k]: e.target.value }))}
      />
    </label>
  );

  const officeOptions = useMemo(
    () => (offices ?? []).map((o) => ({ value: o.id, label: `${o.name} (${o.kind_display})` })),
    [offices],
  );
  const pickedOffice = (offices ?? []).find((o) => o.id === officeId);

  const roleField = (
    <label className="block">
      <span className="section-label">Rola (opcjonalnie)</span>
      <select className="input-base text-xs py-1 mt-1" value={role} onChange={(e) => setRole(e.target.value as OwnerRole)}>
        <option value="">— nie wskazano —</option>
        {OWNER_ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
      </select>
    </label>
  );

  const canSubmit = kind === "office" ? !!officeId : !!pf.full_name;
  const submit = () => {
    if (!canSubmit) return;
    const payload =
      kind === "office"
        ? { parcel: parcelId, kind: "company", office: officeId, ownership_share: share, role }
        : { parcel: parcelId, kind: "person", ...pf, role };
    owners.create.mutate(payload as never, { onSuccess: onDone });
  };

  return (
    <div className="space-y-3 text-xs">
      <div className="flex gap-1 flex-wrap">
        {([["private", "Właściciel prywatny"], ["office", "Urząd / instytucja"]] as const).map(([v, lbl]) => (
          <Button key={v} size="sm" variant={kind === v ? "primary" : "ghost"} onClick={() => setKind(v)}>
            {lbl}
          </Button>
        ))}
      </div>

      {addingOffice && (
        <OfficeFormModal
          office={null}
          onClose={() => setAddingOffice(false)}
          onCreated={(o) => { setOfficeId(o.id); setAddingOffice(false); }}
        />
      )}

      {fixingOffice && pickedOffice && (
        <OfficeFormModal office={pickedOffice} onClose={() => setFixingOffice(false)} />
      )}

      {kind === "office" ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="section-label">Urząd / instytucja</span>
            <span className="flex items-center gap-2">
              {pickedOffice && (
                <button
                  type="button"
                  className="section-label normal-case tracking-normal text-[var(--color-primary)] hover:underline"
                  title="Popraw dane wybranego urzędu we wspólnym rejestrze — zamiast zakładać drugi wpis"
                  onClick={() => setFixingOffice(true)}
                >
                  edytuj dane
                </button>
              )}
              <button
                type="button"
                className="section-label normal-case tracking-normal text-[var(--color-primary)] hover:underline"
                onClick={() => setAddingOffice(true)}
              >
                + dodaj urząd
              </button>
            </span>
          </div>
          <SearchableSelect options={officeOptions} value={officeId} placeholder="— wybierz urząd —" onChange={(e) => setOfficeId(e.target.value)} />
          <label className="block">
            <span className="section-label">Udział</span>
            <input className="input-base text-xs py-1 mt-1" placeholder="np. 1/1" value={share} onChange={(e) => setShare(e.target.value)} />
          </label>
          {roleField}
        </div>
      ) : (
        <div className="space-y-2">
          {field("full_name", "Nazwa / imię i nazwisko")}
          {field("street", "Ulica")}
          <div className="grid grid-cols-2 gap-2">
            {field("building_no", "Nr budynku / lokalu")}
            {field("postal_code", "Kod pocztowy", "00-000")}
          </div>
          {field("city", "Miejscowość")}
          {field("pesel_nip", "PESEL / NIP")}
          {field("phone", "Telefon")}
          {field("email", "E-mail")}
          {field("ownership_share", "Udział (np. 1/1)")}
          {roleField}
        </div>
      )}

      <div className="flex gap-2">
        <Button size="sm" loading={owners.create.isPending} disabled={!canSubmit} onClick={submit}>Dodaj</Button>
        <Button size="sm" variant="ghost" onClick={onDone}>Anuluj</Button>
      </div>
    </div>
  );
}

// ── WPW (zlecenie terenowe / kompleksowe) — w terenie ────────────────────────
// GK→wykonawca: aktualizacja dokumentów wejściowych dla jednego WPW (po zmianie trasy /
// doprecyzowaniu). Powiadamia podwykonawcę i u niego zapala badge „Zaktualizowana".
function WpwSection({ parcel, projectId, connectionId }: { parcel: Parcel; projectId: string; connectionId: string }) {
  const links = useOrderLinkMutations(parcel.id, connectionId);
  const [picking, setPicking] = useState<"" | "wpw_terenowe" | "wpw_kompleksowe">("");
  const { data: orders } = useLinkableOrders(
    { order_type: picking || "wpw_terenowe", project: projectId }, !!picking,
  );
  const wpwLinks = parcel.linked_orders.filter(
    (l) => l.order_type === "wpw_terenowe" || l.order_type === "wpw_kompleksowe",
  );
  const linkedIds = new Set(wpwLinks.map((l) => l.order_id));
  const options = (orders ?? [])
    .filter((o) => !linkedIds.has(o.id))
    .map((o) => ({ value: o.id, label: `${o.order_number}${o.contractor_name ? ` · ${o.contractor_name}` : ""}` }));

  // Per-owner negotiator status + the conversation history from the order — surfaced here so it is
  // visible inside the ZE project (not only on the order / WPW tab).
  const { data: handovers } = useWpwHandovers({ parcel: parcel.id });
  const [convOwner, setConvOwner] = useState<{ ownerId: string; label: string } | null>(null);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {parcel.in_field ? (
          <span className="text-[11px] font-mono text-blue-600">● W terenie (aktywne zlecenie WPW)</span>
        ) : (
          <span className="text-[11px] font-mono text-[var(--color-text-muted)]">Brak aktywnego zlecenia WPW.</span>
        )}
        {/* Hide the add-order buttons once a WPW order is linked — the existing
            order row below stays visible so it can still be detached ("odepnij"). */}
        {wpwLinks.length === 0 && (
          <div className="ml-auto flex gap-1">
            {WPW_ORDER_TYPE_OPTIONS.map((o) => (
              <Button
                key={o.value}
                size="sm"
                variant={picking === o.value ? "primary" : "secondary"}
                onClick={() => setPicking((p) => (p === o.value ? "" : (o.value as "wpw_terenowe" | "wpw_kompleksowe")))}
              >
                {picking === o.value ? "Anuluj" : `+ ${o.value === "wpw_terenowe" ? "Terenowe" : "Kompleksowe"}`}
              </Button>
            ))}
          </div>
        )}
      </div>
      {(handovers ?? []).length > 0 && (
        <div className="space-y-1 border-t border-[var(--color-border-muted)] pt-2">
          <span className="section-label">Negocjator — status i historia rozmowy</span>
          <ul className="space-y-1">
            {handovers!.map((h) => (
              <li key={h.id} className="flex items-center justify-between gap-2 text-[11px] font-mono">
                <span className="inline-flex items-center gap-1.5 min-w-0">
                  <span className="truncate text-[var(--color-text-secondary)]">{h.owner.full_name}</span>
                  <WpwStatusBadge status={h.declared_status} />
                  {h.gk_docs.length > 0 && (
                    <span className="text-[var(--color-text-muted)]" title="Załączniki GK widoczne dla podwykonawcy">
                      📎{h.gk_docs.length}
                    </span>
                  )}
                </span>
                <span className="inline-flex items-center gap-2 shrink-0">
                  {/* Ta sama kontrolka plików co w zamówieniu — z akceptacją porozumień (✓/✗). */}
                  <WpwFilesInline
                    h={h}
                    onShowHistory={() => setConvOwner({ ownerId: h.owner.id, label: `WPW — ${h.owner.full_name}` })}
                  />
                  <button
                    type="button"
                    className="text-[var(--color-primary)] hover:underline"
                    onClick={() => setConvOwner({ ownerId: h.owner.id, label: `WPW — ${h.owner.full_name}` })}
                  >
                    Pokaż rozmowę
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {convOwner && (
        <WpwConversationModal title={convOwner.label} ownerId={convOwner.ownerId} onClose={() => setConvOwner(null)} />
      )}
      {wpwLinks.length > 0 && (
        <ul className="space-y-1">
          {wpwLinks.map((l) => (
            <li key={l.link_id} className="text-[11px] font-mono text-[var(--color-text-secondary)]">
              <div className="flex items-center justify-between gap-2">
                {l.order_id ? (
                  <Link to={`/orders/${l.order_id}`} className="text-[var(--color-primary)] hover:underline truncate">
                    {l.order_number ?? l.order_id}
                  </Link>
                ) : (
                  <span className="truncate">{l.order_number ?? l.order_id}</span>
                )}
                <button
                  className="text-[var(--color-text-muted)] hover:text-red-600 hover:underline shrink-0"
                  title="Odłącz to zlecenie od działki (zlecenie nie zostanie usunięte)"
                  onClick={() => links.unlink.mutate(l.link_id)}
                >
                  odepnij
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 text-[var(--color-text-muted)]">
                {l.order_type && (
                  <span>{ORDER_TYPE_LABEL[l.order_type] ?? l.order_type_display ?? l.order_type}</span>
                )}
                {l.order_status && (
                  <span
                    className="px-1 py-px uppercase tracking-wide text-[9px] border border-[var(--color-border)]"
                    style={{ color: ORDER_STATUS_COLOR[l.order_status] ?? "var(--color-text-muted)" }}
                  >
                    {ORDER_STATUS_LABELS[l.order_status as OrderStatus] ?? l.order_status}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {parcel.linked_requisitions.length > 0 && (
        <div className="space-y-1 border-t border-[var(--color-border-muted)] pt-2">
          <span className="section-label">Powiązane zapotrzebowania</span>
          <ul className="space-y-1">
            {parcel.linked_requisitions.map((r) => (
              <li key={r.link_id} className="flex items-center gap-1.5 text-[11px] font-mono text-[var(--color-text-secondary)]">
                <span
                  className="px-1 py-px uppercase tracking-wide text-[9px] border border-[var(--color-border)]"
                  style={{ color: REQ_STATUS_COLOR[r.status] ?? "var(--color-text-muted)" }}
                >
                  {r.status_display}
                </span>
                <Link to={`/requisitions/${r.requisition_id}`} className="text-[var(--color-primary)] hover:underline">
                  {r.contractor_name || "zapotrzebowanie"}
                </Link>
                {r.order_id && (
                  <Link to={`/orders/${r.order_id}`} className="text-[var(--color-text-muted)] hover:underline">
                    · zlecenie {r.order_number}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {picking && (
        <SearchableSelect
          options={options}
          value=""
          placeholder={options.length ? `— wybierz zlecenie (${picking === "wpw_terenowe" ? "terenowe" : "kompleksowe"}) —` : "Brak dostępnych zleceń WPW"}
          onChange={(e) => e.target.value && links.link.mutate({ order: e.target.value }, { onSuccess: () => setPicking("") })}
        />
      )}
      <p className="text-[10px] font-mono text-[var(--color-text-muted)]">
        Po zwrocie dokumentów oznacz tytuły właścicieli „Pozyskany" ręcznie — zlecenie nie ustawia tytułu automatycznie.
      </p>
    </div>
  );
}

// ── potwierdzenie i wynik listu (LETTER only) ─────────────────────────────────
// Dwa NIEZALEŻNE fakty przy liście do właściciela: doręczenie (zwrotka w Dzienniku albo
// ręczne „Potwierdź list" — organizacyjne, może przyjść później albo wcale) i wynik listu
// (uzgodnienie / odmowa — dopiero on rusza tytuł właściciela). Wynik wpisuje się od razu.
/** Wynik listu: „Uzgodnienie" pozyskuje tytuł właściciela (zgoda właściciela), „Odmowa"
 *  ustawia tytuł „Odmowa". Nie czeka na zwrotkę. Dotyczy każdego listu — wysłanego z systemu
 *  i podpiętego z Dziennika. Opcjonalne pismo z uzgodnieniem / odmową → dokumenty tytułu. */
function LetterOutcomeStep({
  item,
  setOutcome,
}: {
  item: ProceedingItem;
  setOutcome: ReturnType<typeof useNotificationMutations>["setOutcome"];
}) {
  const [note, setNote] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [err, setErr] = useState<string | null>(null);
  if (item.letter_outcome) {
    const ok = item.letter_outcome === "agreement";
    return (
      <div>
        <span className={`inline-flex items-center gap-1 ${ok ? "text-emerald-600" : "text-red-600"}`}>
          {ok ? <CheckCircle2 size={12} /> : <X size={12} />} Wynik listu: {item.letter_outcome_display}
          {ok ? " — tytuł pozyskany (zgoda właściciela)" : " — tytuł: Odmowa"}
        </span>
        <span className="block text-[var(--color-text-muted)]">
          {item.letter_outcome_by_name ?? "—"}
          {item.letter_outcome_at && <> · {fmtDateTime(item.letter_outcome_at)}</>}
          {item.letter_outcome_note && <> · {item.letter_outcome_note}</>}
        </span>
      </div>
    );
  }
  if (!item.can_set_letter_outcome) {
    return (
      <span className="text-[var(--color-text-muted)]">
        Wynik listu (uzgodnienie / odmowa) wpisuje koordynator projektu lub dyrektor techniczny.
      </span>
    );
  }
  const send = (outcome: "agreement" | "refusal") => {
    setErr(null);
    setOutcome.mutate(
      { itemId: item.id, outcome, note: note.trim() || undefined, ...(files.length ? { files } : {}) },
      {
        onSuccess: () => { setNote(""); setFiles([]); },
        onError: (e: unknown) =>
          setErr(
            (e as { response?: { data?: { errors?: { detail?: string } } } })?.response?.data?.errors?.detail ||
              "Nie udało się zapisać wyniku listu.",
          ),
      },
    );
  };
  return (
    <div className="space-y-1.5 border-t border-[var(--color-border-muted)] pt-1.5">
      <span className="section-label">Wynik listu</span>
      <span className="block text-[var(--color-text-muted)]">
        Uzgodnienie albo odmowę wpisz od razu — zwrotka może przyjść później albo wcale.
      </span>
      <input
        className="input-base text-[11px] py-1 w-full"
        placeholder="Notatka (opcjonalnie), np. zgoda podpisana 12.09 / odmowa telefoniczna"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      {/* Opcjonalne pismo z uzgodnieniem / odmową — ląduje na tytule właściciela (dokumenty tytułu). */}
      <div className="flex flex-wrap items-center gap-2">
        <label
          className="inline-flex items-center gap-1 cursor-pointer text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
          title="Opcjonalnie: skan podpisanej zgody albo pisma z odmową — trafi na tytuł właściciela (dokumenty tytułu)"
        >
          <Paperclip size={12} />
          {files.length > 0
            ? files.map((f) => f.name).join(", ")
            : "Dołącz pismo z uzgodnieniem / odmową (opcjonalnie)"}
          <input
            type="file"
            multiple
            accept=".pdf,image/*"
            className="hidden"
            onChange={(e) => { setFiles(Array.from(e.target.files ?? [])); e.target.value = ""; }}
          />
        </label>
        {files.length > 0 && (
          <button
            type="button"
            className="inline-flex items-center text-[var(--color-text-muted)] hover:text-red-600"
            onClick={() => setFiles([])}
            aria-label="Usuń dołączone pliki"
            title="Usuń dołączone pliki"
          >
            <X size={12} />
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          loading={setOutcome.isPending}
          onClick={() => send("agreement")}
          title="Właściciel się zgodził — tytuł dostępu zostaje pozyskany z podstawą „zgoda właściciela”; dołączone pismo (albo dokumenty listu) trafia na tytuł"
        >
          <CheckCircle2 size={12} /> Uzgodnienie
        </Button>
        <Button
          size="sm"
          variant="danger"
          loading={setOutcome.isPending}
          onClick={() => send("refusal")}
          title="Właściciel odmówił — tytuł dostępu przechodzi w „Odmowa”; dołączone pismo z odmową zostaje na tytule"
        >
          <X size={12} /> Odmowa
        </Button>
      </div>
      {err && <p className="text-red-600">{err}</p>}
    </div>
  );
}

/** Zwrotka / potwierdzenie listu i wynik listu — OSOBNO. Doręczenie to fakt organizacyjny
 *  (nie uzgodnienie); krok „Uzgodnienie / Odmowa" jest dostępny od razu, także zanim zwrotka
 *  przyjdzie (albo gdy nie przyjdzie wcale). */
function LetterConfirmation({
  item,
  confirm,
  setOutcome,
}: {
  item: ProceedingItem;
  confirm: ReturnType<typeof useNotificationMutations>["confirmLetter"];
  setOutcome: ReturnType<typeof useNotificationMutations>["setOutcome"];
}) {
  return (
    <div className="space-y-1.5">
      <LetterDeliveryLine item={item} confirm={confirm} />
      <LetterOutcomeStep item={item} setOutcome={setOutcome} />
    </div>
  );
}

/** Stan doręczenia listu. List z obiektem w Dzienniku (wpis rejestru / zlecenie wydruku)
 *  potwierdza TYLKO Dziennik (zwrotka na wpisie / potwierdzenie odbioru na zleceniu); ręczne
 *  „Potwierdź list" zostaje dla pism, których Dziennik nie prowadzi. Stylowane neutralnie —
 *  zwrotka to potwierdzenie odbioru, nie uzgodnienie (to wpisuje się niżej). */
function LetterDeliveryLine({
  item,
  confirm,
}: {
  item: ProceedingItem;
  confirm: ReturnType<typeof useNotificationMutations>["confirmLetter"];
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);

  if (item.letter_confirmed_at) {
    return (
      <div>
        <span className="inline-flex items-center gap-1 text-[var(--color-text-secondary)]">
          <Check size={12} /> List potwierdzony (doręczenie, nie uzgodnienie)
        </span>
        <span className="block text-[var(--color-text-muted)]">
          {item.letter_confirmed_by_name ?? "—"} · {fmtDateTime(item.letter_confirmed_at)}
          {item.letter_confirmation_note && <> · {item.letter_confirmation_note}</>}
        </span>
      </div>
    );
  }
  if (item.letter_delivered) {
    const zwrotka = item.paper_dispatch_confirmation ?? item.print_order_info?.receipt_confirmation ?? null;
    return (
      <div>
        <span className="inline-flex items-center gap-1 text-[var(--color-text-secondary)]">
          <Check size={12} /> Doręczono — potwierdzenie z Dziennika
        </span>
        <span className="block text-[var(--color-text-muted)]">
          {zwrotka ? "Potwierdzenie odbioru wpięte w Dzienniku." : "Doręczenie odnotowane w Dzienniku bez pliku potwierdzenia."}{" "}
          To potwierdzenie odbioru, nie uzgodnienie — wynik listu wpisuje się niżej.
        </span>
      </div>
    );
  }
  // List z obiektem w Dzienniku potwierdza TYLKO Dziennik — tu tylko informacja, gdzie.
  if (item.paper_dispatch || item.print_order) {
    return (
      <span className="text-[var(--color-text-muted)]">
        Doręczenie potwierdza się w Dzienniku (DLC_02):{" "}
        {item.print_order
          ? "dodaj potwierdzenie odbioru na zleceniu wydruku"
          : "oznacz wpis jako doręczony i wepnij zwrotkę"}
        . Zwrotka może przyjść później albo wcale — wynik listu na nią nie czeka.
      </span>
    );
  }
  if (!item.can_confirm_letter) {
    return (
      <span className="text-[var(--color-text-muted)]">
        Potwierdzenie listu może dodać koordynator projektu lub dyrektor techniczny.
      </span>
    );
  }
  if (!open) {
    return (
      <div>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setOpen(true)}
          title="Odnotuj, że list dotarł do właściciela (zwrotka / kontakt zwrotny). Nie rozstrzyga uzgodnienia — to robi wynik listu niżej."
        >
          Potwierdź list
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <input
        className="input-base text-[11px] py-1 w-full"
        placeholder="Notatka (opcjonalnie), np. zwrotka w dzienniku / właściciel oddzwonił"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          loading={confirm.isPending}
          onClick={() => {
            setErr(null);
            confirm.mutate(
              { itemId: item.id, note: note.trim() || undefined },
              {
                onSuccess: () => { setOpen(false); setNote(""); },
                onError: (e: unknown) =>
                  setErr(
                    (e as { response?: { data?: { errors?: { detail?: string } } } })?.response?.data?.errors?.detail ||
                      "Nie udało się potwierdzić listu.",
                  ),
              },
            );
          }}
        >
          Dodaj potwierdzenie
        </Button>
        <Button size="sm" variant="ghost" onClick={() => { setOpen(false); setErr(null); }}>Anuluj</Button>
      </div>
      {err && <p className="text-red-600">{err}</p>}
    </div>
  );
}

/** Jedno pismo właściciela w panelu listów / zawiadomień: stan w Dzienniku, zwrotka, krok
 *  potwierdzenia i wyniku (listy) albo „Uznaj jako dostarczone" (zawiadomienia). Pismo
 *  podpięte z Dziennika pokazuje, pod kogo jeszcze je podpięto (wynik jest per właściciel),
 *  i daje się odpiąć, dopóki nie ma wyniku. */
function OwnerLetterEntry({
  item,
  parcelId,
  isLetter,
  informed,
  canSubmit,
  notif,
  heading,
  separated,
}: {
  item: ProceedingItem;
  parcelId: string;
  isLetter: boolean;
  informed: boolean;
  canSubmit: boolean;
  notif: ReturnType<typeof useNotificationMutations>;
  heading: string | null;
  /** Kreska nad pismem — kolejne pismo tego samego właściciela. */
  separated: boolean;
}) {
  const [detachErr, setDetachErr] = useState<string | null>(null);
  const zwrotka = item.paper_dispatch_confirmation ?? item.print_order_info?.receipt_confirmation ?? null;
  const shared = item.letter_shared_with ?? [];
  const detaching = notif.detach.isPending && notif.detach.variables?.itemId === item.id;
  const detach = () => {
    const what = item.print_order_info ? `zlecenie wydruku #${item.print_order_info.number}` : "pismo z Dziennika";
    if (!window.confirm(
      `Odpiąć ${what} od tego właściciela? Pismo zostaje w Dzienniku i można je podpiąć ponownie; ` +
      "znika tylko jego powiązanie z właścicielem (sprawa listu, jeśli to jej jedyne pismo).",
    )) return;
    setDetachErr(null);
    notif.detach.mutate(
      { itemId: item.id },
      {
        onError: (e: unknown) =>
          setDetachErr(
            (e as { response?: { data?: { errors?: { detail?: string } } } })?.response?.data?.errors?.detail ||
              "Nie udało się odpiąć pisma.",
          ),
      },
    );
  };
  return (
    <div className={clsx("space-y-1.5", separated && "border-t border-[var(--color-border-muted)] pt-1.5")}>
      {heading && (
        <span className="section-label">
          {heading}
          {item.date_sent && <> · {fmtDate(item.date_sent)}</>}
        </span>
      )}
      {/* Stan wpisu w dzienniku DLC_02 wraz z odnośnikiem do „Moich pism do wysłania" dla
          autora — ta sama linijka, co przy wniosku w zakładce Korespondencja. Bez niej z panelu
          listów widać było wyłącznie surowy status („queued"), bez przejścia do pozycji rejestru. */}
      {item.paper_dispatch_info ? (
        <PaperDispatchLine dispatch={item.paper_dispatch_info} />
      ) : item.print_order_info ? (
        <PrintOrderLine order={item.print_order_info} />
      ) : (
        item.paper_dispatch_status && (
          <div className="text-[var(--color-text-muted)]">
            Status w Dzienniku: {DISPATCH_LABEL[item.paper_dispatch_status] ?? item.paper_dispatch_status}
          </div>
        )
      )}
      {shared.length > 0 && (
        <div className="text-[var(--color-text-muted)]">
          To samo pismo podpięte też pod:{" "}
          {shared.map((sw, i) => (
            <Fragment key={`${sw.owner}-${i}`}>
              {i > 0 && ", "}
              <span className="text-[var(--color-text-secondary)]">{sw.owner_name}</span>
              {sw.parcel !== parcelId && ` (dz. ${sw.parcel_label}${sw.same_project ? "" : ", inny projekt"})`}
              {sw.letter_outcome && (
                <span className={sw.letter_outcome === "agreement" ? "text-emerald-600" : "text-red-600"}>
                  {" "}— {sw.letter_outcome_display}
                </span>
              )}
            </Fragment>
          ))}
          . Wynik wpisujesz osobno dla każdego właściciela.
        </div>
      )}
      {zwrotka ? (
        <a
          className="text-[var(--color-primary)] hover:underline inline-flex items-center gap-1"
          href={zwrotka.url ?? "#"} target="_blank" rel="noreferrer"
        >
          <FileText size={12} /> Podgląd potwierdzenia odbioru (PDF)
        </a>
      ) : !isLetter ? (
        <span className="text-[var(--color-text-muted)]">
          Brak zwrotki w Dzienniku (jeszcze nie wpięta przez dział obiegu).
        </span>
      ) : null}
      {/* A letter (consent mode) never resolves the uzgodnienie by itself — the koordynator/
          dyrektor techniczny records the per-owner outcome (uzgodnienie / odmowa) right away;
          the zwrotka / "potwierdzenie listu" is a separate, organisational fact. */}
      {isLetter && (
        <LetterConfirmation item={item} confirm={notif.confirmLetter} setOutcome={notif.setOutcome} />
      )}
      {!informed && canSubmit && !isLetter && (
        <div>
          <Button
            size="sm"
            variant="secondary"
            loading={notif.confirmDelivered.isPending}
            onClick={() => notif.confirmDelivered.mutate({ itemId: item.id })}
            title="Po obejrzeniu zwrotki (lub uznaniu doręczenia po podwójnym awizie) zamknij obowiązek poinformowania tego właściciela"
          >
            Uznaj jako dostarczone
          </Button>
        </div>
      )}
      {isLetter && item.can_detach_letter && (
        <div>
          <Button
            size="sm"
            variant="ghost"
            loading={detaching}
            onClick={detach}
            title="Cofnij podpięcie tego pisma pod właściciela — np. podpięte pod złą osobę. Możliwe, dopóki nie ma wyniku (uzgodnienie / odmowa). Pismo zostaje w Dzienniku."
          >
            <Unlink size={12} /> Odepnij pismo
          </Button>
          {detachErr && <p className="text-red-600">{detachErr}</p>}
        </div>
      )}
    </div>
  );
}

// ── Zawiadomienia o demontażu (NOTIFICATION) — inform all owners; zwrotka closes each ──
// Eksportowane dla testu — panel niesie stan wysyłki z dziennika DLC_02, a renderuje się
// głęboko w szufladzie działki (ParcelDrawer).
export function NotificationSection({
  parcel,
  connectionId,
  canSubmit,
  variant = "notification",
}: {
  parcel: Parcel;
  connectionId: string;
  canSubmit: boolean;
  // "notification" = zawiadomienie o demontażu (zwrotka kończy obowiązek);
  // "letter" = zwykły list do właściciela przy trybie "wymaga pozyskania" —
  // ta sama maszyneria (Dziennik DLC_02 + zwrotka), ale list NIE rozstrzyga
  // uzgodnienia, więc nie oferujemy "Uznaj jako dostarczone".
  variant?: "notification" | "letter";
}) {
  const isLetter = variant === "letter";
  const L = isLetter
    ? {
        empty: "Brak właścicieli — dodaj właścicieli, aby wysłać listy.",
        notSent: "List jeszcze nie wysłany do tego właściciela.",
        securedLabel: "Uzgodniono",
        trigger: "Wyślij list",
        panelHeader: "Listy — osobne pismo dla każdego właściciela",
        sendError: "Nie udało się wysłać listów.",
        footer:
          "1 właściciel = 1 osobna korespondencja. Pisma trafiają do Dziennika korespondencji (DLC_02) " +
          "jako wychodzące — dział obiegu je wysyła i wpina zwrotkę, która potwierdza doręczenie. " +
          "Pismo, które już wyszło przez Dziennik (zlecenie wydruku, wpis rejestru), podepniesz przyciskiem " +
          "„Podepnij pismo z Dziennika” — także pod kilku właścicieli (jedna koperta do współwłaścicieli, ta sama " +
          "osoba na innej działce); dopóki nie ma wyniku, można je odpiąć. Otwarta sprawa listu czyta się przy " +
          "właścicielu jako „W trakcie”. Po potwierdzeniu doręczenia wpisz wynik osobno dla każdego właściciela: " +
          "„Uzgodnienie” pozyskuje tytuł (zgoda właściciela), „Odmowa” ustawia tytuł „Odmowa”.",
      }
    : {
        empty: "Brak właścicieli — dodaj właścicieli, aby wysłać zawiadomienia.",
        notSent: "Zawiadomienie jeszcze nie wysłane do tego właściciela.",
        securedLabel: "Poinformowano",
        trigger: "+ Wyślij zawiadomienia",
        panelHeader: "Zawiadomienia — osobne pismo dla każdego właściciela",
        sendError: "Nie udało się wysłać zawiadomień.",
        footer:
          "1 właściciel = 1 osobna korespondencja. Pisma trafiają do Dziennika korespondencji (DLC_02) " +
          "jako wychodzące — dział obiegu je wysyła i wpina zwrotkę. Działka „uzgodniona”, gdy wszyscy poinformowani.",
      };
  const notif = useNotificationMutations(connectionId, parcel.id, isLetter);
  const { data: cases } = useProceedings({ connection: connectionId, owner_parcel: parcel.id });
  // Istniejące pismo z Dziennika (DLC_02) jako list: kandydatów dociągamy dopiero po
  // otwarciu wyboru przy właścicielu — panel nie pyta Dziennika bez potrzeby.
  const [attachFor, setAttachFor] = useState<string | null>(null);
  const [attachEntry, setAttachEntry] = useState("");
  // Pod kogo podpinamy wybrane pismo — domyślnie właściciel, przy którym otwarto wybór; jedną
  // kopertę do współwłaścicieli podpina się pod wszystkich naraz (wynik i tak per właściciel).
  const [attachOwners, setAttachOwners] = useState<Set<string>>(new Set());
  const [attachErr, setAttachErr] = useState<string | null>(null);
  const { data: attachable } = useAttachableLetters(connectionId, isLetter && attachFor !== null);
  const closeAttach = () => { setAttachFor(null); setAttachEntry(""); setAttachOwners(new Set()); setAttachErr(null); };
  const pickedLetter = attachable?.find((a) => `${a.kind}:${a.id}` === attachEntry) ?? null;
  // Właściciele, którzy już mają TO pismo — drugi raz pod tego samego się nie da.
  const alreadyOn = new Set((pickedLetter?.attached_to ?? []).map((a) => a.owner));
  const attachTargets = [...attachOwners].filter((id) => !alreadyOn.has(id));
  const attachedNote = (a: AttachableLetter) => {
    const names = a.attached_to.map((t) => (t.parcel === parcel.id ? t.owner_name : `${t.owner_name} (dz. ${t.parcel_label})`));
    const parts: string[] = [];
    if (names.length) parts.push(`podpięte: ${names.slice(0, 2).join(", ")}${names.length > 2 ? ` +${names.length - 2}` : ""}`);
    if (a.attached_elsewhere) parts.push(`w innych projektach: ${a.attached_elsewhere}`);
    return parts.length ? ` · ${parts.join(" · ")}` : "";
  };
  const attachControl = (ownerId: string) => {
    if (!isLetter || !canSubmit) return null;
    if (attachFor !== ownerId) {
      return (
        <div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => { setAttachFor(ownerId); setAttachEntry(""); setAttachOwners(new Set([ownerId])); setAttachErr(null); }}
            title="Pismo, które już wyszło przez Dziennik (zlecenie wydruku z wysyłką albo wpis z „Moich pism do wysłania”), podpinasz tu jako list — bez drugiego wpisu. To samo pismo możesz podpiąć pod kilku właścicieli (jedna koperta do współwłaścicieli, ta sama osoba na innej działce). Doręczenie potwierdza zwrotka / potwierdzenie odbioru w Dzienniku, a wynik — uzgodnienie albo odmowę — wpisujesz osobno dla każdego właściciela"
          >
            <Link2 size={12} /> Podepnij pismo z Dziennika
          </Button>
        </div>
      );
    }
    const pendingAuto = parcel.owners.find((po) => po.id === ownerId)?.wpw_mail_letter;
    return (
      <div className="space-y-1.5 border border-[var(--color-border)] p-2">
        <span className="section-label">Pismo z Dziennika (DLC_02)</span>
        {/* Podpięte pismo zastępuje automatyczny list WPW — informacja PRZED podpięciem. */}
        {pendingAuto && ["ready", "scheduled", "held", "blocked"].includes(pendingAuto.state) && (
          <p className="text-amber-700 dark:text-amber-400" data-testid="wpw-mail-cancel-notice-attach">
            ⚠ Ten właściciel ma automatyczny list WPW w kolejce ({pendingAuto.state_display}
            {pendingAuto.due_at ? `, termin ${fmtDate(pendingAuto.due_at)}` : ""}) — podpięcie pisma go anuluje.
          </p>
        )}
        {!attachable ? (
          <Spinner size="sm" />
        ) : attachable.length === 0 ? (
          <p className="text-[var(--color-text-muted)]">
            Brak pism do podpięcia — kandydatami są wysłane zlecenia wydruku z wysyłką oraz pisma wychodzące
            z „Moich pism do wysłania” (własne albo księgowane na to przedsięwzięcie).
          </p>
        ) : (
          // Lista z filtrem po nazwie i przewijaniem — pism w Dzienniku bywa setki.
          <SearchableSelect
            options={attachable.map((a) => ({
              value: `${a.kind}:${a.id}`,
              label:
                `${a.label} · ${a.status_display}` +
                (a.date ? ` · ${fmtDate(a.date)}` : "") +
                (a.delivered ? " · doręczone" : "") +
                attachedNote(a),
            }))}
            value={attachEntry}
            placeholder="Wybierz pismo…"
            onChange={(e) => setAttachEntry(e.target.value)}
          />
        )}
        {parcel.owners.length > 1 && (
          <div className="space-y-1">
            <span className="text-[var(--color-text-muted)]">Podepnij pod właścicieli tej działki:</span>
            {parcel.owners.map((po) => {
              const has = alreadyOn.has(po.id);
              return (
                <label key={po.id} className={clsx("flex items-center gap-2", has ? "opacity-60" : "cursor-pointer")}>
                  <input
                    type="checkbox"
                    disabled={has}
                    checked={has || attachOwners.has(po.id)}
                    onChange={(e) =>
                      setAttachOwners((cur) => {
                        const n = new Set(cur);
                        if (e.target.checked) n.add(po.id); else n.delete(po.id);
                        return n;
                      })
                    }
                  />
                  <span className="text-[var(--color-text-primary)]">{po.full_name}</span>
                  {has && <span className="text-[var(--color-text-muted)]">(już podpięte)</span>}
                </label>
              );
            })}
          </div>
        )}
        {pickedLetter && parcel.owners.length === 1 && alreadyOn.has(ownerId) && (
          <p className="text-[var(--color-text-muted)]">To pismo jest już podpięte pod tego właściciela.</p>
        )}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            disabled={!attachEntry || attachTargets.length === 0}
            loading={notif.attach.isPending}
            onClick={() => {
              setAttachErr(null);
              const [kind, ...rest] = attachEntry.split(":");
              notif.attach.mutate(
                { ownerIds: attachTargets, kind: kind as "register" | "print_order", id: rest.join(":") },
                {
                  onSuccess: closeAttach,
                  onError: (e: unknown) =>
                    setAttachErr(
                      (e as { response?: { data?: { errors?: { detail?: string } } } })?.response?.data?.errors?.detail ||
                        "Nie udało się podpiąć pisma.",
                    ),
                },
              );
            }}
          >
            {attachTargets.length > 1 ? `Podepnij (${attachTargets.length})` : "Podepnij"}
          </Button>
          <Button size="sm" variant="ghost" onClick={closeAttach}>Anuluj</Button>
        </div>
        {attachErr && <p className="text-red-600">{attachErr}</p>}
      </div>
    );
  };
  const [sending, setSending] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [filesByOwner, setFilesByOwner] = useState<Record<string, File[]>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<string | null>(null);

  // owner_id → EVERY letter for that owner, newest first (its DLC_02 object carries the zwrotka).
  // An owner can hold several: a re-send, or one Dziennik object attached under co-owners / the
  // same person on another parcel — each keeps its own outcome. The letter variant lives on a
  // LETTER-scope thread, the demontaż variant on a NOTIFICATION-scope one — so each section only
  // rolls up its own sends and never mislabels a list as a zawiadomienie.
  const wantScope = isLetter ? "letter" : "notification";
  const itemsByOwner = new Map<string, ProceedingItem[]>();
  (cases ?? [])
    .filter((c) => c.scope === wantScope)
    .forEach((c) =>
      c.items.forEach((it) => {
        if (!it.recipient_owner) return;
        itemsByOwner.set(it.recipient_owner, [...(itemsByOwner.get(it.recipient_owner) ?? []), it]);
      }),
    );
  itemsByOwner.forEach((list) => list.sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? "")));

  const openSend = () => {
    const fresh = parcel.owners.filter((o) => !itemsByOwner.has(o.id)).map((o) => o.id);
    setPicked(new Set(fresh.length ? fresh : parcel.owners.map((o) => o.id)));
    setFilesByOwner({});
    setErr(null);
    setSending(true);
  };

  const submit = () => {
    setErr(null);
    notif.send.mutate(
      { owners: [...picked].map((id) => ({ id, files: filesByOwner[id] ?? [] })) },
      {
        onSuccess: () => setSending(false),
        onError: (e: unknown) =>
          setErr(
            (e as { response?: { data?: { errors?: { detail?: string } } } })?.response?.data?.errors?.detail ||
              L.sendError,
          ),
      },
    );
  };

  return (
    <div className="space-y-2">
      {parcel.owners.length === 0 ? (
        <p className="text-[11px] font-mono text-[var(--color-text-muted)]">{L.empty}</p>
      ) : (
        <ul className="space-y-1.5">
          {parcel.owners.map((o) => {
            const items = itemsByOwner.get(o.id) ?? [];
            const item = items[0];
            const informed = o.access?.status === "secured";
            const isOpen = expanded.has(o.id);
            const dispatchLabel = item?.paper_dispatch_status
              ? DISPATCH_LABEL[item.paper_dispatch_status] ?? item.paper_dispatch_status
              : item?.print_order_info
                ? `wydruk: ${item.print_order_info.status_display}`
                : "";
            return (
              <li key={o.id} className="text-[11px] font-mono border border-[var(--color-border-muted)]">
                <button
                  type="button"
                  className="w-full flex items-center justify-between gap-2 p-2 text-left hover:bg-[var(--color-bg-elevated)]"
                  onClick={() => setExpanded((s) => { const n = new Set(s); if (isOpen) n.delete(o.id); else n.add(o.id); return n; })}
                >
                  <span className="inline-flex items-center gap-1.5 text-[var(--color-text-primary)] truncate">
                    <ChevronRight size={12} className={`shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                    {o.full_name}
                  </span>
                  {informed ? (
                    <span className="inline-flex items-center gap-1 text-emerald-600 shrink-0"><CheckCircle2 size={12} /> {L.securedLabel}</span>
                  ) : item ? (
                    <span className={`shrink-0 ${item.letter_outcome === "refusal" ? "text-red-600" : "text-amber-600"}`}>
                      {item.letter_outcome
                        ? item.letter_outcome_display
                        : item.print_order_info && !["sent", "returned"].includes(item.print_order_info.status)
                          // list automatyczny WPW podpięty od razu po zleceniu — jeszcze nie wyszedł
                          ? `Zlecono wydruk · ${item.print_order_info.status_display}`
                          : `Wysłane${dispatchLabel ? ` · ${dispatchLabel}` : ""}`}
                      {items.length > 1 && ` · pism: ${items.length}`}
                    </span>
                  ) : (
                    <span className="text-[var(--color-text-muted)] shrink-0">Nie wysłano</span>
                  )}
                </button>
                {isOpen && (
                  <div className="px-2 pb-2 pt-1.5 space-y-1.5 border-t border-[var(--color-border-muted)]">
                    {items.length === 0 ? (
                      <p className="text-[var(--color-text-muted)]">{L.notSent}</p>
                    ) : (
                      items.map((it, idx) => (
                        <OwnerLetterEntry
                          key={it.id}
                          item={it}
                          parcelId={parcel.id}
                          isLetter={isLetter}
                          informed={informed}
                          canSubmit={canSubmit}
                          notif={notif}
                          heading={items.length > 1 ? (idx === 0 ? "Najnowsze pismo" : "Wcześniejsze pismo") : null}
                          separated={idx > 0}
                        />
                      ))
                    )}
                    {attachControl(o.id)}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {canSubmit && parcel.owners.length > 0 && !sending && (
        <Button size="sm" variant="secondary" onClick={openSend}>{L.trigger}</Button>
      )}

      {sending && (
        <div className="border border-[var(--color-border)] p-2 space-y-2">
          <span className="section-label">{L.panelHeader}</span>
          <ul className="space-y-2">
            {parcel.owners.map((o) => {
              const checked = picked.has(o.id);
              return (
                <li key={o.id} className="border border-[var(--color-border-muted)] p-2 space-y-1.5">
                  <label className="flex items-center gap-2 text-[11px] font-mono cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) =>
                        setPicked((s) => {
                          const n = new Set(s);
                          if (e.target.checked) n.add(o.id); else n.delete(o.id);
                          return n;
                        })
                      }
                    />
                    <span className="text-[var(--color-text-primary)]">{o.full_name}</span>
                    {itemsByOwner.has(o.id) && <span className="text-[var(--color-text-muted)]">(już wysłano)</span>}
                  </label>
                  {/* Ręczny list zastępuje automatyczny (ścieżka listowna WPW) — informacja PRZED wysyłką. */}
                  {checked && isLetter && o.wpw_mail_letter && ["ready", "scheduled", "held", "blocked"].includes(o.wpw_mail_letter.state) && (
                    <p className="text-[11px] font-mono text-amber-700 dark:text-amber-400" data-testid="wpw-mail-cancel-notice">
                      ⚠ Ten właściciel ma automatyczny list WPW w kolejce ({o.wpw_mail_letter.state_display}
                      {o.wpw_mail_letter.due_at ? `, termin ${fmtDate(o.wpw_mail_letter.due_at)}` : ""}) — ręczna wysyłka go anuluje.
                    </p>
                  )}
                  {checked && (
                    <MultiFileInput
                      files={filesByOwner[o.id] ?? []}
                      onChange={(f) => setFilesByOwner((s) => ({ ...s, [o.id]: f }))}
                      label="Pismo dla tego właściciela"
                    />
                  )}
                </li>
              );
            })}
          </ul>
          {err && <p className="text-[11px] text-amber-600">{err}</p>}
          <div className="flex gap-2">
            <Button size="sm" disabled={picked.size === 0 || notif.send.isPending} loading={notif.send.isPending} onClick={submit}>
              Przekaż do Dziennika ({picked.size})
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSending(false)}>Anuluj</Button>
          </div>
        </div>
      )}

      <p className="text-[10px] font-mono text-[var(--color-text-muted)]">{L.footer}</p>
    </div>
  );
}

// ── "Podstawa dostępu" — read-only summary of the parcel access rollup ────────
function AccessSummary({ parcel }: { parcel: Parcel }) {
  const s = parcel.access_summary;
  const bases = Object.entries(s.bases ?? {});
  return (
    <div className="space-y-2 text-[11px] font-mono">
      {parcel.access_mode === "not_required" ? (
        <p className="text-[var(--color-text-secondary)]">Nie wymaga pozyskania ani powiadomienia.</p>
      ) : parcel.access_mode === "notification" ? (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[var(--color-text-secondary)]">
          <span>poinformowano: <span className="text-[var(--color-text-primary)] font-bold">{s.owners_agreed}/{s.owners_total}</span></span>
          {s.problem > 0 && <span className="text-red-600">problemy: {s.problem}</span>}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[var(--color-text-secondary)]">
            <span>rozstrzygnięto: <span className="text-[var(--color-text-primary)] font-bold">{s.owners_agreed}/{s.owners_total}</span></span>
            {s.stale > 0 && <span className="text-amber-600">nieaktualna wersja: {s.stale}</span>}
            {s.refused > 0 && <span className="text-red-600">odmowy: {s.refused}</span>}
            {s.problem > 0 && <span className="text-red-600">problemy: {s.problem}</span>}
          </div>
          {bases.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {bases.map(([code, count]) => (
                <span key={code} className="px-1.5 py-0.5 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]">
                  {code}: {count}
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── sprawy / postępowania obejmujące tę działkę (read-only, klik → podgląd) ──
function ParcelProceedings({ connectionId, parcelId }: { connectionId: string; parcelId: string }) {
  const { data: cases, isLoading } = useProceedings({ connection: connectionId, owner_parcel: parcelId });
  const [openCase, setOpenCase] = useState<Proceeding | null>(null);
  if (isLoading) return <div className="py-3 flex justify-center"><Spinner /></div>;
  if (!cases || cases.length === 0)
    return <p className="text-[11px] font-mono text-[var(--color-text-muted)]">Brak postępowań obejmujących tę działkę. Załóż sprawę w zakładce „Sprawy / Korespondencja".</p>;
  return (
    <>
      <ul className="space-y-1.5">
        {cases.map((cs) => (
          <li key={cs.id}>
            <button
              type="button"
              onClick={() => setOpenCase(cs)}
              className="w-full flex flex-wrap items-center gap-1.5 text-left text-[11px] font-mono text-[var(--color-text-secondary)] hover:underline"
            >
              <ProceedingScopeBadge scope={cs.scope} />
              <ProceedingStatusBadge status={cs.status} />
              {cs.status === "closed" && <OutcomeBadge outcome={cs.outcome} />}
              <span className="text-[var(--color-text-primary)]">{cs.type_name}</span>
              {cs.office_name && <span>· {cs.office_name}</span>}
              {cs.route_version_label && <span className="text-[var(--color-text-muted)]">· trasa {cs.route_version_label}</span>}
            </button>
          </li>
        ))}
      </ul>
      <ProceedingDetailModal proceeding={openCase} onClose={() => setOpenCase(null)} />
    </>
  );
}

// ── read-only podgląd sprawy/postępowania w modalu (na wierzchu drawera działki) ──
export function ProceedingDetailModal({ proceeding, onClose }: { proceeding: Proceeding | null; onClose: () => void }) {
  return (
    <Modal open={!!proceeding} onClose={onClose} title={proceeding?.type_name ?? ""} size="lg">
      {proceeding && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <ProceedingScopeBadge scope={proceeding.scope} />
            <ProceedingStatusBadge status={proceeding.status} />
            {proceeding.status === "closed" && <OutcomeBadge outcome={proceeding.outcome} />}
            {proceeding.status !== "closed" && (
              <KpaBadge deadline={proceeding.kpa_deadline} status={proceeding.kpa_deadline_status} overdue={proceeding.kpa_overdue} />
            )}
          </div>
          {proceeding.type_note && <p className="text-[13px] text-[var(--color-text-secondary)]">{proceeding.type_note}</p>}
          <dl className="grid grid-cols-[max-content_1fr] gap-x-2 gap-y-1 text-[13px] text-[var(--color-text-secondary)]">
            {proceeding.office_name && (
              <>
                <dt className="section-label self-center">Urząd</dt>
                <dd>{proceeding.office_name}</dd>
              </>
            )}
            {proceeding.route_version_label && (
              <>
                <dt className="section-label self-center">Trasa</dt>
                <dd>{proceeding.route_version_label}</dd>
              </>
            )}
            <dt className="section-label self-center">Założono</dt>
            <dd>{fmtDate(proceeding.created_at)}</dd>
            {proceeding.status === "closed" && (
              <>
                <dt className="section-label self-center">Zamknięto</dt>
                <dd>{proceeding.closed_by_name ?? "—"}{proceeding.closed_at ? ` · ${fmtDate(proceeding.closed_at)}` : ""}</dd>
              </>
            )}
          </dl>
          <div>
            <p className="section-label mb-1.5">Pozycje ({proceeding.items.length})</p>
            {proceeding.items.length === 0 ? (
              <p className="text-[11px] font-mono text-[var(--color-text-muted)]">Brak pozycji.</p>
            ) : (
              <ul className="space-y-2">
                {proceeding.items.map((it) => <ProceedingItemPreview key={it.id} item={it} />)}
              </ul>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

// one thread position (application / response / event), read-only mirror of CorrespondenceTab's ItemRow
function ProceedingItemPreview({ item }: { item: ProceedingItem }) {
  if (item.kind === "application") {
    return (
      <li className="border-l-2 border-blue-400 pl-3 py-1.5">
        <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-mono">
          <span className="text-blue-700 font-bold">→ Wniosek</span>
          <ApplicationStatusBadge status={item.app_status} />
          {item.our_ref && <span className="text-[var(--color-text-secondary)]">znak: {item.our_ref}</span>}
          {item.channel_display && <span className="text-[var(--color-text-secondary)]">· {item.channel_display}</span>}
          {item.date_sent ? (
            <span className="text-[var(--color-text-secondary)]">· wysłano {fmtDate(item.date_sent)}</span>
          ) : (
            <span className="text-[var(--color-text-secondary)]">· utworzono {fmtDate(item.created_at)}</span>
          )}
        </div>
        {item.note && <p className="text-[12px] text-[var(--color-text-secondary)] mt-1 whitespace-pre-wrap">{item.note}</p>}
        {item.files.length > 0 && <div className="mt-1"><AttachmentLinks files={item.files} /></div>}
      </li>
    );
  }
  if (item.kind === "response") {
    return (
      <li className="border-l-2 border-emerald-400 pl-3 py-1.5">
        <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-mono">
          <span className="text-emerald-700 font-bold">← Odpowiedź</span>
          {item.office_ref && <span className="text-[var(--color-text-secondary)]">znak: {item.office_ref}</span>}
          {item.date_received ? (
            <span className="text-[var(--color-text-secondary)]">· wpływ {fmtDate(item.date_received)}</span>
          ) : (
            <span className="text-[var(--color-text-secondary)]">· utworzono {fmtDate(item.created_at)}</span>
          )}
          {item.closes_thread && <span className="text-[var(--color-primary)] font-bold">[zamyka wątek]</span>}
          {item.closes_thread && item.outcome !== "none" && <OutcomeBadge outcome={item.outcome} />}
        </div>
        {item.note && <p className="text-[12px] text-[var(--color-text-secondary)] mt-1 whitespace-pre-wrap">{item.note}</p>}
        {item.files.length > 0 && <div className="mt-1"><AttachmentLinks files={item.files} /></div>}
      </li>
    );
  }
  return (
    <li className="border-l-2 border-[var(--color-border-strong)] pl-3 py-1.5">
      <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-mono text-[var(--color-text-secondary)]">
        <span className="font-bold">{item.event_type_display || "Zdarzenie"}</span>
        <span>· {fmtDate(item.created_at)}</span>
        {item.created_by_name && <span>· {item.created_by_name}</span>}
      </div>
      {item.note && <p className="text-[12px] text-[var(--color-text-secondary)] mt-1 whitespace-pre-wrap">{item.note}</p>}
      {item.files.length > 0 && <div className="mt-1"><AttachmentLinks files={item.files} /></div>}
    </li>
  );
}

// ── route course through this parcel, per version (ParcelRouteSegment) ───────
function ParcelRouteCourse({ connectionId, parcelId }: { connectionId: string; parcelId: string }) {
  const { data: segs, isLoading } = useParcelRouteSegments(connectionId, { parcel: parcelId });
  if (isLoading) return <div className="py-3 flex justify-center"><Spinner /></div>;
  if (!segs || segs.length === 0)
    return <p className="text-[11px] font-mono text-[var(--color-text-muted)]">Brak zapisanego przebiegu trasy przez tę działkę.</p>;
  return (
    <ul className="space-y-1">
      {segs.map((s) => (
        <li key={s.id} className="flex items-center justify-between gap-2 text-[11px] font-mono text-[var(--color-text-secondary)]">
          <span>
            wersja <span className="text-[var(--color-text-primary)]">{s.route_version_label ?? "—"}</span> · {fmtDateTime(s.created_at)}
          </span>
          <span className="text-[var(--color-text-primary)]">{s.length_m != null ? `${s.length_m} m` : "—"}</span>
        </li>
      ))}
    </ul>
  );
}

// ── per-parcel history (route changes + access titles + proceedings) ──────────
function ParcelHistory({ connectionId, parcelId }: { connectionId: string; parcelId: string }) {
  const { data: events, isLoading } = useProjectEvents(connectionId, { parcel: parcelId });
  if (isLoading) return <div className="py-4 flex justify-center"><Spinner /></div>;
  return <ProjectEventList events={events ?? []} />;
}

// ── drawer building blocks ───────────────────────────────────────────────────
function MetaCell({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="px-4 py-2 min-w-0">
      <p className="section-label">{label}</p>
      <p className="font-mono text-xs font-semibold text-[var(--color-text-primary)] truncate mt-0.5" title={typeof value === "string" ? value : undefined}>
        {value}
      </p>
    </div>
  );
}

/* Like MetaCell, but double-click turns the value into an inline editor.
   Enter / blur saves (only when changed); Escape cancels. */
function EditableMetaCell({
  label,
  value,
  onSave,
  multiline = false,
  emptyHint,
}: {
  label: string;
  value: string;
  onSave: (next: string) => void;
  multiline?: boolean;
  /** Shown muted in place of „—" while the value is blank (e.g. the fallback a
   *  document variable will use instead). */
  emptyHint?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const start = () => {
    setDraft(value);
    setEditing(true);
  };
  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next !== (value ?? "")) onSave(next);
  };

  return (
    <div
      className="px-4 py-2 min-w-0 group cursor-text"
      onDoubleClick={editing ? undefined : start}
      title={editing ? undefined : "Kliknij dwukrotnie, aby edytować"}
    >
      <p className="section-label flex items-center gap-1">
        {label}
        {!editing && (
          <Pencil size={9} className="opacity-0 group-hover:opacity-60 transition-opacity text-[var(--color-text-muted)]" />
        )}
      </p>
      {editing ? (
        multiline ? (
          <textarea
            autoFocus
            rows={2}
            className="input-base mt-0.5 w-full px-1 py-1 font-mono text-xs"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Escape") setEditing(false);
            }}
          />
        ) : (
          <input
            autoFocus
            className="input-base mt-0.5 h-6 w-full px-1 py-0 font-mono text-xs"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commit();
              } else if (e.key === "Escape") {
                setEditing(false);
              }
            }}
          />
        )
      ) : (
        <p
          className={`font-mono text-xs font-semibold text-[var(--color-text-primary)] mt-0.5 group-hover:text-[var(--color-primary)] transition-colors ${
            multiline ? "whitespace-pre-wrap break-words" : "truncate"
          }`}
          title={value || emptyHint || "—"}
        >
          {value || (emptyHint ? (
            <span className="font-normal text-[var(--color-text-muted)]">{emptyHint}</span>
          ) : "—")}
        </p>
      )}
    </div>
  );
}

type DrawerTabKey =
  | "status"
  | "access"
  | "owners"
  | "notes"
  | "wpw"
  | "notifications"
  | "regulacja"
  | "route"
  | "history";

type DrawerTabDef = { key: DrawerTabKey; label: string; icon: LucideIcon; badge?: string };

const DRAWER_HDR_CHIP =
  "text-[9px] font-mono uppercase tracking-wide px-1 py-px border border-[var(--color-border)] text-[var(--color-text-muted)] whitespace-nowrap";
// Siatka komórek meta (EditableMetaCell) w grupach „Dane ogólne": dwie kolumny z
// przegrodami, każda komórka z dolną kreską.
const DRAWER_META_GRID =
  "grid grid-cols-2 divide-x divide-[var(--color-border-muted)] [&>*]:border-b [&>*]:border-[var(--color-border-muted)]";
const DRAWER_META_GRID_1 = "grid grid-cols-1 [&>*]:border-b [&>*]:border-[var(--color-border-muted)]";
const ADD_MODE_BTN =
  "inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-[11px] font-medium uppercase tracking-wider transition-colors";

const kwAnalysisSummary = (p: Parcel): string => {
  const kw = p.kw_summary;
  if (!kw || kw.state === "none") return "nie — brak odpisu";
  if (kw.state === "pending") return "nie — odpis nieanalizowany";
  const when = kw.analyzed_at ? ` · ${fmtDate(kw.analyzed_at)}` : "";
  return kw.state === "ok"
    ? `tak — bez zastrzeżeń${when}`
    : `tak — ${kw.issues.length} ${plUwagi(kw.issues.length)}${when}`;
};

function DrawerTabs({
  tabs,
  active,
  onChange,
}: {
  tabs: DrawerTabDef[];
  active: DrawerTabKey;
  onChange: (k: DrawerTabKey) => void;
}) {
  return (
    <nav className="shrink-0 flex border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2 overflow-x-auto overflow-y-hidden">
      {tabs.map((t) => {
        const Icon = t.icon;
        return (
          <button
            key={t.key}
            onClick={() => onChange(t.key)}
            className={clsx(
              "px-3.5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-wider whitespace-nowrap border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5",
              active === t.key
                ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                : "border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]",
            )}
          >
            <Icon size={12} />
            {t.label}
            {t.badge && (
              <span className="text-[10px] font-mono px-1 py-0.5 border border-[var(--color-border)] leading-none">
                {t.badge}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}

// ── parcel detail drawer ─────────────────────────────────────────────────────
export function ParcelDrawer({ parcelId, connectionId, projectId, operator, onClose, initialTab }: { parcelId: string; connectionId: string; projectId: string; operator: string; onClose: () => void; initialTab?: DrawerTabKey }) {
  const { data: parcel } = useParcel(parcelId);
  // „pokaż nieaktualnych (N)” — właściciele, których ponowny wypis nie potwierdził.
  const [showOutdated, setShowOutdated] = useState(false);
  const pm = useParcelMutations(connectionId);
  // Same per-project gate as Sprawy/Korespondencja — mirrors backend `_can`, so
  // przedsięwzięcie coordinators are covered without an explicit grant.
  const { canSubmit } = useMyProjectGate(connectionId);
  // Ręczne „Uzgodniona" to fakt per właściciel: backend wpisuje tytuł „Pozyskany"
  // każdemu właścicielowi bez tytułu i wymaga do tego podstawy — pytamy o nią w
  // miejscu, zamiast nadpisywać sam status (po którym widoki liczone z tytułów, np.
  // prowadzenie przez podwykonawcę, pokazywały działkę jako nierozpoczętą).
  const { data: bases } = useAccessBasisTypes(true);
  const [okForm, setOkForm] = useState<{ basis: string; secured_at: string; note: string } | null>(null);
  const okNeedsBasis = !!parcel && parcel.access_mode !== "not_required" && parcel.owners.length > 0;
  // Tytuły a bieżąca wersja trasy — stan przycisku „Porównaj tytuły z aktualną trasą"
  // (backend title_reconcile): liczba tytułów poza bieżącą wersją, a gdy 0 — powód, dla
  // którego nie ma czego porównywać — trafia do tooltipa jako „NIEDOSTĘPNA (…)".
  const titleReconcile = useMemo(() => {
    const owners = parcel?.owners ?? [];
    const secured = owners.filter((o) => o.access?.status === "secured");
    const stale = secured.filter((o) => o.access?.is_stale).length;
    let disabledReason: string | null = null;
    if (stale === 0) {
      if (owners.length === 0) {
        disabledReason = "działka nie ma właścicieli — nie ma tytułów do porównania z trasą";
      } else if (secured.length === 0) {
        disabledReason = "żaden właściciel nie ma jeszcze pozyskanego tytułu — nie ma czego porównywać z trasą";
      } else {
        const version = secured[0].access?.route_version_label;
        disabledReason =
          `wszystkie pozyskane tytuły (${secured.length}) są na aktualnej wersji trasy` +
          `${version ? ` (${version})` : ""} — porównanie nie jest potrzebne`;
      }
    }
    return { stale, secured: secured.length, disabledReason };
  }, [parcel]);
  // Werdykty ostatniego porównania — per właściciel, tylko dla TEJ działki (mutacja jest
  // wspólna dla szuflady, więc po przejściu na inną działkę stary wynik nie ma się pokazać).
  const reconcileVerdicts = useMemo(() => {
    const res = pm.reconcileTitles.data?.result;
    if (!res || res.parcel !== parcel?.id) return [];
    return res.titles.map((t) => {
      if (t.verdict === "extended") {
        return {
          owner: t.owner,
          tone: "text-emerald-700 dark:text-emerald-400",
          text: `${t.owner_name}: przebieg i typy urządzeń bez zmian (≤ 0,7 m) — tytuł rozciągnięty na wersję ${res.current_version ?? "bieżącą"}.`,
        };
      }
      if (t.verdict === "changed") {
        return {
          owner: t.owner,
          tone: "text-amber-700 dark:text-amber-400",
          text: `${t.owner_name}: zmiana wobec wersji ${t.from_version ?? "tytułu"} — ${t.detail || "inny przebieg lub typ"}; tytuł zostaje do Twojej decyzji (nowa zgoda).`,
        };
      }
      return {
        owner: t.owner,
        tone: "text-[var(--color-text-muted)]",
        text: `${t.owner_name}: nie da się porównać z wersją ${t.from_version ?? "tytułu"} — ${t.detail || "brak danych"}; rozstrzygnij ręcznie.`,
      };
    });
  }, [pm.reconcileTitles.data, parcel?.id]);
  const location = useLocation();
  // Dodawanie właściciela poza listą: „Ręcznie" (formularz) albo „Z pliku CSV" (strefa
  // upuszczania) — jeden przełącznik, jeden panel; drugie kliknięcie zwija.
  const [addMode, setAddMode] = useState<"" | "manual" | "csv">("");
  const [ownerPrefill, setOwnerPrefill] = useState<OwnerPrefill | undefined>(undefined);
  const toggleAddMode = (mode: "manual" | "csv") => {
    if (mode === "manual") setOwnerPrefill(undefined);
    setAddMode((cur) => (cur === mode ? "" : mode));
  };
  const [uldkErr, setUldkErr] = useState<string | null>(null);
  const [ownErr, setOwnErr] = useState<string | null>(null);
  const [streetModal, setStreetModal] = useState(false);
  const [kwOpen, setKwOpen] = useState(false);
  // Właściciel-instytucja wpisany samą nazwą (office=null) → modal dopasowania urzędu;
  // wybrany rekord ląduje na TYM wpisie (bez nowego właściciela), a backend przelicza
  // kategorię lokalizacji i status działki.
  const [officePickerOwner, setOfficePickerOwner] = useState<ParcelOwner | null>(null);
  const drawerOwners = useOwnerMutations(parcelId, connectionId);
  // Szczegóły odczytu klasoużytku pod urządzeniem (metoda, powód, wycinki mapy) — ten
  // sam modal co z tabeli działek, otwierany z grupy „Odrolnienie".
  const [sourceOpen, setSourceOpen] = useState(false);
  const [tab, setTab] = useState<DrawerTabKey>(initialTab ?? "status");

  // Regulacja prawna (OMI) = procedura TAURON-a. Poza TAURON-em zakładka znika, ale
  // najpierw sprawdzamy, czy ktoś nie wgrał już tu załączników — wtedy zostawiamy do
  // nich dostęp (sam probe leci tylko w projektach nie-TAURON).
  const regulacja = isRegulacjaOperator(operator);
  const { data: regChecklist } = useRegulatoryChecklist(parcelId, !regulacja);
  const hasRegUploads = (regChecklist?.items ?? []).some((it) => it.source === "uploaded");
  // Licznik na zakładce — wątek i tak jest potrzebny kartom właścicieli (ten sam cache).
  const { data: notes } = useParcelNotes(parcelId);

  // WPW / notification tabs exist only for the matching access mode.
  const drawerTabs = useMemo<DrawerTabDef[]>(() => {
    if (!parcel) return [];
    return [
      { key: "status", label: "Dane ogólne", icon: CircleDot },
      { key: "access", label: "Podstawa dostępu", icon: KeyRound },
      { key: "owners", label: "Właściciele", icon: Users, badge: `${parcel.owners_agreed}/${parcel.owners_total}` },
      {
        key: "notes",
        label: "Notatki",
        icon: MessageSquare,
        ...(notes && notes.length > 0 ? { badge: String(notes.length) } : {}),
      },
      // WPW / Zawiadomienia are NOT separate tabs — they render inside the Status
      // tab, driven by the parcel's "Wymaga" (access_mode) selection.
      ...(regulacja || hasRegUploads
        ? [{ key: "regulacja" as const, label: "Załączniki (regulacja)", icon: FileStack }]
        : []),
      { key: "route", label: "Przebieg trasy", icon: Route },
      { key: "history", label: "Historia", icon: History },
    ];
  }, [parcel, regulacja, hasRegUploads, notes]);
  // Fall back when the active tab disappears (e.g. access mode changed away from WPW).
  const activeTab = drawerTabs.some((t) => t.key === tab) ? tab : "status";

  // Lock the page scroll behind the drawer while it is open.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  // Escape closes the drawer — but never when a nested modal/overlay is open
  // (it owns Escape), nor while an inline field is being edited (Escape cancels
  // that edit). Overlay clicks still do not close, to avoid accidental loss.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (document.querySelector("[data-modal-layer]")) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-black/30 backdrop-blur-[2px] animate-overlay-in"
      role="dialog"
      aria-modal="true"
    >
      <div className="animate-drawer-in flex h-full w-[90%] flex-col bg-[var(--color-bg-surface)] border-l border-[var(--color-border-strong)] shadow-[-8px_0_28px_rgba(0,0,0,0.14)] overflow-hidden">
        {!parcel ? (
          <div className="flex flex-1 items-center justify-center"><Spinner /></div>
        ) : (
          <>
            {/* ── header: teryt + red accent, action buttons, meta strip ── */}
            <header className="shrink-0 bg-[var(--color-bg-elevated)] border-b border-[var(--color-border-strong)]">
              <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 shrink-0"
                      style={{ backgroundColor: parcelDotColor(parcel.status) }}
                      title={STATUS_LABEL[parcel.status]}
                    />
                    <h3
                      className="font-mono text-base font-bold text-[var(--color-text-primary)] truncate cursor-copy select-all"
                      title="Kliknij, aby skopiować"
                      onClick={() => navigator.clipboard.writeText(parcel.teryt)}
                    >
                      {parcel.teryt}
                    </h3>
                    <ParcelStatusBadge status={parcel.status} />
                    {parcel.is_connected_entity && (
                      <span className={clsx(DRAWER_HDR_CHIP, "text-violet-700")} title="Działka podmiotu przyłączanego (nietranzytowa)">
                        przyłączany
                      </span>
                    )}
                    {parcel.is_manual && <span className={DRAWER_HDR_CHIP} title="Działka dodana ręcznie">ręczna</span>}
                    {!parcel.in_current_route && <span className={DRAWER_HDR_CHIP} title="Poza aktualną wersją trasy">poza trasą</span>}
                    {!parcel.geometry && <span className={clsx(DRAWER_HDR_CHIP, "text-amber-600")}>bez geom.</span>}
                    {parcel.kw_number && (
                      <span className={clsx(DRAWER_HDR_CHIP, "normal-case")} title="Numer księgi wieczystej">KW {parcel.kw_number}</span>
                    )}
                  </div>
                  <div className="h-[3px] w-10 bg-[var(--color-primary)] mt-2" />
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  {/* Plan sytuacyjny (formatka) tej działki — pobranie scalonego PDF
                      z jej prywatnego zestawu, jeśli już powstał (np. po splicie paczki). */}
                  {parcel.formatka_map?.url && (
                    <a
                      href={parcel.formatka_map.url}
                      download={`PLAN_SYT_dz-${(parcel.short_number || parcel.teryt).replace(/[^\p{L}\p{N}]+/gu, "-")}.pdf`}
                      title="Pobierz plan sytuacyjny (mapę) tej działki"
                      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-mono text-[var(--color-primary)] hover:bg-[var(--color-bg-surface)] transition-colors"
                    >
                      <FileStack size={15} /> Plan sytuacyjny
                    </a>
                  )}
                  <button
                    onClick={onClose}
                    title="Zamknij"
                    className="p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] hover:bg-[var(--color-bg-surface)] transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>
            </header>

            {/* ── section tabs ── */}
            <DrawerTabs tabs={drawerTabs} active={activeTab} onChange={setTab} />

            {/* ── scrollable body ── */}
            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
              {!parcel.geometry && (
                <div className="p-3 border border-amber-300 bg-amber-50/60 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <MapPin size={14} className="text-amber-600 shrink-0" />
                    <span className="text-[11px] font-mono text-amber-700">Brak geometrii — działka nie pokaże się na mapie.</span>
                    <Button
                      size="sm" variant="secondary" className="ml-auto" loading={pm.fetchUldk.isPending}
                      onClick={() => {
                        setUldkErr(null);
                        pm.fetchUldk.mutate(parcel.id, {
                          onError: (e: unknown) =>
                            setUldkErr(
                              (e as { response?: { data?: { errors?: { detail?: string } } } })?.response?.data?.errors?.detail ||
                                "ULDK nie zwrócił geometrii dla tego numeru.",
                            ),
                        });
                      }}
                    >
                      Dociągnij z ULDK
                    </Button>
                  </div>
                  {uldkErr && <p className="text-[11px] text-amber-600">{uldkErr}</p>}
                </div>
              )}

              {parcel.route_review_needed && (
                <div className="p-3 border border-amber-300 bg-amber-50 space-y-2">
                  <div className="flex items-center gap-1.5 text-amber-800 font-medium text-sm">
                    <AlertTriangle size={14} /> Zmiana trasy na tej działce — do weryfikacji
                  </div>
                  <p className="text-[11px] text-amber-700">
                    Trasa przesunęła się na działce, na której jest już postęp pozyskania. Zweryfikuj, czy
                    obejmuje on aktualny przebieg:
                  </p>
                  <ul className="text-[11px] text-amber-700 list-disc pl-4 space-y-0.5">
                    <li>tytuł <b>pozyskany</b> → „Zaakceptuj uzgodnienie dla aktualnej trasy" przy właścicielu albo nowe zlecenie WPW,</li>
                    <li>negocjator <b>w terenie</b> → zaktualizuj załączniki zlecenia (sekcja WPW) albo odklik poniżej.</li>
                  </ul>
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={pm.clearRouteReview.isPending}
                    onClick={() => pm.clearRouteReview.mutate({ id: parcel.id })}
                  >
                    Zmiana nie wymaga działania
                  </Button>
                </div>
              )}

              {activeTab === "status" && (
                <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-3 items-start">
                  {/* Statusy składowe: jedna kolumna na maszynę stanów (podstawa dostępu, sprawa,
                      wniosek w sprawie, pakiet WNU, wypis, MZ, MDCP, zlecenie WPW, deklaracja
                      negocjatora) z bieżącym statusem albo „brak" — pełna szerokość nad kolumnami. */}
                  <DrawerGroup
                    title="Statusy składowe"
                    icon={Table2}
                    className="md:col-span-2 xl:col-span-3"
                    bodyClassName="p-0"
                    action={<span className={DRAWER_HDR_CHIP}>wspólny = obiekt na kilka działek</span>}
                  >
                    <ParcelStatusMatrixTable parcelId={parcel.id} />
                  </DrawerGroup>
                  {/* kolumna 1: status i kwalifikacja + narzędzia zależne od trybu dostępu */}
                  <div className="flex flex-col gap-3">
                    <DrawerGroup
                      title="Status i kwalifikacja"
                      icon={CircleDot}
                      action={
                        <span className={DRAWER_HDR_CHIP}>{parcel.status_locked ? "ustawiono ręcznie" : "auto"}</span>
                      }
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <ParcelStatusBadge status={parcel.status} />
                        <select
                          className="input-base text-xs py-1 w-auto"
                          value={parcel.status}
                          onChange={(e) => {
                            const next = e.target.value;
                            if (next === "ok" && okNeedsBasis) {
                              setOkForm({ basis: "", secured_at: new Date().toISOString().slice(0, 10), note: "" });
                              return;
                            }
                            pm.setStatus.mutate({ id: parcel.id, status: next });
                          }}
                          title="Ręczna zmiana statusu — do czasu „Przywróć automatyczny” automat go nie nadpisze"
                        >
                          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                        </select>
                      </div>
                      {okForm && (
                        <div className="space-y-2 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-2">
                          <div className="text-[11px] text-[var(--color-text-muted)]">
                            Uzgodnienie zapisze się jako tytuł „Pozyskany” u każdego właściciela bez tytułu
                            ({parcel.owners.length}) — wskaż podstawę. Status działki policzy się z tytułów.
                          </div>
                          <select
                            className="input-base text-[11px] py-1 w-auto"
                            value={okForm.basis}
                            onChange={(e) => setOkForm((f) => f && { ...f, basis: e.target.value })}
                          >
                            <option value="">— podstawa tytułu —</option>
                            {(bases ?? []).map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
                          </select>
                          <div className="flex flex-wrap gap-2">
                            <label className="block">
                              <span className="section-label">Data pozyskania</span>
                              <DatePicker
                                className="text-[11px] py-1 w-36 mt-1"
                                value={okForm.secured_at}
                                onChange={(v) => setOkForm((f) => f && { ...f, secured_at: v })}
                              />
                            </label>
                            <label className="block flex-1 min-w-[10rem]">
                              <span className="section-label">Uwaga (opcjonalnie)</span>
                              <input
                                className="input-base text-[11px] py-1 mt-1"
                                value={okForm.note}
                                onChange={(e) => setOkForm((f) => f && { ...f, note: e.target.value })}
                              />
                            </label>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            <Button
                              size="sm"
                              disabled={!okForm.basis}
                              loading={pm.setStatus.isPending}
                              onClick={() =>
                                pm.setStatus.mutate(
                                  { id: parcel.id, status: "ok", note: okForm.note, basis: okForm.basis, secured_at: okForm.secured_at || undefined },
                                  { onSuccess: () => setOkForm(null) },
                                )
                              }
                            >
                              Oznacz uzgodnioną
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setOkForm(null)}>Anuluj</Button>
                          </div>
                        </div>
                      )}
                      {/* Tytuły dostępu a bieżąca wersja trasy: porównanie przebiegu + typów linii
                          i obiektów (0,7 m). Zawsze widoczne — gdy nie ma czego porównywać, przycisk
                          jest wyszarzony, a powód stoi w tooltipie po „NIEDOSTĘPNA". */}
                      <div className="border-t border-[var(--color-border-muted)] pt-2 space-y-1">
                        {/* title na opakowaniu, nie na przycisku: wyszarzony <button> nie dostaje
                            zdarzeń myszy (Firefox), więc jego własny tooltip by nie wyskoczył. */}
                        <span
                          className="inline-flex"
                          title={
                            "Porównaj przebieg trasy na tej działce w wersji tytułu z bieżącą wersją (odcinek, typy linii i obiekty, tolerancja 0,7 m). Bez zmiany tytuł wraca na bieżącą wersję sam; inaczej zostaje do Twojej decyzji." +
                            (titleReconcile.disabledReason ? ` NIEDOSTĘPNA (${titleReconcile.disabledReason})` : "")
                          }
                        >
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={titleReconcile.disabledReason !== null || pm.reconcileTitles.isPending}
                            loading={pm.reconcileTitles.isPending}
                            onClick={() => pm.reconcileTitles.mutate({ id: parcel.id })}
                          >
                            <Route size={12} /> Porównaj tytuły z aktualną trasą
                            {titleReconcile.stale > 0 ? ` (${titleReconcile.stale})` : ""}
                          </Button>
                        </span>
                        {reconcileVerdicts.map((v) => (
                          <div key={v.owner} className={clsx("text-[11px]", v.tone)}>{v.text}</div>
                        ))}
                      </div>
                      <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] font-mono text-[var(--color-text-muted)]">
                        <span>{parcel.status_changed_at ? `zmieniono ${fmtDate(parcel.status_changed_at)}` : "\u00a0"}</span>
                        {parcel.status_locked && (
                          <Button size="sm" variant="ghost" loading={pm.resetStatus.isPending} onClick={() => pm.resetStatus.mutate(parcel.id)}>
                            Przywróć automatyczny
                          </Button>
                        )}
                      </div>
                      <div
                        className="flex flex-col gap-1.5 border-t border-[var(--color-border-muted)] pt-2"
                        title="Czego wymaga ta działka: pozyskania zgody, tylko powiadomienia właścicieli (demontaż — kończy zwrotka), albo niczego"
                      >
                        <span className="section-label">Tytuł dostępu</span>
                        {PARCEL_ACCESS_MODE_OPTIONS.map((o) => (
                          <label key={o.value} className="flex items-center gap-2 text-xs cursor-pointer">
                            <input
                              type="radio"
                              name={`access-mode-${parcel.id}`}
                              className="accent-[var(--color-primary)]"
                              value={o.value}
                              checked={parcel.access_mode === o.value}
                              disabled={pm.setAccessMode.isPending}
                              onChange={() => pm.setAccessMode.mutate({ id: parcel.id, accessMode: o.value as ParcelAccessMode })}
                            />
                            {o.label}
                          </label>
                        ))}
                      </div>
                      <div className="flex flex-col gap-2 border-t border-[var(--color-border-muted)] pt-2">
                        <ParcelUsageControl
                          parcel={parcel}
                          pending={pm.setUsed.isPending}
                          onSet={(value) => pm.setUsed.mutate({ id: parcel.id, value })}
                        />
                        {/* Droga wg KW albo decyzji koordynatora — od niej zależy skrót
                            „droga + ponad 50% udziałów = Uzgodniona". */}
                        <ParcelRoadControl
                          parcel={parcel}
                          pending={pm.setRoad.isPending}
                          onSet={(value, note) => pm.setRoad.mutate({ id: parcel.id, value, note })}
                        />
                        {/* Ścieżka listowna WPW: blokada automatycznej wysyłki dla całej działki
                            (per właściciel — na jego karcie; jednorazowo — na liście listów). */}
                        <ParcelMailBlockControl parcel={parcel} canSubmit={canSubmit} />
                        {/* Podmiot przyłączany vs działka tranzytowa — rozstrzyga koordynator
                            przedsięwzięcia / dyrektor. Przestawia kategorię uzgodnienia (zegar)
                            i zmienia wzór kreskowania działki na mapie. */}
                        <label
                          className={`flex items-start gap-2 text-xs ${parcel.can_set_connected_entity ? "cursor-pointer" : "opacity-60"}`}
                          title={parcel.can_set_connected_entity
                            ? "Zaznacz, gdy działka należy do podmiotu przyłączanego (inwestora), a nie jest działką tranzytową. Na mapie dostaje osobny wzór kreskowania."
                            : "Oznaczyć może koordynator przedsięwzięcia albo dyrektor."}
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5 accent-[var(--color-primary)]"
                            checked={parcel.is_connected_entity}
                            disabled={!parcel.can_set_connected_entity || pm.setConnectedEntity.isPending}
                            onChange={(e) => pm.setConnectedEntity.mutate({ id: parcel.id, value: e.target.checked })}
                          />
                          <span>
                            <span className="font-medium">Działka podmiotu przyłączanego</span>
                            <span className="block text-[11px] text-[var(--color-text-muted)]">
                              {parcel.can_set_connected_entity
                                ? "Nietranzytowa — należy do inwestora, do którego prowadzi przyłącze."
                                : "Ustawia koordynator przedsięwzięcia albo dyrektor."}
                            </span>
                          </span>
                        </label>
                      </div>
                      {/* Status liczy się z tytułów dostępu, więc działka bywa „Uzgodniona"
                          mimo wiszącego postępowania — wiszące sprawy wprost, jako linki. */}
                      <div className="flex flex-col gap-1 border-t border-[var(--color-border-muted)] pt-2">
                        {parcel.open_proceedings.length > 0 ? (
                          <>
                            <span className="section-label font-bold text-red-600">
                              Niezakończone sprawy ({parcel.open_proceedings.length})
                            </span>
                            {parcel.open_proceedings.map((c) => (
                              <Link
                                key={c.id}
                                to={`${location.pathname}?tab=correspondence&proceeding=${c.id}`}
                                className="inline-flex items-center gap-1.5 text-xs text-[var(--color-primary)] hover:underline"
                                title="Otwórz sprawę w zakładce Sprawy"
                              >
                                <Gavel size={12} /> {c.label} <ChevronRight size={12} />
                              </Link>
                            ))}
                          </>
                        ) : (
                          <span className="section-label">Brak niezakończonych spraw</span>
                        )}
                      </div>
                    </DrawerGroup>

                    {/* Narzędzia trybu dostępu (nie osobne zakładki): wybór „Tytuł dostępu"
                        decyduje, co się pokazuje. */}
                    {parcel.access_mode === "consent" && (
                      <DrawerGroup title="WPW — zlecenie terenowe / kompleksowe" icon={HardHat}>
                        <WpwSection parcel={parcel} projectId={projectId} connectionId={connectionId} />
                      </DrawerGroup>
                    )}
                    {parcel.access_mode === "notification" && (
                      <DrawerGroup title="Zawiadomienie o demontażu" icon={Megaphone}>
                        <NotificationSection parcel={parcel} connectionId={connectionId} canSubmit={canSubmit} />
                      </DrawerGroup>
                    )}
                  </div>

                  {/* kolumna 2: ewidencja, księga wieczysta, listy */}
                  <div className="flex flex-col gap-3">
                    <DrawerGroup
                      title="Dane ewidencyjne"
                      icon={LandPlot}
                      bodyClassName="p-0"
                      action={
                        parcel.geometry && !parcel.ownership_code ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            loading={pm.fetchOwnership.isPending}
                            title="Brak kodu własności (grupa rejestrowa) — pobierz z geoportalu"
                            onClick={() => {
                              setOwnErr(null);
                              pm.fetchOwnership.mutate(parcel.id, {
                                onError: (e: unknown) =>
                                  setOwnErr(
                                    (e as { response?: { data?: { errors?: { detail?: string } } } })?.response?.data?.errors?.detail ||
                                      "Geoportal nie zwrócił kodu własności.",
                                  ),
                              });
                            }}
                          >
                            Pobierz z geoportalu
                          </Button>
                        ) : undefined
                      }
                    >
                      <div className={DRAWER_META_GRID}>
                        <EditableMetaCell
                          label="Województwo"
                          value={parcel.voivodeship}
                          onSave={(v) => pm.update.mutate({ id: parcel.id, payload: { voivodeship: v } })}
                        />
                        <EditableMetaCell
                          label="Powiat"
                          value={parcel.county}
                          onSave={(v) => pm.update.mutate({ id: parcel.id, payload: { county: v } })}
                        />
                        <EditableMetaCell
                          label="Gmina"
                          value={parcel.commune}
                          onSave={(v) => pm.update.mutate({ id: parcel.id, payload: { commune: v } })}
                        />
                        <EditableMetaCell
                          label="Obręb"
                          value={parcel.obreb}
                          onSave={(v) => pm.update.mutate({ id: parcel.id, payload: { obreb: v } })}
                        />
                        <EditableMetaCell
                          label="Jednostka ewidencyjna"
                          value={parcel.jednostka_ewidencyjna}
                          onSave={(v) => pm.update.mutate({ id: parcel.id, payload: { jednostka_ewidencyjna: v } })}
                        />
                        <EditableMetaCell
                          label="Kod własności"
                          value={parcel.ownership_code}
                          onSave={(v) => pm.update.mutate({ id: parcel.id, payload: { ownership_code: v } })}
                        />
                        <MetaCell label="Powierzchnia" value={fmtArea(parcel.area_m2)} />
                        <EditableMetaCell
                          label="Karta mapy"
                          value={parcel.karta_mapy}
                          onSave={(v) => pm.update.mutate({ id: parcel.id, payload: { karta_mapy: v } })}
                        />
                      </div>
                      {ownErr && <p className="px-3 pt-1.5 text-[11px] text-amber-600">{ownErr}</p>}
                      <p className="px-3 py-1.5 text-[10px] font-mono text-[var(--color-text-muted)]">
                        Dwuklik w wartość = edycja w miejscu.
                      </p>
                    </DrawerGroup>

                    {/* KW dostępna zawsze (nie tylko przy regulacji) — dane z księgi przydają
                        się też do art. 124, wniosków i ustalania właścicieli. */}
                    <DrawerGroup
                      title="Księga wieczysta"
                      icon={BookOpen}
                      bodyClassName="p-0"
                      action={
                        <span className={clsx(DRAWER_HDR_CHIP, kwAnalyzed(parcel) && "border-emerald-300 bg-emerald-50 text-emerald-700")}>
                          analiza: {kwAnalyzed(parcel) ? "tak" : "nie"}
                        </span>
                      }
                    >
                      <div className={DRAWER_META_GRID}>
                        <EditableMetaCell
                          label="Nr KW"
                          value={parcel.kw_number}
                          onSave={(v) => pm.update.mutate({ id: parcel.id, payload: { kw_number: v } })}
                        />
                        <MetaCell label="Analiza AI" value={kwAnalysisSummary(parcel)} />
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 px-3 py-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setKwOpen(true)}
                          title="Wgraj odpis z EKW — AI odczyta Dział II/III/IV i porówna właścicieli z wypisem"
                        >
                          <Sparkles size={12} /> Analiza KW (AI)
                        </Button>
                      </div>
                    </DrawerGroup>

                  </div>

                  {/* kolumna 3: adres i planowanie, opisy urządzeń */}
                  <div className="flex flex-col gap-3">
                    <DrawerGroup
                      title="Adres i planowanie"
                      icon={MapPin}
                      bodyClassName="p-0"
                      action={
                        parcel.geometry ? (
                          <Button size="sm" variant="ghost" onClick={() => setStreetModal(true)} title="Zaproponuj miejscowość i adres działki (do oświadczeń) na podstawie map">
                            Pobierz adres
                          </Button>
                        ) : undefined
                      }
                    >
                      <div className={DRAWER_META_GRID_1}>
                        <EditableMetaCell
                          label="Miejscowość"
                          value={parcel.locality ?? ""}
                          emptyHint={
                            parcel.document_locality
                              ? `— (w dokumentach: ${parcel.document_locality}, z obrębu/gminy)`
                              : undefined
                          }
                          onSave={(v) => pm.update.mutate({ id: parcel.id, payload: { locality: v } })}
                        />
                        <EditableMetaCell
                          label="Ulica i nr (do oświadczeń)"
                          value={parcel.street_and_number}
                          onSave={(v) => pm.update.mutate({ id: parcel.id, payload: { street_and_number: v } })}
                        />
                        <EditableMetaCell
                          label="Przeznaczenie w MPZP"
                          value={parcel.mpzp_designation}
                          onSave={(v) => pm.update.mutate({ id: parcel.id, payload: { mpzp_designation: v } })}
                        />
                      </div>
                      <div className="px-3 py-2">
                        <span className="section-label flex items-center gap-1.5">
                          <FileSearch size={12} /> Wypisy MPZP
                        </span>
                        <div className="mt-2">
                          <MpzpParcelOrdersSection parcelId={parcel.id} />
                        </div>
                      </div>
                    </DrawerGroup>

                    {/* Odrolnienie (wyłączenie z produkcji rolniczej): werdykt, decyzja ręczna
                        w obie strony, sugestia ELGW5 z klasoużytku POD urządzeniem (ze źródłem)
                        i powierzchnia, która idzie do wniosku. */}
                    <DrawerGroup
                      title="Odrolnienie"
                      icon={Ruler}
                      action={<OdrolnienieVerdictChip odr={parcel.odrolnienie} />}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="section-label">Decyzja</span>
                        <select
                          className="input-base text-xs py-1 w-auto max-w-[240px]"
                          value={parcel.odrolnienie?.override ?? ""}
                          disabled={pm.setOdrolnienie.isPending}
                          title="Wyłączenie z produkcji rolniczej: automat (sugestia ELGW5) albo decyzja ręczna w obie strony"
                          onChange={(e) =>
                            pm.setOdrolnienie.mutate({
                              id: parcel.id,
                              value: (e.target.value || null) as OdrolnienieOverride | null,
                            })
                          }
                        >
                          <option value="">Automat — {odrolnienieAutoLabel(parcel.odrolnienie)}</option>
                          <option value="required">Wymagane (ręcznie)</option>
                          <option value="not_required">Niewymagane (ręcznie)</option>
                        </select>
                      </div>
                      {parcel.odrolnienie?.override && (
                        <p className="text-[11px] text-[var(--color-text-secondary)]">
                          Ustawione ręcznie
                          {parcel.odrolnienie.override_by ? ` — ${parcel.odrolnienie.override_by}` : ""}
                          {parcel.odrolnienie.override_at ? `, ${fmtDate(parcel.odrolnienie.override_at)}` : ""}
                          {parcel.odrolnienie.override_note ? `: ${parcel.odrolnienie.override_note}` : "."}
                        </p>
                      )}
                      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-[11px]">
                        <dt className="section-label self-start pt-0.5">Sugestia ELGW5</dt>
                        <dd className="text-[var(--color-text-secondary)]">
                          {(parcel.odrolnienie?.auto ?? parcel.odrolnienie)?.reason || "—"}
                        </dd>
                        <dt className="section-label self-center">Pod urządzeniem</dt>
                        <dd className="flex flex-wrap items-center gap-1.5">
                          {(() => {
                            const src = parcel.odrolnienie?.source;
                            const reads = (src?.reads ?? []).filter((r) => r.in_project);
                            const under = Array.from(new Set(reads.map((r) => r.klasouzytek || "brak konturu"))).join(", ");
                            const chip = src ? LAND_CLASS_METHOD_CHIP[src.method] : undefined;
                            return (
                              <>
                                <span className={clsx("font-mono", !under && "text-[var(--color-text-muted)]")}>
                                  {under || "brak odczytu punktowego"}
                                </span>
                                {chip && (
                                  <button
                                    type="button"
                                    onClick={() => setSourceOpen(true)}
                                    className={`inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide px-1 py-px border ${chip.className} hover:bg-[var(--color-bg-hover)]`}
                                    title={`${src?.method_label ?? ""} — ${src?.why ?? ""}\n\nKliknij, żeby zobaczyć szczegóły odczytu.`}
                                  >
                                    <Crosshair size={9} /> {chip.label}
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </dd>
                        <dt className="section-label self-center">Wykaz działki</dt>
                        <dd className="font-mono text-[var(--color-text-secondary)]">
                          {[parcel.land_use, parcel.soil_class].filter(Boolean).join(" · ") ||
                            (parcel.geoportal_land_use ? `${parcel.geoportal_land_use} (geoportal)` : "—")}
                        </dd>
                        <dt className="section-label self-center">Do wyłączenia</dt>
                        <dd
                          className="font-mono text-[var(--color-text-secondary)]"
                          title={parcel.exclusion_area?.by_kind?.length
                            ? parcel.exclusion_area.by_kind.map((k) => `${k.count} × ${k.kind} = ${k.area_m2} m²`).join(", ")
                            : "Brak urządzeń naziemnych ze stawką powierzchni na tej działce"}
                        >
                          {parcel.exclusion_area?.area_m2 ? `${parcel.exclusion_area.area_m2} m²` : "—"}
                          {(parcel.exclusion_area?.missing_rates?.length ?? 0) > 0 && (
                            <span className="ml-1 text-amber-600" title={`Brak stawki dla: ${parcel.exclusion_area.missing_rates.join(", ")} — powierzchnia zaniżona.`}>
                              <AlertTriangle size={10} className="inline" />
                            </span>
                          )}
                        </dd>
                      </dl>
                    </DrawerGroup>

                    {/* Opisy urządzeń zasilają wyłącznie wniosek regulacyjny (#regulacja_urzadzenia_*#),
                        więc poza TAURON-em pokazujemy je tylko, gdy ktoś już coś wpisał. */}
                    {(regulacja || parcel.designed_devices_description || parcel.existing_devices_description) && (
                      <DrawerGroup title="Urządzenia — opis (regulacja)" icon={FileText} bodyClassName="p-0">
                        <div className={DRAWER_META_GRID_1}>
                          <EditableMetaCell
                            label="Projektowane"
                            value={parcel.designed_devices_description}
                            multiline
                            onSave={(v) => pm.update.mutate({ id: parcel.id, payload: { designed_devices_description: v } })}
                          />
                          <EditableMetaCell
                            label="Istniejące"
                            value={parcel.existing_devices_description}
                            multiline
                            onSave={(v) => pm.update.mutate({ id: parcel.id, payload: { existing_devices_description: v } })}
                          />
                        </div>
                      </DrawerGroup>
                    )}
                  </div>
                </section>
              )}

              {/* Podstawa dostępu = tytuły właścicieli + to, co je pozyskuje: sprawy
                  i postępowania obejmujące działkę oraz listy do właścicieli (jedna zakładka
                  zamiast trzech miejsc — decyzja 2026-09-24). */}
              {activeTab === "access" && (
                <section className="grid gap-3 md:grid-cols-2 items-start">
                  <DrawerGroup title="Podstawa dostępu" icon={KeyRound} className="md:col-span-2">
                    <AccessSummary parcel={parcel} />
                  </DrawerGroup>
                  <DrawerGroup title="Sprawy i postępowania" icon={FileText}>
                    <ParcelProceedings connectionId={connectionId} parcelId={parcelId} />
                  </DrawerGroup>
                  {parcel.access_mode === "consent" && (
                    <DrawerGroup title="Listy do właścicieli" icon={Send}>
                      <NotificationSection variant="letter" parcel={parcel} connectionId={connectionId} canSubmit={canSubmit} />
                      {/* Ścieżka listowna WPW: listy automatyczne (kolejka, blokady, zwroty). */}
                      <div className="mt-3 border-t border-[var(--color-border-muted)] pt-3">
                        <WpwMailLettersPanel parcel={parcel} />
                      </div>
                    </DrawerGroup>
                  )}
                </section>
              )}

              {activeTab === "owners" && (
                <section className="space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2 font-mono text-[10px] tracking-wider text-[var(--color-text-muted)]">
                      rozstrzygnięto{" "}
                      <span className="text-xs font-bold text-[var(--color-text-primary)]">
                        {parcel.owners_agreed}/{parcel.owners_total}
                      </span>
                      <span className="block h-1.5 w-32 border border-[var(--color-border)] bg-[var(--color-bg-surface)]" aria-hidden>
                        <span
                          className="block h-full bg-[var(--color-primary)]"
                          style={{
                            width: parcel.owners_total
                              ? `${Math.round((parcel.owners_agreed / parcel.owners_total) * 100)}%`
                              : 0,
                          }}
                        />
                      </span>
                    </div>
                    {(parcel.outdated_owners_total ?? 0) > 0 && (
                      <label
                        className="flex cursor-pointer select-none items-center gap-1.5 text-[11px] text-[var(--color-text-muted)]"
                        title="Właściciele oznaczeni jako nieaktualni po ponownie zamówionym wypisie — nie liczą się do uzgodnień; ich tytuły, WPW, WNU i sprawy zostają jako historia."
                      >
                        <input
                          type="checkbox"
                          className="accent-[var(--color-primary)] cursor-pointer"
                          checked={showOutdated}
                          onChange={(e) => setShowOutdated(e.target.checked)}
                        />
                        pokaż nieaktualnych ({parcel.outdated_owners_total})
                      </label>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="section-label">Dodaj właściciela:</span>
                      <div className="inline-flex border border-[var(--color-border-strong)]">
                        <button
                          type="button"
                          onClick={() => toggleAddMode("manual")}
                          className={clsx(
                            ADD_MODE_BTN,
                            addMode === "manual"
                              ? "bg-[var(--color-primary)] text-[var(--color-text-inverse)]"
                              : "text-[var(--color-text-primary)] hover:text-[var(--color-primary)]",
                          )}
                        >
                          <UserPlus size={12} /> Ręcznie
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleAddMode("csv")}
                          className={clsx(
                            ADD_MODE_BTN,
                            "border-l border-[var(--color-border-strong)]",
                            addMode === "csv"
                              ? "bg-[var(--color-primary)] text-[var(--color-text-inverse)]"
                              : "text-[var(--color-text-primary)] hover:text-[var(--color-primary)]",
                          )}
                        >
                          <FileUp size={12} /> Z pliku CSV
                        </button>
                      </div>
                    </div>
                  </div>
                  {addMode === "manual" && (
                    <DrawerGroup title="Nowy właściciel — ręcznie" icon={UserPlus} className="border-[var(--color-border-strong)]">
                      <OwnerAddForm
                        parcelId={parcelId}
                        connectionId={connectionId}
                        prefill={ownerPrefill}
                        onDone={() => { setAddMode(""); setOwnerPrefill(undefined); }}
                      />
                    </DrawerGroup>
                  )}
                  {addMode === "csv" && (
                    <DrawerGroup title="Właściciele prywatni z pliku CSV" icon={FileUp} className="border-[var(--color-border-strong)]">
                      <OwnerCsvImport parcelId={parcelId} connectionId={connectionId} />
                    </DrawerGroup>
                  )}
                  {/* Dlaczego „Przydziel urzędy" pomija tę działkę — do tej pory powód szedł
                      tylko do listy „pominięte" w modalu, a tu użytkownik widział wyłącznie
                      nieaktywną opcję. Z właścicielem rozpoznanym po nazwie: skrót do urzędu. */}
                  {parcel.office_hint && (
                    <div className="flex flex-wrap items-start gap-2 border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-2.5 py-2 text-[11px] text-[var(--color-text-secondary)]">
                      <AlertTriangle size={13} className="mt-0.5 shrink-0 text-amber-600" />
                      <div className="min-w-0 flex-1 space-y-1">
                        <div>
                          <b>Automat urzędów pomija tę działkę:</b> {parcel.office_hint.reason}.
                        </div>
                        {parcel.office_hint.owner ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <span>
                              Właściciel „{parcel.office_hint.owner.name}” wygląda na{" "}
                              {parcel.office_hint.owner.label.toLowerCase()} — dopasuj mu rekord z rejestru,
                              a kategoria i ścieżka urzędowa przeliczą się same.
                            </span>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => {
                                const target = parcel.owners.find((o) => o.id === parcel.office_hint?.owner?.id);
                                if (target) setOfficePickerOwner(target);
                              }}
                            >
                              Przypisz urząd właścicielowi
                            </Button>
                          </div>
                        ) : (
                          <div>
                            Dodaj urząd ręcznie („Urząd / instytucja” przy dodawaniu właściciela) albo popraw kod
                            własności — zmiana kodu od razu przelicza kategorię.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="space-y-2">
                    {parcel.owners.length === 0 && (
                      <p className="text-[11px] font-mono text-[var(--color-text-muted)]">Brak przypisanych właścicieli.</p>
                    )}
                    {parcel.owners.map((o) => (
                      <OwnerCard
                        key={o.id}
                        owner={o}
                        siblings={parcel.owners}
                        connectionId={connectionId}
                        regulacja={regulacja}
                        onCopyFrom={(prefill) => { setOwnerPrefill(prefill); setAddMode("manual"); }}
                        onAssignOffice={o.kind === "company" && !o.office ? () => setOfficePickerOwner(o) : undefined}
                      />
                    ))}
                    {showOutdated && <OutdatedOwnersList parcelId={parcel.id} />}
                  </div>
                </section>
              )}

              {activeTab === "notes" && (
                <section>
                  <ParcelNotes parcelId={parcelId} connectionId={connectionId} owners={parcel.owners} />
                </section>
              )}

              {activeTab === "regulacja" && (
                <section className="space-y-2">
                  <RegulatoryAttachmentsPanel parcelId={parcelId} connectionId={connectionId} />
                </section>
              )}

              {activeTab === "route" && (
                <section className="space-y-3">
                  <ParcelRouteMiniMap connectionId={connectionId} parcelId={parcelId} height={560} />
                  <ParcelRouteCourse connectionId={connectionId} parcelId={parcelId} />
                </section>
              )}

              {activeTab === "history" && (
                <section>
                  <ParcelHistory connectionId={connectionId} parcelId={parcelId} />
                </section>
              )}
            </div>
          </>
        )}
      </div>
      {parcel && (
        <SuggestStreetModal
          open={streetModal}
          connectionId={connectionId}
          parcelId={parcel.id}
          parcelTeryt={parcel.teryt}
          currentStreet={parcel.street_and_number ?? ""}
          currentLocality={parcel.locality ?? ""}
          onClose={() => setStreetModal(false)}
        />
      )}
      {sourceOpen && parcel && (
        <LandClassSourceModal parcel={parcel} connectionId={connectionId} onClose={() => setSourceOpen(false)} />
      )}
      {officePickerOwner && parcel && (
        <OwnerOfficePickerModal
          base="parcels"
          sourceId={parcel.id}
          ownerName={officePickerOwner.full_name}
          ownershipCode={parcel.ownership_code}
          teryt={parcel.teryt}
          parcelId={parcel.id}
          onClose={() => setOfficePickerOwner(null)}
          onPick={(o: PickedOffice) => {
            drawerOwners.update.mutate(
              {
                id: officePickerOwner.id,
                payload: {
                  kind: "company", office: o.office_id, full_name: o.full_name,
                  street: o.street, building_no: o.building_no, postal_code: o.postal_code, city: o.city,
                },
              },
              { onSuccess: () => setOfficePickerOwner(null) },
            );
          }}
        />
      )}
      {/* KW — modal w korzeniu szuflady, żeby przycisk działał z KAŻDEJ zakładki. */}
      {kwOpen && parcel && (
        <KwAnalysisModal
          parcel={{ id: parcelId, teryt: parcel.teryt, kw_number: parcel.kw_number }}
          onClose={() => setKwOpen(false)}
        />
      )}
    </div>
  );
}

// ── manual parcel create ─────────────────────────────────────────────────────
function ParcelCreateForm({ connectionId, onDone }: { connectionId: string; onDone: () => void }) {
  const pm = useParcelMutations(connectionId);
  const [f, setF] = useState({ teryt: "", short_number: "", obreb: "", kw_number: "" });
  const [err, setErr] = useState<string | null>(null);
  const busy = pm.create.isPending || pm.fetchUldk.isPending;

  // Surface why a create/fetch was rejected. The uniqueness violation
  // (unique_together = [connection, teryt]) lands under `non_field_errors`, not `detail`,
  // so read both before falling back — otherwise a duplicate działka fails silently.
  const errMessage = (e: unknown, fallback: string): string => {
    const errors = (e as { response?: { data?: { errors?: unknown } } })?.response?.data?.errors;
    if (typeof errors === "string") return errors;
    if (errors && typeof errors === "object") {
      const o = errors as Record<string, unknown>;
      const detail = o.detail;
      if (typeof detail === "string") return detail;
      const nonField = o.non_field_errors;
      if (Array.isArray(nonField) && typeof nonField[0] === "string") return nonField[0];
      // First field error (e.g. { teryt: ["…"] }).
      const first = Object.values(o).find((v) => Array.isArray(v) && typeof v[0] === "string");
      if (Array.isArray(first)) return first[0] as string;
    }
    return fallback;
  };

  const createFailed = (e: unknown) =>
    setErr(errMessage(e, "Nie udało się dodać działki — sprawdź numer i spróbuj ponownie."));

  // Primary path: create the parcel, then pull its official geometry from ULDK by TERYT.
  const addWithUldk = () => {
    setErr(null);
    pm.create.mutate({ connection: connectionId, ...f } as never, {
      onSuccess: (p) => {
        if (!p?.id) {
          setErr("Działkę dodano, ale nie odczytano jej id — otwórz szczegóły i kliknij „Dociągnij z ULDK”.");
          return;
        }
        pm.fetchUldk.mutate(p.id, {
          onSuccess: onDone,
          onError: (e: unknown) =>
            setErr(errMessage(e, "ULDK nie zwrócił geometrii — działkę dodano bez kształtu (możesz ponowić w szczegółach).")),
        });
      },
      onError: createFailed,
    });
  };

  return (
    <div className="card p-3 mb-3">
      <div className="flex flex-wrap gap-3 items-end">
        {([["teryt", "Nr działki ewid."], ["short_number", "Nr działki"], ["obreb", "Obręb"], ["kw_number", "KW"]] as const).map(([k, lbl]) => (
          <label key={k} className="block">
            <span className="section-label">{lbl}</span>
            <input className="input-base text-xs py-1 mt-1" value={f[k]} onChange={(e) => setF((s) => ({ ...s, [k]: e.target.value }))} />
          </label>
        ))}
        <Button size="sm" disabled={!f.teryt || busy} loading={busy} onClick={addWithUldk}>
          Dodaj + pobierz z ULDK
        </Button>
        <Button size="sm" variant="ghost" disabled={!f.teryt || busy} onClick={() => { setErr(null); pm.create.mutate({ connection: connectionId, ...f } as never, { onSuccess: onDone, onError: createFailed }); }}>
          Tylko numer
        </Button>
      </div>
      <p className="text-[10px] font-mono text-[var(--color-text-muted)] mt-2">
        „Nr działki ewid." musi być pełnym identyfikatorem TERYT (np. 226101_1.0001.123 albo 226101_1.0001.123/4 — ukośnik tylko przy podziale działki), żeby ULDK zwrócił geometrię. Gdy ULDK nie odpowie, usługa GUGiK bywa chwilowo przeciążona — spróbuj ponownie.
      </p>
      {err && <p className="text-[11px] text-amber-600 mt-1">{err}</p>}
    </div>
  );
}

// Skany dołączone przez negocjatora do zgody czekającej na potwierdzenie — klik otwiera
// podgląd (PDF/obraz) w modalu, żeby GK widziało treść przed „Potwierdź → pozyskaj tytuł”.
function WpwProposalFiles({ files }: { files: WpwHandoverFile[] }) {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const viewable = files.filter((f) => !!f.url);

  if (files.length === 0) {
    return (
      <p className="text-[11px] text-amber-700/80 dark:text-amber-400/70 italic">
        Brak skanów w WPW — negocjator nie wgrał dokumentu.
      </p>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[11px] text-amber-800/80 dark:text-amber-300/80 inline-flex items-center gap-1">
        <Paperclip size={11} /> Do potwierdzenia:
      </span>
      {files.map((f) => {
        const idx = viewable.findIndex((v) => v.id === f.id);
        return idx < 0 ? (
          <span key={f.id} className="text-[11px] text-[var(--color-text-muted)] max-w-[16rem] truncate" title={f.original_name}>
            {f.original_name}
          </span>
        ) : (
          <button
            key={f.id}
            type="button"
            onClick={() => setOpenIdx(idx)}
            className="inline-flex items-center gap-1 rounded border border-amber-300 dark:border-amber-700 bg-[var(--color-bg-surface)] px-1.5 py-0.5 text-[11px] text-[var(--color-primary)] hover:underline max-w-[18rem]"
            title={`Podgląd: ${f.original_name}`}
          >
            <FileSearch size={11} className="shrink-0" />
            <span className="truncate">{f.original_name}</span>
          </button>
        );
      })}
      {openIdx !== null && viewable[openIdx] && (
        <WpwFilePreviewModal
          files={viewable}
          index={openIdx}
          onIndex={setOpenIdx}
          onClose={() => setOpenIdx(null)}
        />
      )}
    </div>
  );
}

function WpwFilePreviewModal({
  files, index, onIndex, onClose,
}: {
  files: WpwHandoverFile[];
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
}) {
  const file = files[index];
  const name = file.original_name.toLowerCase();
  const isPdf = name.endsWith(".pdf");
  const isImage = /\.(png|jpe?g|gif|webp|bmp|tiff?)$/.test(name);
  const { blobUrl, loading } = useBlobUrl(isPdf || isImage ? file.url : null);

  return (
    <Modal open onClose={onClose} title={`Podgląd — ${file.original_name}`} size="xl">
      {files.length > 1 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {files.map((f, i) => (
            <button
              key={f.id}
              type="button"
              onClick={() => onIndex(i)}
              className={clsx(
                "px-2 py-0.5 text-[11px] border max-w-[16rem] truncate",
                i === index
                  ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                  : "border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]",
              )}
              title={f.original_name}
            >
              {f.original_name}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between gap-3 mb-2 text-[11px] text-[var(--color-text-muted)]">
        <span className="truncate">
          {file.uploaded_by_name ? `wgrał: ${file.uploaded_by_name}` : ""}
          {file.created_at ? ` · ${new Date(file.created_at).toLocaleString("pl-PL")}` : ""}
        </span>
        {file.url && (
          <a href={file.url} target="_blank" rel="noopener noreferrer"
            className="shrink-0 inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline">
            <ExternalLink size={12} /> Nowa karta
          </a>
        )}
      </div>
      {loading ? (
        <div className="h-[70vh] flex items-center justify-center"><Spinner /></div>
      ) : blobUrl && isPdf ? (
        <iframe src={blobUrl} className="w-full h-[70vh] border-0" title={file.original_name} />
      ) : blobUrl && isImage ? (
        <img src={blobUrl} alt={file.original_name} className="max-h-[70vh] mx-auto object-contain" />
      ) : (
        <div className="h-[30vh] flex flex-col items-center justify-center gap-3 text-[var(--color-text-muted)]">
          <p className="text-sm">Brak podglądu dla tego formatu.</p>
          {file.url && (
            <a href={file.url} target="_blank" rel="noopener noreferrer" download={file.original_name}
              className="text-sm text-[var(--color-primary)] hover:underline inline-flex items-center gap-1">
              <Download size={13} /> Pobierz plik
            </a>
          )}
        </div>
      )}
    </Modal>
  );
}

function useBlobUrl(fileUrl: string | null | undefined): { blobUrl: string | null; loading: boolean } {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!fileUrl) { setBlobUrl(null); return; }
    let alive = true;
    setLoading(true);
    setBlobUrl(null);
    fetch(fileUrl, { credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => { if (alive) { setBlobUrl(URL.createObjectURL(blob)); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => {
      alive = false;
      setBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    };
  }, [fileUrl]);

  return { blobUrl, loading };
}

function WypisPreviewPane({ wypis }: { wypis: WypisFile }) {
  const isPdf = wypis.file.original_filename.toLowerCase().endsWith(".pdf");
  const { blobUrl, loading } = useBlobUrl(isPdf ? wypis.file.url : null);

  return (
    <div className="flex-1 min-w-0 flex flex-col min-h-0">
      {/* toolbar */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)] shrink-0 gap-4">
        <div className="flex items-center gap-2 min-w-0">
          <FileText size={14} className="shrink-0 text-[var(--color-text-muted)]" />
          <span className="text-sm font-medium text-[var(--color-text-primary)] truncate">{wypis.file.original_filename}</span>
          {wypis.note && <span className="text-[11px] text-[var(--color-text-muted)] italic shrink-0">— {wypis.note}</span>}
        </div>
        {wypis.file.url && (
          <a href={wypis.file.url} target="_blank" rel="noopener noreferrer"
            className="shrink-0 inline-flex items-center gap-1 text-[12px] text-[var(--color-primary)] hover:underline">
            <ExternalLink size={12} /> Nowa karta
          </a>
        )}
      </div>

      {/* content */}
      {isPdf ? (
        loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Spinner />
          </div>
        ) : blobUrl ? (
          <iframe
            src={blobUrl}
            className="flex-1 w-full border-0"
            title={wypis.file.original_filename}
          />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-[var(--color-text-muted)]">
            <p className="text-sm">Nie udało się załadować podglądu.</p>
            {wypis.file.url && (
              <a href={wypis.file.url} target="_blank" rel="noopener noreferrer"
                className="text-sm text-[var(--color-primary)] hover:underline inline-flex items-center gap-1">
                <ExternalLink size={13} /> Otwórz w nowej karcie
              </a>
            )}
          </div>
        )
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-[var(--color-text-muted)]">
          <FileText size={40} className="opacity-20" />
          <p className="text-sm">Podgląd dostępny tylko dla PDF.</p>
          {wypis.file.url && (
            <a href={wypis.file.url} target="_blank" rel="noopener noreferrer"
              className="text-sm text-[var(--color-primary)] hover:underline inline-flex items-center gap-1">
              <ExternalLink size={13} /> Pobierz / otwórz plik
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// Odpięcie wypisu od TEJ działki — panel rozwijany w podglądzie wypisu. Sens biznesowy:
// wypis podpięty (zwykle analizą AI) trafił nie na tę działkę / trzeba zamówić wypis raz
// jeszcze. Domyślnie znikają też właściciele wpisani z tego wypisu, bo panel zamawiania
// bierze tylko działki BEZ właścicieli i BEZ wypisu (wypisy/services.candidate_parcels_qs).
function WypisDetachResult({ parcel, summary, onDone }: {
  parcel: Parcel;
  summary: WypisDetachSummary;
  onDone: () => void;
}) {
  return (
    <div className="px-5 py-3 border-b border-[var(--color-border)] bg-emerald-50/60 text-[12px] text-[var(--color-text-secondary)]">
      <p className="font-medium text-emerald-800">
        Odpięto wypis od działki {parcel.teryt}
        {summary.owners_removed > 0 && ` · usunięto właścicieli: ${summary.owners_removed}`}
      </p>
      {summary.owners_kept.length > 0 && (
        <p className="mt-1">
          Nie usunięto (jest już na nich praca — tytuł prawny, WPW lub zapotrzebowanie):{" "}
          {summary.owners_kept.join(", ")}.
        </p>
      )}
      <p className="mt-1">
        {summary.orderable_again
          ? "Działka wróciła do panelu zamawiania wypisów — można zamówić wypis ponownie."
          : summary.owners_left > 0
            ? `Działka ma jeszcze ${summary.owners_left} właściciel${summary.owners_left === 1 ? "a" : "i"} — panel zamawiania jej nie pokaże, dopóki tam są.`
            : "Działka nadal nie jest kandydatem do zamówienia (inny wypis, aktywne zamówienie lub działka poza aktualną trasą)."}
      </p>
      <button type="button" onClick={onDone} className="mt-1.5 text-[11px] text-[var(--color-primary)] hover:underline">
        Zamknij
      </button>
    </div>
  );
}

function WypisDetachPanel({ parcel, wypis, mutations, onSuccess, onCancel }: {
  parcel: Parcel;
  wypis: WypisFile;
  mutations: ReturnType<typeof useWypisMutations>;
  onSuccess: (summary: WypisDetachSummary) => void;
  onCancel: () => void;
}) {
  const [removeOwners, setRemoveOwners] = useState(true);
  const [error, setError] = useState("");
  const fromThisWypis = parcel.owners.filter((o) => o.wypis_file?.id === wypis.file.id);

  const run = async () => {
    setError("");
    try {
      const res = await mutations.detachParcel.mutateAsync({
        id: wypis.id, parcelId: parcel.id, removeOwners,
      });
      onSuccess(res.detach_summary);
    } catch {
      setError("Nie udało się odpiąć wypisu.");
    }
  };

  return (
    <div className="px-5 py-3 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-[12px] text-[var(--color-text-secondary)]">
      <p>
        Odepnij <span className="font-medium text-[var(--color-text-primary)]">{wypis.file.original_filename}</span>{" "}
        od działki <span className="font-mono">{parcel.teryt}</span>. Plik zostaje w projekcie (może obejmować
        inne działki), dane rejestrowe działki (KW, użytek, powierzchnia) zostają bez zmian.
      </p>
      <label className="flex items-start gap-2 mt-2 cursor-pointer">
        <input
          type="checkbox"
          className="mt-0.5 accent-[var(--color-primary)]"
          checked={removeOwners}
          onChange={(e) => setRemoveOwners(e.target.checked)}
        />
        <span>
          <span className="text-[var(--color-text-primary)]">
            Usuń też właścicieli wpisanych z tego wypisu ({fromThisWypis.length})
          </span>
          <span className="block text-[10px] text-[var(--color-text-muted)]">
            Bez tego działka nie wróci do panelu zamawiania wypisów — panel bierze tylko działki bez
            właścicieli. Właściciele, na których jest już praca (tytuł prawny, WPW, zapotrzebowanie),
            i tak zostaną nietknięci.
          </span>
        </span>
      </label>
      {error && <p className="text-[11px] text-red-600 mt-1.5">{error}</p>}
      <div className="flex gap-2 mt-2.5">
        <Button size="sm" variant="danger" loading={mutations.detachParcel.isPending}
          disabled={mutations.detachParcel.isPending} onClick={run}>
          Odepnij od działki
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Anuluj</Button>
      </div>
    </div>
  );
}

function WypisPreviewModal({ parcel, wypisy, mutations, onClose }: {
  parcel: Parcel;
  wypisy: WypisFile[];
  mutations: ReturnType<typeof useWypisMutations>;
  onClose: () => void;
}) {
  const forParcel = wypisy.filter((w) => w.covered_parcels.some((cp) => cp.id === parcel.id));
  const [selectedId, setSelectedId] = useState<string | null>(forParcel[0]?.id ?? null);
  // Lista przychodzi z zewnątrz i zmienia się po odpięciu — trzymamy id, nie obiekt.
  const selected = forParcel.find((w) => w.id === selectedId) ?? forParcel[0] ?? null;
  const setSelected = (w: WypisFile) => setSelectedId(w.id);
  const [detachOpen, setDetachOpen] = useState(false);
  // Podsumowanie trzymamy TU, nie w panelu: po odpięciu wypis znika z listy tej działki,
  // panel by się odmontował i użytkownik nie zobaczyłby, co się właściwie stało.
  const [detachSummary, setDetachSummary] = useState<WypisDetachSummary | null>(null);

  // close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div data-modal-layer className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-6" onClick={onClose}>
      <div
        className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] shadow-2xl flex flex-col w-full max-w-5xl overflow-hidden"
        style={{ height: "calc(100dvh - 80px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)] shrink-0 gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <FileText size={16} className="shrink-0 text-[var(--color-text-muted)]" />
            <div className="min-w-0">
              <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--color-text-muted)] leading-none mb-1">Wypisy z rejestru gruntów</p>
              <p className="text-base font-semibold font-mono text-[var(--color-text-primary)] leading-none truncate">{parcel.teryt}</p>
            </div>
          </div>
          <button type="button" onClick={onClose}
            className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full hover:bg-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors">
            <X size={16} />
          </button>
        </div>

        {detachSummary ? (
          <WypisDetachResult
            parcel={parcel}
            summary={detachSummary}
            onDone={() => setDetachSummary(null)}
          />
        ) : selected && (
          detachOpen ? (
            <WypisDetachPanel
              key={selected.id}
              parcel={parcel}
              wypis={selected}
              mutations={mutations}
              onSuccess={(summary) => { setDetachOpen(false); setDetachSummary(summary); }}
              onCancel={() => setDetachOpen(false)}
            />
          ) : (
            <div className="px-5 py-2 border-b border-[var(--color-border)] flex justify-end">
              <button type="button" onClick={() => setDetachOpen(true)}
                title="Odepnij ten wypis od działki (np. żeby zamówić wypis dla niej jeszcze raz)"
                className="inline-flex items-center gap-1 text-[11px] text-[var(--color-text-muted)] hover:text-red-600 transition-colors">
                <Unlink size={12} /> Odepnij od działki
              </button>
            </div>
          )
        )}

        {forParcel.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-[var(--color-text-muted)] text-sm">
            Brak wypisów przypisanych do tej działki.
          </div>
        ) : (
          <div className="flex flex-1 min-h-0">
            {/* sidebar — only when multiple */}
            {forParcel.length > 1 && (
              <div className="w-56 shrink-0 border-r border-[var(--color-border)] overflow-y-auto">
                {forParcel.map((w) => (
                  <button key={w.id} type="button" onClick={() => { setSelected(w); setDetachOpen(false); }}
                    className={`w-full text-left px-3 py-3 border-b border-[var(--color-border-muted)] transition-colors ${
                      selected?.id === w.id
                        ? "bg-[var(--color-primary)]/10 pl-3.5 border-l-[3px] border-l-[var(--color-primary)]"
                        : "hover:bg-[var(--color-bg-elevated)]"
                    }`}
                  >
                    <span className="block text-[12px] font-medium text-[var(--color-text-primary)] truncate leading-snug">{w.file.original_filename}</span>
                    <span className="block text-[10px] text-[var(--color-text-muted)] mt-0.5">
                      {new Date(w.created_at).toLocaleDateString("pl-PL")}
                      {w.uploaded_by_name && ` · ${w.uploaded_by_name}`}
                    </span>
                    {w.note && <span className="block text-[10px] text-[var(--color-text-muted)] italic mt-0.5">{w.note}</span>}
                  </button>
                ))}
              </div>
            )}

            {selected && <WypisPreviewPane key={selected.id} wypis={selected} />}
          </div>
        )}
      </div>
    </div>
  );
}

// "1-3, 5" → the page numbers it references; `valid` is false on malformed tokens.
function parsePageSpec(spec: string): { pages: number[]; valid: boolean } {
  const pages: number[] = [];
  let valid = true;
  for (const part of spec.split(/[,;]/).map((s) => s.trim()).filter(Boolean)) {
    const range = part.match(/^(\d+)\s*[-–]\s*(\d+)$/);
    if (range) {
      const a = Number(range[1]);
      const b = Number(range[2]);
      if (a < 1 || a > b) valid = false;
      for (let i = a; i <= b; i++) pages.push(i);
    } else if (/^\d+$/.test(part)) {
      pages.push(Number(part));
    } else {
      valid = false;
    }
  }
  return { pages, valid };
}

// Polish plural for "strona": 1 strona, 2-4 strony, 5+ stron (with teen/decade rules).
function strony(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (n === 1) return "strona";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return "strony";
  return "stron";
}

// Manual editor for the page↔parcel mapping of a single wypis. The user records which
// PDF pages cover which parcel; a later AI pass will write the same structure. Rows are
// seeded from the existing map plus every covered parcel not yet annotated.
function WypisPageMapModal({ wypis, parcels, mutations, onClose }: {
  wypis: WypisFile;
  parcels: Parcel[];
  mutations: ReturnType<typeof useWypisMutations>;
  onClose: () => void;
}) {
  const parcelLabel = (p: Parcel) => p.teryt || p.short_number || p.id.slice(0, 8);
  const parcelOptions = useMemo(
    () => parcels.map((p) => ({ value: p.id, label: parcelLabel(p) })),
    [parcels],
  );
  const pageCount = wypis.page_count ?? 0;

  // `pick` — wiersz tekstowy (np. po odpięciu wypisu od działki zostaje sam teryt)
  // przełączony na wybór działki z listy, żeby dało się ją podpiąć z powrotem.
  type Row = { key: string; parcelId: string; teryt: string; pages: string; pick?: boolean };
  const seedRef = useRef(0);
  const nextKey = () => `r${seedRef.current++}`;

  const [rows, setRows] = useState<Row[]>(() => {
    const mapped: Row[] = wypis.page_map.map((e) => ({
      key: nextKey(),
      parcelId: e.parcel_id ?? "",
      teryt: e.teryt,
      pages: e.pages,
    }));
    const seen = new Set(mapped.map((r) => r.parcelId).filter(Boolean));
    // Offer a ready blank row for each covered parcel that isn't mapped yet.
    for (const cp of wypis.covered_parcels) {
      if (!seen.has(cp.id)) {
        mapped.push({ key: nextKey(), parcelId: cp.id, teryt: cp.teryt, pages: "" });
        seen.add(cp.id);
      }
    }
    if (mapped.length === 0) mapped.push({ key: nextKey(), parcelId: "", teryt: "", pages: "" });
    return mapped;
  });

  const setRow = (key: string, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const removeRow = (key: string) => setRows((rs) => rs.filter((r) => r.key !== key));
  const addRow = () => setRows((rs) => [...rs, { key: nextKey(), parcelId: "", teryt: "", pages: "" }]);

  const handleSave = async () => {
    // Linked rows send only the id — the backend denormalises teryt from the parcel.
    // Free-text rows carry the typed label.
    const pageMap = rows
      .map((r) => ({
        parcel_id: r.parcelId || null,
        teryt: r.parcelId ? "" : r.teryt.trim(),
        pages: r.pages.trim(),
      }))
      .filter((e) => e.parcel_id || e.teryt || e.pages);
    await mutations.setPageMap.mutateAsync({ id: wypis.id, pageMap });
    onClose();
  };

  return (
    <Modal open onClose={onClose} title="Strony wypisu → działki" size="lg">
      <div className="flex flex-col gap-4">
        <p className="text-[12px] text-[var(--color-text-muted)]">
          Zapisz, które strony pliku <span className="font-mono text-[var(--color-text-secondary)]">{wypis.file.original_filename}</span> dotyczą której działki.
          Numery stron wpisz ręcznie (np. <span className="font-mono">1-3</span> lub <span className="font-mono">2, 5</span>).
        </p>

        {pageCount > 0 ? (
          <div className="flex items-center gap-1.5 text-[12px]">
            <FileText size={13} className="text-[var(--color-text-muted)] shrink-0" />
            <span className="text-[var(--color-text-secondary)]">
              PDF ma <span className="font-semibold text-[var(--color-text-primary)]">{pageCount}</span> {strony(pageCount)}
              <span className="text-[var(--color-text-muted)]"> (dozwolone strony 1–{pageCount})</span>
            </span>
          </div>
        ) : (
          <p className="text-[11px] text-amber-600 italic">Nie udało się ustalić liczby stron PDF — numery stron nie będą sprawdzane.</p>
        )}

        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-[1fr_140px_32px] gap-2 items-center">
            <span className="section-label">Działka</span>
            <span className="section-label">Strony</span>
            <span />
          </div>
          {rows.map((r) => {
            const spec = parsePageSpec(r.pages);
            const outOfRange = pageCount > 0 && spec.pages.some((p) => p < 1 || p > pageCount);
            const badPages = r.pages.trim() !== "" && (!spec.valid || outOfRange);
            return (
              <div key={r.key} className="flex flex-col gap-1">
                <div className="grid grid-cols-[1fr_140px_32px] gap-2 items-center">
                  {r.parcelId || r.pick || !r.teryt ? (
                    <SearchableSelect
                      options={parcelOptions}
                      value={r.parcelId}
                      placeholder={r.teryt ? `Wybierz działkę… (${r.teryt})` : "Wybierz działkę…"}
                      onChange={(e) => {
                        const id = e.target.value;
                        const p = parcels.find((x) => x.id === id);
                        setRow(r.key, { parcelId: id, teryt: p ? parcelLabel(p) : r.teryt, pick: true });
                      }}
                    />
                  ) : (
                    <div className="flex items-center gap-1">
                      <input
                        className="input-base text-xs py-1.5 w-full font-mono"
                        value={r.teryt}
                        onChange={(e) => setRow(r.key, { teryt: e.target.value })}
                        placeholder="Oznaczenie działki"
                      />
                      {(() => {
                        // Teryt z wiersza pasuje do działki projektu (typowo: wypis odpięty
                        // od tej działki) → jeden klik podpina ją z powrotem; inaczej
                        // przełącz na wybór z listy.
                        const match = parcels.find((x) => parcelLabel(x) === r.teryt.trim());
                        return (
                          <button
                            type="button"
                            className="shrink-0 text-[10px] uppercase tracking-wide px-1 py-px border border-dashed border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
                            title={match
                              ? `Podepnij działkę ${parcelLabel(match)} z powrotem do tego wypisu (zapisze się po „Zapisz przypisanie”).`
                              : "Zamień wpis tekstowy na wybór działki z listy."}
                            onClick={() => setRow(r.key, match ? { parcelId: match.id, pick: true } : { pick: true })}
                          >
                            {match ? "podepnij" : "wybierz"}
                          </button>
                        );
                      })()}
                    </div>
                  )}
                  <input
                    className={`input-base text-xs py-1.5 w-full font-mono ${badPages ? "border-red-400 text-red-600" : ""}`}
                    value={r.pages}
                    onChange={(e) => setRow(r.key, { pages: e.target.value })}
                    placeholder="np. 1-3"
                  />
                  <button
                    type="button"
                    title="Usuń wiersz"
                    className="text-[var(--color-text-muted)] hover:text-red-500 transition-colors flex justify-center"
                    onClick={() => removeRow(r.key)}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
                {badPages && (
                  <p className="text-[10px] text-red-500 pl-1">
                    {!spec.valid
                      ? "Nieprawidłowy zapis stron (użyj np. 1-3, 5)."
                      : `Poza zakresem — PDF ma ${pageCount} ${strony(pageCount)}.`}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <div>
          <Button size="sm" variant="ghost" onClick={addRow}>+ Dodaj działkę</Button>
        </div>

        <div className="flex gap-2 pt-2 border-t border-[var(--color-border-muted)]">
          <Button size="sm" loading={mutations.setPageMap.isPending} disabled={mutations.setPageMap.isPending} onClick={handleSave}>
            Zapisz przypisanie
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose}>Anuluj</Button>
        </div>
      </div>
    </Modal>
  );
}

function WypisySection({ parcels, wypisy, mutations }: {
  parcels: Parcel[];
  wypisy: WypisFile[];
  mutations: ReturnType<typeof useWypisMutations>;
}) {
  const [pageMapWypis, setPageMapWypis] = useState<WypisFile | null>(null);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [selectedParcels, setSelectedParcels] = useState<string[]>([]);
  const [note, setNote] = useState("");

  // ── analiza AI pojedynczego wypisu (bez zamówienia) — ta sama ekstrakcja i modal
  // przeglądu co przy zamówieniu wypisów. `aiWypisId` = plik w trakcie analizy; wynik
  // (propozycja) przychodzi w rezultacie zadania i otwiera modal przeglądu. ──
  const [aiWypisId, setAiWypisId] = useState<string | null>(null);
  const [aiTaskId, setAiTaskId] = useState<string | null>(null);
  const [aiReview, setAiReview] = useState<
    { wypis: WypisFile; proposal: WypisExtractionProposal; costUsd: string | null } | null
  >(null);
  const [aiError, setAiError] = useState("");
  const aiTask = useTaskPolling(aiTaskId);

  useEffect(() => {
    if (!aiTaskId) return;
    const s = aiTask.data?.status;
    if (s === "success") {
      const r = (aiTask.data?.result ?? {}) as { proposal?: WypisExtractionProposal; cost_usd?: string };
      const w = wypisy.find((x) => x.id === aiWypisId) ?? null;
      if (r.proposal && w) setAiReview({ wypis: w, proposal: r.proposal, costUsd: r.cost_usd ?? null });
      else setAiError("Nie udało się odczytać danych z wypisu — spróbuj ponownie.");
      setAiTaskId(null);
      setAiWypisId(null);
    } else if (s === "failure" || s === "cancelled") {
      const tail = taskErrorTail(aiTask.data?.error);
      setAiError(`Analiza AI nie powiodła się${tail ? `: ${tail}` : ""} — spróbuj ponownie.`);
      setAiTaskId(null);
      setAiWypisId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiTask.data?.status]);

  const startAi = async (w: WypisFile) => {
    setAiError("");
    try {
      const { task_id } = await mutations.extract.mutateAsync(w.id);
      setAiWypisId(w.id);
      setAiTaskId(task_id);
    } catch {
      setAiError("Nie udało się uruchomić analizy.");
    }
  };

  const eligibleParcels = useMemo(
    () => parcels.filter((p) => p.access_mode === "consent" || p.access_mode === "notification"),
    [parcels],
  );

  const handleSubmit = async () => {
    if (!file) return;
    await mutations.upload.mutateAsync({ file, parcelIds: selectedParcels, note });
    setOpen(false);
    setFile(null);
    setSelectedParcels([]);
    setNote("");
  };

  const toggleParcel = (id: string) =>
    setSelectedParcels((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  return (
    <div className="mt-8 border-t border-[var(--color-border-muted)] pt-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText size={14} className="text-[var(--color-text-muted)]" />
          <span className="section-label">Wypisy z rejestru gruntów</span>
          {wypisy.length > 0 && (
            <span className="text-[10px] font-mono px-1.5 py-px bg-[var(--color-bg-elevated)] border border-[var(--color-border)] text-[var(--color-text-muted)]">
              {wypisy.length}
            </span>
          )}
        </div>
        <Button size="sm" variant={open ? "ghost" : "secondary"} onClick={() => setOpen((v) => !v)}>
          {open ? "Anuluj" : <><Upload size={12} className="mr-1.5" />Dodaj wypis</>}
        </Button>
      </div>

      {aiError && <p className="text-[11px] text-red-600 mb-2">{aiError}</p>}
      {aiWypisId && (
        <p className="text-[11px] text-[var(--color-text-muted)] mb-2 flex items-center gap-1.5">
          <Sparkles size={11} className="animate-pulse text-[var(--color-primary)]" />
          Analizuję wypis (AI) — odczytuję właścicieli, KW i użytki…
        </p>
      )}

      {open && (
        <div className="card p-4 mb-4 flex flex-col gap-4 border-[var(--color-primary)]/30">
          <div>
            <label className="section-label block mb-1.5">Plik PDF *</label>
            <FileDropZone
              files={file ? [file] : []}
              onFilesChange={(fs) => setFile(fs[0] ?? null)}
              accept=".pdf,application/pdf"
            />
          </div>

          {eligibleParcels.length > 0 && (
            <div>
              <label className="section-label block mb-1.5">Działki objęte wypisem
                <span className="ml-1 font-normal normal-case text-[var(--color-text-muted)]">(opcjonalnie)</span>
              </label>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                {eligibleParcels.map((p) => {
                  const checked = selectedParcels.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => toggleParcel(p.id)}
                      className={`text-[11px] font-mono px-2 py-1 border transition-colors ${
                        checked
                          ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white"
                          : "border-[var(--color-border)] hover:border-[var(--color-primary)] text-[var(--color-text-secondary)]"
                      }`}
                    >
                      {p.teryt || p.short_number || p.id.slice(0, 8)}
                    </button>
                  );
                })}
              </div>
              {selectedParcels.length > 0 && (
                <p className="text-[10px] text-[var(--color-text-muted)] mt-1.5">{selectedParcels.length} zaznaczonych</p>
              )}
            </div>
          )}

          <div>
            <label className="section-label block mb-1.5">Uwaga
              <span className="ml-1 font-normal normal-case text-[var(--color-text-muted)]">(opcjonalnie)</span>
            </label>
            <input
              className="input-base text-xs py-1.5 w-full"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="np. obręb 0001 / 2024"
            />
          </div>

          <div className="flex gap-2 pt-1">
            <Button size="sm" disabled={!file || mutations.upload.isPending} loading={mutations.upload.isPending} onClick={handleSubmit}>
              Zapisz wypis
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setOpen(false); setFile(null); setSelectedParcels([]); setNote(""); }}>
              Anuluj
            </Button>
          </div>
        </div>
      )}

      {wypisy.length === 0 && !open ? (
        <p className="text-[12px] text-[var(--color-text-muted)] italic">Brak wgranych wypisów.</p>
      ) : wypisy.length > 0 && (
        <TableShell>
          <thead>
            <tr>
              <Th>Plik</Th>
              <Th>Działki</Th>
              <Th>Data wgrania</Th>
              <Th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {wypisy.map((w) => (
              <tr key={w.id} className="border-t border-[var(--color-border-muted)] hover:bg-[var(--color-bg-elevated)] transition-colors">
                <td className="px-3 py-2.5">
                  {w.file.url ? (
                    <a href={w.file.url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-[var(--color-primary)] hover:underline font-medium text-[13px]"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FileText size={13} className="shrink-0" />
                      <span className="truncate max-w-[280px]">{w.file.original_filename}</span>
                      <ExternalLink size={10} className="shrink-0 opacity-50" />
                    </a>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-[var(--color-text-muted)] text-[13px]">
                      <FileText size={13} className="shrink-0" />{w.file.original_filename}
                    </span>
                  )}
                  {w.note && <p className="text-[11px] text-[var(--color-text-muted)] mt-0.5 ml-[21px]">{w.note}</p>}
                  {!!w.page_count && (
                    <p className="text-[11px] text-[var(--color-text-muted)] mt-0.5 ml-[21px]">{w.page_count} {strony(w.page_count)}</p>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  {(() => {
                    const pagesById = new Map(
                      w.page_map.filter((e) => e.parcel_id).map((e) => [e.parcel_id!, e.pages]),
                    );
                    // page_map rows not linked to a covered parcel (free text / AI-unmatched)
                    const coveredIds = new Set(w.covered_parcels.map((cp) => cp.id));
                    const freeEntries = w.page_map.filter(
                      (e) => (!e.parcel_id || !coveredIds.has(e.parcel_id)) && (e.teryt || e.pages),
                    );
                    if (w.covered_parcels.length === 0 && freeEntries.length === 0) {
                      return <span className="text-[var(--color-text-muted)] text-[12px]">—</span>;
                    }
                    return (
                      <div className="flex flex-wrap gap-1">
                        {w.covered_parcels.map((cp) => {
                          const pg = pagesById.get(cp.id);
                          return (
                            <span key={cp.id} className="px-1.5 py-0.5 bg-[var(--color-bg-elevated)] border border-[var(--color-border)] text-[10px] font-mono text-[var(--color-text-secondary)]">
                              {cp.teryt}
                              {pg && <span className="text-[var(--color-primary)]"> · str. {pg}</span>}
                            </span>
                          );
                        })}
                        {freeEntries.map((e, i) => (
                          <span key={`f${i}`} className="px-1.5 py-0.5 bg-[var(--color-bg-elevated)] border border-dashed border-[var(--color-border)] text-[10px] font-mono text-[var(--color-text-secondary)]">
                            {e.teryt || "—"}
                            {e.pages && <span className="text-[var(--color-primary)]"> · str. {e.pages}</span>}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-3 py-2.5 text-[12px] text-[var(--color-text-muted)] whitespace-nowrap">
                  {new Date(w.created_at).toLocaleDateString("pl-PL")}
                  {w.uploaded_by_name && <span className="block text-[11px]">{w.uploaded_by_name}</span>}
                </td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  {w.file.original_filename.toLowerCase().endsWith(".pdf") && (
                    <button type="button" title="Odczytaj dane z wypisu (AI)"
                      className="text-[var(--color-primary)] hover:opacity-70 transition-opacity mr-2 disabled:opacity-40 align-middle"
                      onClick={() => startAi(w)}
                      disabled={!!aiWypisId}
                    >
                      {aiWypisId === w.id ? <Spinner size="sm" /> : <Sparkles size={13} />}
                    </button>
                  )}
                  <button type="button" title="Przypisz strony do działek"
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors mr-2 align-middle"
                    onClick={() => setPageMapWypis(w)}
                  >
                    <Pencil size={13} />
                  </button>
                  <button type="button" title="Usuń wypis"
                    className="text-[var(--color-text-muted)] hover:text-red-500 transition-colors"
                    onClick={() => window.confirm("Usunąć wypis?") && mutations.remove.mutate(w.id)}
                    disabled={mutations.remove.isPending}
                  >
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </TableShell>
      )}

      {pageMapWypis && (
        <WypisPageMapModal
          wypis={pageMapWypis}
          parcels={parcels}
          mutations={mutations}
          onClose={() => setPageMapWypis(null)}
        />
      )}

      {aiReview && (
        <ExtractionReviewModal
          source={{
            kind: "wypis",
            id: aiReview.wypis.id,
            proposal: aiReview.proposal,
            costUsd: aiReview.costUsd,
            parcelOptions: parcels.map((p) => ({
              value: p.id,
              label: p.teryt || p.short_number || p.id.slice(0, 8),
            })),
            pageCounts: { [aiReview.wypis.id]: aiReview.wypis.page_count ?? 0 },
            officeBase: "parcel-wypisy",
            apply: (rows) =>
              mutations.apply.mutateAsync({ id: aiReview.wypis.id, parcels: rows }).then(() => undefined),
            applyPending: mutations.apply.isPending,
          }}
          onClose={() => setAiReview(null)}
        />
      )}
    </div>
  );
}

// ── WYPIS działki — ślad w kolumnie właścicieli ──────────────────────────────
// Cztery przypadki, żeby z tabeli było widać nie tylko czy wypis JEST, ale i czy ktoś
// go w ogóle zamówił:
//   • wypis podpięty → zielony przycisk „N wypisy" (otwiera podgląd pliku);
//   • brak wypisu, ale działka wchodzi w zamówienie → link do zamówienia z jego statusem
//     (wypis w drodze; anulowane zamówienie pokazujemy wyszarzone, też jako ślad);
//   • brak wypisu, brak zamówienia i brak właścicieli → link do panelu zamawiania (panel
//     bierze taką działkę sam, patrz wypisy/services.candidate_parcels_qs);
//   • brak wypisu, brak zamówienia, ale właściciel wpisany z ręki (np. urząd powiedział,
//     czyja to działka) → przycisk „potrzebny wypis”. Panel zamawiania domyślnie takiej
//     działki nie bierze, więc bez tego zgłoszenia wypis trzeba było załatwiać poza
//     systemem; klik wraca ją do panelu, NIE ruszając wpisanych właścicieli. Zgłoszenie
//     zdejmuje się tym samym przyciskiem, a samo gaśnie, gdy wypis do działki trafi.
function ParcelWypisMarks({ parcel: p, wypisy, onPreview, onSetWypisNeeded, pending }: {
  parcel: Parcel;
  wypisy: WypisFile[];
  onPreview: () => void;
  onSetWypisNeeded: (value: boolean) => void;
  pending: boolean;
}) {
  const hasWypis = wypisy.length > 0;
  const eligible = p.access_mode === "consent" || p.access_mode === "notification";
  // Backend sortuje: żywe zamówienia przed anulowanymi, potem od najnowszego.
  const order = (p.wypis_orders ?? [])[0];
  const orderInProgress = !!order && order.status !== "cancelled" && order.status !== "completed";

  const orderLink = order ? (
    <Link
      to={`/power-projects/wypisy/${order.id}`}
      onClick={(e) => e.stopPropagation()}
      title={`Zamówienie wypisu ${order.number} — ${order.status_display} (utworzone ${fmtDate(order.created_at)}). Kliknij, aby otworzyć zamówienie.`}
      className={clsx(
        "inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide px-1 py-px border transition-colors",
        order.status === "cancelled"
          ? "border-[var(--color-border)] text-[var(--color-text-muted)] line-through hover:bg-[var(--color-bg-elevated)]"
          : "border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100",
      )}
    >
      <FileSearch size={9} /> zam. {order.number} · {order.status_display}
      <ExternalLink size={8} />
    </Link>
  ) : null;

  // Zgłoszenie świeci (ręczne „potrzebny wypis” albo ponowne zamówienie z panelu) →
  // link do panelu + „cofnij”. Samo PONOWNE zgłoszenie robi się zbiorczo w panelu
  // zamawiania (modal „Zamów ponownie wypisy”), nie z wiersza jednej działki.
  const flagged = (
    <span className="inline-flex items-center gap-1">
      <Link
        to="/power-projects/zamawianie?tab=wypisy"
        onClick={(e) => e.stopPropagation()}
        title={
          (hasWypis
            ? "Zgłoszona do PONOWNEGO zamówienia wypisu — czeka w panelu zamawiania wypisów EGiB."
            : "Działka zgłoszona jako „potrzebny wypis” — czeka w panelu zamawiania wypisów EGiB.")
          + (p.wypis_needed_reason ? ` Powód: ${p.wypis_needed_reason}.` : "")
          + " Kliknij, aby tam przejść."
        }
        className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide px-1 py-px border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
      >
        <FileSearch size={9} /> {hasWypis ? "ponowny wypis" : "potrzebny wypis"}
        <ExternalLink size={8} />
      </Link>
      <button
        type="button"
        disabled={pending}
        onClick={(e) => { e.stopPropagation(); onSetWypisNeeded(false); }}
        title="Zdejmij zgłoszenie — działka wypadnie z panelu zamawiania wypisów."
        className="text-[9px] uppercase tracking-wide px-1 py-px border border-[var(--color-border)] text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] disabled:opacity-50 transition-colors"
      >
        cofnij
      </button>
    </span>
  );

  if (hasWypis) {
    // Wypis jest. Obok chipu: ponowne zamówienie w toku albo zgłoszenie do ponownego
    // zamówienia. Starego wypisu nie odpinamy — działka może mieć ich wiele.
    return (
      <span className="inline-flex flex-wrap items-center gap-1">
        <button type="button" onClick={(e) => { e.stopPropagation(); onPreview(); }}
          className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide px-1 py-px border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors">
          <FileText size={9} /> {wypisy.length} wypis{wypisy.length === 1 ? "" : "y"}
        </button>
        {orderInProgress ? orderLink : (eligible && p.wypis_needed ? flagged : null)}
      </span>
    );
  }
  if (order) return orderLink;
  if (!eligible) return null;
  if (p.wypis_needed) return flagged;
  if (p.owners.length > 0) {
    return (
      <button
        type="button"
        disabled={pending}
        onClick={(e) => { e.stopPropagation(); onSetWypisNeeded(true); }}
        title="Działka nie występuje w żadnym zamówieniu wypisów. Panel zamawiania bierze domyślnie tylko działki bez wpisanych właścicieli — kliknij, żeby mimo wpisanego właściciela zgłosić, że wypis jest potrzebny. Wpisani właściciele zostają."
        className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide px-1 py-px border border-dashed border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700 disabled:opacity-50 transition-colors"
      >
        <FileSearch size={9} /> potrzebny wypis?
      </button>
    );
  }
  return (
    <Link
      to="/power-projects/zamawianie?tab=wypisy"
      onClick={(e) => e.stopPropagation()}
      title="Działka nie występuje w żadnym zamówieniu wypisów. Kliknij, aby przejść do zamawiania wypisów EGiB."
      className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide px-1 py-px border border-[var(--color-border)] text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-secondary)] transition-colors"
    >
      <FileSearch size={9} /> brak zamówienia w systemie
      <ExternalLink size={8} />
    </Link>
  );
}

// Właściciele NIEAKTUALNI działki (ponowny wypis nie potwierdził ich) — rekordy zostają z
// historią (tytuł, WPW, WNU, sprawy), ale karta ich nie pokazuje; ta lista wchodzi po
// checkboxie „pokaż nieaktualnych (N)”.
function OutdatedOwnersList({ parcelId }: { parcelId: string }) {
  const { data, isLoading } = useParcelOutdatedOwners(parcelId);
  if (isLoading) return <Spinner size="sm" />;
  if (!data?.length) return null;
  return (
    <div className="border border-dashed border-[var(--color-border)] p-2 space-y-1 opacity-80">
      <div className="section-label">Nieaktualni właściciele ({data.length})</div>
      {data.map((o) => (
        <div key={o.id} className="text-[11px] font-mono flex flex-wrap items-baseline gap-x-2">
          <span className="line-through text-[var(--color-text-secondary)]">{o.full_name}</span>
          {o.ownership_share && <span className="text-[var(--color-text-muted)]">{o.ownership_share}</span>}
          <span className="text-[10px] text-[var(--color-text-muted)]">
            od {o.outdated_at ? fmtDate(o.outdated_at) : "—"}
            {o.outdated_by_order_number && (
              <> · <Link className="hover:underline" to={`/power-projects/wypisy/${o.outdated_by_order}`} onClick={(e) => e.stopPropagation()}>{o.outdated_by_order_number}</Link></>
            )}
            {o.outdated_reason && <> · {o.outdated_reason}</>}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── ANALIZA KW — badge w kolumnie Status ─────────────────────────────────────
// Zielono = księga przeanalizowana i nic do zgłoszenia (właściciele zgodni z wypisem,
// czysty Dział III i IV, numer KW się zgadza). Pomarańczowo = są „ale" — wszystkie
// wypisane w tooltipie. Rozbieżności zaakceptowane w oknie analizy KW („GMINA WAGANIEC"
// ↔ „Urząd Gminy Waganiec") już się nie liczą, więc taka działka wraca na zielono.
// Osobno, czerwono: DROGA WEWNĘTRZNA wyczytana z księgi — istotna przy pozyskiwaniu
// dostępu, więc widać ją wprost na liście, bez wchodzenia w działkę.
// Wynik analizy księgi wieczystej dla wąskiej kolumny „Analiza KW": TAK (analiza
// wykonana; z liczbą uwag) albo NIE (brak odpisu / odpis jeszcze nieanalizowany).
// Wpisy o drogach w KW zostają jako osobny znacznik pod spodem.
const kwAnalyzed = (p: Parcel): boolean =>
  !!p.kw_summary && (p.kw_summary.state === "ok" || p.kw_summary.state === "issues");

function KwAnalysisCell({ parcel: p }: { parcel: Parcel }) {
  const kw = p.kw_summary;
  if (!kw || !kwAnalyzed(p)) {
    return (
      <span className="inline-flex flex-col items-center gap-0.5">
        <span
          className="inline-flex items-center text-[10px] font-mono uppercase tracking-wide px-1.5 py-px border border-[var(--color-border)] text-[var(--color-text-muted)]"
          title={
            kw?.state === "pending"
              ? "Księga wieczysta wgrana, ale jeszcze nieanalizowana"
              : "Brak analizy księgi wieczystej — otwórz działkę → Księga wieczysta → Analiza KW (AI)"
          }
        >
          Nie
        </span>
        <RoadChip parcel={p} />
      </span>
    );
  }
  const title =
    kw.state === "ok"
      ? "Analiza KW bez zastrzeżeń: właściciele zgodni z wypisem, brak wpisów w Dziale III i IV." +
        (kw.accepted > 0 ? `\nZaakceptowane rozbieżności nazw: ${kw.accepted}` : "")
      : `Analiza KW — do sprawdzenia:\n• ${kw.issues.join("\n• ")}`;
  return (
    <span className="inline-flex flex-col items-center gap-0.5">
      <span
        className="inline-flex items-center gap-0.5 text-[10px] font-mono font-bold uppercase tracking-wide px-1.5 py-px border border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
        title={title}
      >
        <CheckCircle2 size={9} /> Tak
      </span>
      {kw.state === "issues" && (
        <span className="inline-flex items-center gap-0.5 text-[9px] font-mono text-amber-700 dark:text-amber-300" title={title}>
          <AlertTriangle size={9} /> {kw.issues.length} {plUwagi(kw.issues.length)}
        </span>
      )}
      <RoadChip parcel={p} />
    </span>
  );
}

// Znacznik drogi pod werdyktem KW: wpisy drogowe z księgi albo decyzja koordynatora.
// „Nie droga" mimo wpisu w KW zostaje widoczne (przekreślone), żeby było jasne, że
// wpis z księgi świadomie pominięto, a nie że go nie ma.
export function RoadChip({ parcel: p }: { parcel: Parcel }) {
  const road = p.road;
  const entries = road?.entries ?? p.kw_summary?.roads ?? [];
  const manual = road?.source === "manual";
  const isRoad = road ? road.is_road : entries.length > 0;
  const internal = road ? road.internal : !!p.kw_summary?.internal_road;
  if (!isRoad && !(manual && entries.length > 0)) return null;
  const kwLines = entries.map(ROAD_KW_LABEL).join("\n");
  const decision = manual
    ? `Decyzja koordynatora${road?.override_by_name ? ` (${road.override_by_name})` : ""}`
      + (road?.note ? `: ${road.note}` : "")
    : "";
  if (!isRoad) {
    return (
      <span
        className="inline-flex items-center text-[9px] uppercase tracking-wide px-1 py-px border border-[var(--color-border)] text-[var(--color-text-muted)] line-through"
        title={`${decision} — działka NIE jest drogą mimo wpisów w KW:\n${kwLines}`}
      >
        droga w KW
      </span>
    );
  }
  const label = internal
    ? "droga wewnętrzna (KW)"
    : manual && !road?.kw_is_road ? "droga (koordynator)" : "droga w KW";
  return (
    <span
      className={
        "inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide px-1 py-px border " +
        (internal
          ? "border-red-300 bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300"
          : "border-[var(--color-border)] text-[var(--color-text-muted)]")
      }
      title={[
        kwLines ? `Wpisy o drogach w księdze wieczystej:\n${kwLines}` : "",
        decision,
      ].filter(Boolean).join("\n\n")}
    >
      {label}
    </span>
  );
}

// Suma UDZIAŁÓW z pozyskanym tytułem — obok licznika „2/14", bo dwa podpisy bywają
// połową działki albo jedną setną. Liczy backend (jednostki zgody: małżeństwo = jeden
// wspólny udział, wchodzi dopiero gdy podpiszą oboje). Ponad 50% podświetlone: przy
// drodze w KW to próg, na którym działka robi się uzgodniona.
function ParcelSharesMark({ summary }: { summary: ParcelAccessSummary }) {
  // Działka bez wpisanych właścicieli nie ma udziałów do sumowania — „0%" byłoby tam
  // szumem, a nie informacją.
  if (!summary.owners_total || !summary.shares_secured) return null;
  const unknown = summary.shares_unknown_units;
  const title =
    `Udziały z pozyskanym tytułem: ${summary.shares_secured}` +
    (summary.shares_total ? ` (suma udziałów w wypisie: ${summary.shares_total})` : "") +
    "\nMałżeństwo ma jeden wspólny udział — liczy się raz i dopiero, gdy tytuł mają oboje." +
    (unknown > 0
      ? `\nUwaga: ${unknown} udział(y) nieczytelne w wypisie — suma jest zaniżona.`
      : "");
  return (
    <span
      className={
        "inline-flex items-center gap-1 " +
        (summary.shares_majority
          ? "text-emerald-700 dark:text-emerald-400 font-semibold"
          : "text-[var(--color-text-muted)]")
      }
      title={title}
    >
      {summary.shares_secured} udz.
      {unknown > 0 && <span className="text-amber-600" title="Nieczytelne udziały w wypisie">?</span>}
    </span>
  );
}

// ── klasoużytek + odrolnienie cell ───────────────────────────────────────────
// Shows the klasoużytek (użytek + klasa bonitacyjna): the wypis value (`land_use`,
// authoritative) on top, the free i-geoportal read (`geoportal_land_use`) as a muted
// cross-check badge when present, and the per-działka odrolnienie verdict as a badge.
// „?" = geoportal was queried but the county publishes no klasoużytek data; the value
// will come from the wypis, which always overrides the geoportal read.
// Skrót metody, którą ustalono klasę pod urządzeniem. Wystarczy rzut oka: odczyt z usługi
// powiatu to twarda dana z rejestru, odczyt z mapy to interpretacja rysunku, a wykaz
// działki w ogóle nie mówi, na którym klasoużytku stoi obiekt.
const LAND_CLASS_METHOD_CHIP: Record<string, { label: string; className: string }> = {
  county_api: {
    label: "powiat",
    className: "border-emerald-300 text-emerald-700 dark:text-emerald-300",
  },
  map_ai: {
    label: "mapa (AI)",
    className: "border-sky-300 text-sky-700 dark:text-sky-300",
  },
  manual: {
    label: "ręcznie",
    className: "border-violet-300 text-violet-700 dark:text-violet-300",
  },
  mixed: {
    label: "różne źródła",
    className: "border-sky-300 text-sky-700 dark:text-sky-300",
  },
  wypis: {
    label: "wykaz działki",
    className: "border-[var(--color-border)] text-[var(--color-text-muted)]",
  },
  geoportal_list: {
    label: "wykaz działki",
    className: "border-[var(--color-border)] text-[var(--color-text-muted)]",
  },
};

// Sugestia automatu jednym słowem — do listy w szufladzie i do menu przy znaczniku.
const odrolnienieAutoLabel = (odr: ParcelOdrolnienie | undefined): string => {
  const auto = odr?.auto ?? odr;
  if (!auto) return "brak danych";
  return auto.required ? "wymagane" : auto.manual ? "do decyzji (odrolnienie?)" : "niewymagane";
};

// Werdykt jednym chipem (nagłówek grupy „Odrolnienie" w szufladzie): wymagane / do
// decyzji / niewymagane, z „✎" przy decyzji ręcznej.
function OdrolnienieVerdictChip({ odr }: { odr: ParcelOdrolnienie | undefined }) {
  if (!odr) return null;
  const cls = odr.required
    ? "border-red-300 bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300"
    : odr.manual
      ? "border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
      : "border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300";
  const label = odr.required ? "wymagane" : odr.manual ? "do decyzji" : "niewymagane";
  return (
    <span className={clsx(DRAWER_HDR_CHIP, cls)} title={odr.reason}>
      {label}{odr.override ? " ✎" : ""}
    </span>
  );
}

const ODROLNIENIE_OPTIONS: { value: OdrolnienieOverride | null; label: string; hint: string }[] = [
  { value: "required", label: "Wymagane", hint: "Działka wchodzi do wniosku o wyłączenie z produkcji rolniczej." },
  { value: "not_required", label: "Niewymagane", hint: "Wyłączenie niepotrzebne — działka nie wchodzi do wniosku." },
  { value: null, label: "Automat (sugestia ELGW5)", hint: "Wróć do werdyktu z klasoużytku pod urządzeniem." },
];

// Znacznik odrolnienia jako ROZWIJANY przycisk: „odrolnienie" (klasa chroniona),
// „odrolnienie?" (automat nie zna klasy — decyzja ręczna) albo „bez odrolnienia ✎"
// (odkliknięte). Człowiek, który był w terenie, rozstrzyga w obie strony; sugestia
// automatu zostaje w menu, żeby było widać, co odkliknięto.
function OdrolnienieControl({
  parcel: p, pending, onSet,
}: {
  parcel: Parcel;
  pending?: boolean;
  onSet?: (value: OdrolnienieOverride | null) => void;
}) {
  const odr = p.odrolnienie;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);
  if (!odr) return null;
  const override = odr.override ?? null;
  // Bez znacznika i bez decyzji nie ma czego odklikiwać (decyzję „wymagane" wbrew
  // automatowi podejmuje się w szufladzie działki).
  if (!odr.required && !odr.manual && !override) return null;
  const cls = odr.required
    ? "border-red-300 bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300"
    : odr.manual
      ? "border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
      : "border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)]";
  const label = odr.required ? "odrolnienie" : odr.manual ? "odrolnienie?" : "bez odrolnienia";
  return (
    <div ref={ref} className="relative self-start" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        disabled={!onSet || pending}
        onClick={() => setOpen((v) => !v)}
        title={`${odr.reason}${onSet ? "\n\nKliknij, aby rozstrzygnąć ręcznie (wymagane / niewymagane)." : ""}`}
        className={clsx(
          "inline-flex items-center gap-0.5 whitespace-nowrap text-[9px] uppercase tracking-wide px-1 py-px border",
          cls,
          onSet && "hover:opacity-80 cursor-pointer",
          pending && "opacity-50",
        )}
      >
        <AlertTriangle size={9} /> {label}
        {override && <span title="Decyzja ręczna">✎</span>}
        {onSet && <ChevronDown size={9} />}
      </button>
      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 min-w-[250px] card shadow-hard-red py-1 text-left normal-case tracking-normal">
          <p className="px-3 py-1 font-mono text-[10px] text-[var(--color-text-muted)]">
            Sugestia ELGW5: {odrolnienieAutoLabel(odr)}
          </p>
          {ODROLNIENIE_OPTIONS.filter((o) => o.value !== null || override).map((o) => (
            <button
              key={String(o.value)}
              type="button"
              className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-xs hover:bg-[var(--color-bg-elevated)]"
              title={o.hint}
              onClick={() => { setOpen(false); onSet?.(o.value); }}
            >
              {o.label}
              {override === o.value && <Check size={12} className="text-[var(--color-primary)]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ParcelLandClassCell({
  parcel: p, onShowSource, onSetOdrolnienie, odrolnieniePending,
}: {
  parcel: Parcel;
  onShowSource?: (parcel: Parcel) => void;
  onSetOdrolnienie?: (value: OdrolnienieOverride | null) => void;
  odrolnieniePending?: boolean;
}) {
  const wypis = (p.land_use || "").trim();
  const geo = (p.geoportal_land_use || "").trim();
  const showGeo = geo && geo !== wypis; // don't repeat an identical value
  const geoCheckedNoData = !geo && !!p.geoportal_land_use_checked_at;
  const odr = p.odrolnienie;
  const source = odr?.source;
  const pointReads = (source?.reads ?? []).filter((r) => r.in_project);
  const chip = source ? LAND_CLASS_METHOD_CHIP[source.method] : undefined;
  // Klasoużytek spod urządzenia — inny niż wykaz całej działki i to jest cała różnica.
  const underDevice = Array.from(
    new Set(pointReads.map((r) => r.klasouzytek || "brak konturu")),
  ).join(", ");
  return (
    <div className="flex flex-col gap-1 text-xs">
      <span
        className="font-mono text-[var(--color-text-primary)]"
        title={
          !wypis && geoCheckedNoData
            ? "Sprawdzono w i-geoportalu — powiat nie udostępnia klasoużytków. Wartość pojawi się z wypisu (wypis zawsze nadpisuje geoportal)."
            : undefined
        }
      >
        {wypis || (geoCheckedNoData ? "?" : "—")}
      </span>
      {showGeo && (
        <span
          className="self-start inline-flex items-center gap-1 font-mono text-[10px] text-[var(--color-text-muted)]"
          title="Klasoużytek odczytany z i-geoportalu (KIEG) — dane orientacyjne, wypis jest wiążący"
        >
          <Globe size={9} /> {geo}
        </span>
      )}
      {underDevice && (
        <span
          className="self-start inline-flex items-center gap-1 font-mono text-[10px] text-[var(--color-text-primary)]"
          title={`Klasoużytek pod urządzeniem (${pointReads.map((r) => r.device_type).join(", ")}) — to on rozstrzyga, a nie wykaz całej działki.`}
        >
          <Crosshair size={9} /> {underDevice}
        </span>
      )}
      {/* Powierzchnia trwale zajęta — ta sama liczba, która idzie do wniosku
          o wyłączenie z produkcji i którą widać zakreskowaną na mapie. */}
      {!!p.exclusion_area?.area_m2 && (
        <span
          className="self-start inline-flex items-center gap-1 font-mono text-[10px] text-[var(--color-text-muted)]"
          title={`Do wyłączenia: ${p.exclusion_area.by_kind
            .map((k) => `${k.count} × ${k.kind} = ${k.area_m2} m²`)
            .join(", ")}${p.exclusion_area.missing_rates.length
              ? `\n\nBrak stawki dla: ${p.exclusion_area.missing_rates.join(", ")} — powierzchnia zaniżona.`
              : ""}`}
        >
          <Ruler size={9} /> {p.exclusion_area.area_m2} m²
          {p.exclusion_area.missing_rates.length > 0 && (
            <AlertTriangle size={9} className="text-amber-600" />
          )}
        </span>
      )}
      <OdrolnienieControl parcel={p} pending={odrolnieniePending} onSet={onSetOdrolnienie} />
      {/* Skąd wziął się werdykt. Klikalne, gdy jest co pokazać — metoda, jej powód
          i wycinki mapy, na których oparto odczyt. */}
      {chip && (
        <button
          type="button"
          onClick={onShowSource ? () => onShowSource(p) : undefined}
          disabled={!onShowSource}
          className={`self-start inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide px-1 py-px border ${chip.className} ${onShowSource ? "hover:bg-[var(--color-bg-hover)] cursor-pointer" : "cursor-default"}`}
          title={`${source?.method_label ?? ""} — ${source?.why ?? ""}${onShowSource ? "\n\nKliknij, żeby zobaczyć szczegóły odczytu." : ""}`}
        >
          <Info size={9} /> {chip.label}
        </button>
      )}
    </div>
  );
}

// ── sortable table header ─────────────────────────────────────────────────────
// Clicking a header cycles asc → desc → off. The active column shows a filled
// arrow; inactive columns show a muted up/down hint. The "Tytuły dostępu" column
// offers TWO sorts: by the access-mode badge (access_status) and by the
// agreed/total completeness (access_count).
type ParcelSortKey =
  | "teryt"
  | "ownership_code"
  | "access_status"
  | "access_count"
  | "status"
  | "kw"
  | "owner";
type ParcelSort = { key: ParcelSortKey; dir: "asc" | "desc" };

function SortableHeader({
  label, sortKey, sort, onToggle, className, style,
}: {
  label: string;
  sortKey: ParcelSortKey;
  sort: ParcelSort | null;
  onToggle: (key: ParcelSortKey) => void;
  className?: string;
  style?: React.CSSProperties;
}) {
  const active = sort?.key === sortKey;
  const Icon = !active ? ChevronsUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <Th className={className} style={style}>
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        className="inline-flex items-center gap-1 font-mono text-[0.65rem] font-bold uppercase tracking-widest hover:text-[var(--color-text-primary)] transition-colors"
        title="Kliknij, aby sortować"
      >
        <span>{label}</span>
        <Icon size={11} className={active ? "text-[var(--color-primary)]" : "opacity-40"} />
      </button>
    </Th>
  );
}

// Access-mode filter categories. „inne" = wszystko poza pozyskaniem/powiadomieniem
// (np. dostęp niewymagany). Działki tylko-do-powiadomienia zakłamują statystykę
// uzgodnień, więc domyślnie pokazujemy TYLKO „pozyskanie".
type AccessCat = "consent" | "notification" | "other";
const ACCESS_CATS: { key: AccessCat; label: string }[] = [
  { key: "consent", label: "pozyskanie" },
  { key: "notification", label: "powiadomienie" },
  { key: "other", label: "inne" },
];
const accessCategory = (mode: string): AccessCat =>
  mode === "consent" ? "consent" : mode === "notification" ? "notification" : "other";

// Remembers the parcels-tab filters (status / nr działki / właściciel / tryb dostępu +
// sort) per project for the current browser session. Switching tabs remounts ParcelsTab,
// which would otherwise reset every filter; restoring them lets the user leave and come
// back without re-entering them. Session-scoped on purpose: a full page reload starts clean.
const PARCEL_FILTERS_STORAGE_PREFIX = "powerProjects.parcelFilters.";

type PersistedParcelFilters = {
  statusFilter: string;
  terytFilter: string;
  ownerFilter: string;
  mpzpFilter: string; // "" | "with" | "without" — zamówiony wypis MPZP
  kwFilter: string; // "" | "analyzed" | "not_analyzed" — analiza księgi wieczystej
  usedOnly: boolean; // tylko działki wykorzystywane w projekcie (domyślnie tak)
  accessCats: AccessCat[];
  sort: ParcelSort | null;
};

const isAccessCat = (c: unknown): c is AccessCat =>
  c === "consent" || c === "notification" || c === "other";

const isParcelSort = (s: unknown): s is ParcelSort => {
  if (!s || typeof s !== "object") return false;
  const { key, dir } = s as { key?: unknown; dir?: unknown };
  const keys: ParcelSortKey[] = [
    "teryt", "ownership_code", "access_status", "access_count", "status", "kw", "owner",
  ];
  return keys.includes(key as ParcelSortKey) && (dir === "asc" || dir === "desc");
};

function loadParcelFilters(projectId: string): PersistedParcelFilters | null {
  try {
    const raw = sessionStorage.getItem(PARCEL_FILTERS_STORAGE_PREFIX + projectId);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<PersistedParcelFilters>;
    return {
      statusFilter: typeof v.statusFilter === "string" ? v.statusFilter : "",
      terytFilter: typeof v.terytFilter === "string" ? v.terytFilter : "",
      ownerFilter: typeof v.ownerFilter === "string" ? v.ownerFilter : "",
      mpzpFilter: v.mpzpFilter === "with" || v.mpzpFilter === "without" ? v.mpzpFilter : "",
      kwFilter: v.kwFilter === "analyzed" || v.kwFilter === "not_analyzed" ? v.kwFilter : "",
      usedOnly: typeof v.usedOnly === "boolean" ? v.usedOnly : true,
      accessCats: Array.isArray(v.accessCats)
        ? v.accessCats.filter(isAccessCat)
        : ["consent"],
      sort: isParcelSort(v.sort) ? v.sort : null,
    };
  } catch {
    // Malformed JSON or storage unavailable (private mode) — start from defaults.
    return null;
  }
}

function saveParcelFilters(projectId: string, filters: PersistedParcelFilters): void {
  try {
    sessionStorage.setItem(
      PARCEL_FILTERS_STORAGE_PREFIX + projectId,
      JSON.stringify(filters),
    );
  } catch {
    // Ignore write failures (quota / private mode) — persistence is best-effort.
  }
}
// Secondary sort toggle for a column exposing more than one sort criterion
// (e.g. "Tytuły dostępu": by status badge OR by agreed/total count).
function MiniSortButton({
  label, sortKey, sort, onToggle,
}: {
  label: string;
  sortKey: ParcelSortKey;
  sort: ParcelSort | null;
  onToggle: (key: ParcelSortKey) => void;
}) {
  const active = sort?.key === sortKey;
  const Icon = !active ? ChevronsUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={() => onToggle(sortKey)}
      className={clsx(
        "inline-flex items-center gap-0.5 font-mono text-[0.6rem] lowercase tracking-wider transition-colors",
        active ? "text-[var(--color-primary)]" : "text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]",
      )}
      title={`Sortuj po: ${label}`}
    >
      <span>{label}</span>
      <Icon size={10} className={active ? "" : "opacity-40"} />
    </button>
  );
}


/**
 * Rozbieżności właścicieli: księga wieczysta kontra wypis z ewidencji.
 *
 * Serwer liczył to porównanie per plik KW, więc zobaczyć je można było dopiero po
 * otwarciu okna konkretnej księgi konkretnej działki — przy 40 działkach nikt tego
 * nie robił. Panel milczy, gdy nie ma rozbieżności.
 */
function OwnerMismatchPanel({ connectionId }: { connectionId: string }) {
  const { data } = useOwnerMismatches(connectionId);
  const [open, setOpen] = useState(false);

  if (!data || data.mismatch_count === 0) return null;

  return (
    <div className="mb-3 border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-800 dark:bg-amber-900/20">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-amber-900 dark:text-amber-200">
          Właściciele z KW nie zgadzają się z wypisem: {data.mismatch_count}{" "}
          {data.mismatch_count === 1 ? "działka" : "działek"}
        </span>
        <span className="text-amber-800/80 dark:text-amber-200/70">
          (sprawdzono {data.checked_parcels}
          {data.without_wypis > 0 ? `, w tym ${data.without_wypis} bez właścicieli z wypisu` : ""})
        </span>
        <button
          onClick={() => setOpen((v) => !v)}
          className="ml-auto text-amber-900 underline dark:text-amber-200"
        >
          {open ? "Zwiń" : "Pokaż"}
        </button>
      </div>
      {open && (
        <ul className="mt-2 space-y-1">
          {data.rows.map((row) => (
            <li key={row.parcel_id} className="text-amber-900 dark:text-amber-200">
              <b>{row.parcel_number}</b>
              {row.obreb ? ` (${row.obreb})` : ""} —{" "}
              {row.db_empty ? (
                <>brak właścicieli z wypisu; w KW: {row.only_in_kw.join(", ")}</>
              ) : (
                <>
                  {row.only_in_kw.length > 0 && <>tylko w KW: {row.only_in_kw.join(", ")}</>}
                  {row.only_in_kw.length > 0 && row.only_in_db.length > 0 && "; "}
                  {row.only_in_db.length > 0 && <>tylko u nas: {row.only_in_db.join(", ")}</>}
                </>
              )}
              {row.accepted_count > 0 && (
                <span className="text-amber-700/70 dark:text-amber-300/60">
                  {" "}· {row.accepted_count} różnic zaakceptowanych wcześniej
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="mt-1 text-[11px] text-amber-800/70 dark:text-amber-200/60">
        Sprawdź przed podpisaniem zgody i przed wnioskiem art. 124 — zgoda od osoby,
        która wg księgi nie jest właścicielem, jest bezskuteczna.
      </p>
    </div>
  );
}


// Rodzaj rekordu urzędu (offices.OfficeKind), którego żąda kod własności — lustro
// jst_assign.INSTITUTIONS × institutions_for_code: gmina → „gmina", powiat / województwo →
// zarządca drogi (ZDP / ZDW), Skarb Państwa → GDDKiA (zarządca drogi) / starostwo / Wody.
// KOWR i Lasy mają rodzaj „inny", który niczego nie dowodzi (pod nim siedzi też urząd
// użytkownika wieczystego), więc go tu nie ma — licznik jest podpowiedzią, propozycję i tak
// zatwierdza człowiek w modalu.
const LAND_OWNER_OFFICE_KINDS: Record<string, Set<string>> = {
  "1": new Set(["zarzadca_drogi", "starostwo", "wody"]),
  "2": new Set(["zarzadca_drogi", "starostwo", "wody"]),
  "4": new Set(["gmina"]),
  "5": new Set(["gmina"]),
  "11": new Set(["zarzadca_drogi"]),
  "12": new Set(["zarzadca_drogi"]),
  "13": new Set(["zarzadca_drogi"]),
  "14": new Set(["zarzadca_drogi"]),
};

export function ParcelsTab({ connection }: { connection: PowerProject }) {
  const { data: parcels, isLoading } = useParcels(connection.id);
  const pm = useParcelMutations(connection.id);
  const qc = useQueryClient();
  // Same per-project gate as Sprawy/Korespondencja — mirrors backend `_can`, so
  // przedsięwzięcie coordinators are covered without an explicit grant.
  const { canSubmit } = useMyProjectGate(connection.id);
  // WPW handovers for the whole project → surface a "problem / w trakcie" flag per owner in the
  // table (the conversation history lives one click away in WpwConversationModal).
  const { data: handovers } = useWpwHandovers({ connection: connection.id });
  const wpwByOwner = useMemo(() => {
    const m = new Map<string, WpwHandover>();
    for (const h of handovers ?? []) {
      const prev = m.get(h.owner.id);
      // Prefer a flagged (problem / in-progress) handover over a quiet one for the same owner.
      if (!prev || (WPW_FLAG_STATUSES.has(h.declared_status) && !WPW_FLAG_STATUSES.has(prev.declared_status))) {
        m.set(h.owner.id, h);
      }
    }
    return m;
  }, [handovers]);
  const { data: wypisy } = useWypisy(connection.id);
  const wm = useWypisMutations(connection.id);
  const { coveredParcelIds, wypisByParcel } = useMemo(() => {
    const covered = new Set<string>();
    const byParcel = new Map<string, WypisFile[]>();
    for (const w of wypisy ?? []) {
      w.covered_parcels.forEach((cp) => {
        covered.add(cp.id);
        byParcel.set(cp.id, [...(byParcel.get(cp.id) ?? []), w]);
      });
    }
    return { coveredParcelIds: covered, wypisByParcel: byParcel };
  }, [wypisy]);

  const [wypisPreviewParcel, setWypisPreviewParcel] = useState<Parcel | null>(null);

  const [wpwConv, setWpwConv] = useState<{ ownerId: string; label: string } | null>(null);
  // Restore the filters this project last used this session (see saveParcelFilters):
  // switching tabs remounts this component and would otherwise reset them all.
  const [restoredFilters] = useState(() => loadParcelFilters(connection.id));
  const [statusFilter, setStatusFilter] = useState(restoredFilters?.statusFilter ?? "");
  const [terytFilter, setTerytFilter] = useState(restoredFilters?.terytFilter ?? "");
  const [ownerFilter, setOwnerFilter] = useState(restoredFilters?.ownerFilter ?? "");
  const [mpzpFilter, setMpzpFilter] = useState(restoredFilters?.mpzpFilter ?? "");
  const [kwFilter, setKwFilter] = useState(restoredFilters?.kwFilter ?? "");
  // Panel filtrów zwija się per przeglądarka; domyślnie rozwinięty.
  const [filtersOpen, setFiltersOpen] = usePersistedFlag("elg5:ze-parcel-filters-open", true);
  // Domyślnie tylko działki wykorzystywane w projekcie (trasa/obiekty na działce) —
  // bez zamówionych w wypisach „na zapas". Odznaczenie pokazuje wszystkie.
  const [usedOnly, setUsedOnly] = useState(restoredFilters?.usedOnly ?? true);
  // Domyślnie tylko „pozyskanie" (patrz ACCESS_CATS) — powiadomienia/inne wykluczone.
  const [accessCats, setAccessCats] = useState<Set<AccessCat>>(
    () => new Set<AccessCat>(restoredFilters?.accessCats ?? ["consent"]),
  );
  const toggleAccessCat = (c: AccessCat) =>
    setAccessCats((prev) => {
      const n = new Set(prev);
      n.has(c) ? n.delete(c) : n.add(c);
      return n;
    });
  const [sort, setSort] = useState<ParcelSort | null>(restoredFilters?.sort ?? null);
  const toggleSort = (key: ParcelSortKey) =>
    setSort((s) => (s?.key !== key ? { key, dir: "asc" } : s.dir === "asc" ? { key, dir: "desc" } : null));
  const [selected, setSelected] = useState<string | null>(null);
  // Deep link do działki (z dymka na mapie, nowa karta): ?parcel=<id> otwiera szufladę,
  // opcjonalne &parcel_tab=<zakładka> — od razu na tej zakładce (powiadomienia o listach
  // WPW → „Podstawa dostępu"). Parametry zdejmujemy po otwarciu, żeby zamknięcie szuflady
  // nie wracało do nich; licznik przemontowuje szufladę, gdy link przyjdzie przy otwartej.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepParcelId = searchParams.get("parcel");
  const [drawerTab, setDrawerTab] = useState<DrawerTabKey | undefined>(undefined);
  const [deepLinkSeq, setDeepLinkSeq] = useState(0);
  useEffect(() => {
    if (!deepParcelId) return;
    setSelected(deepParcelId);
    setDrawerTab((searchParams.get("parcel_tab") as DrawerTabKey | null) ?? undefined);
    setDeepLinkSeq((n) => n + 1);
    const next = new URLSearchParams(searchParams);
    next.delete("parcel");
    next.delete("parcel_tab");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepParcelId]);
  const [creating, setCreating] = useState(false);
  const [bulkCsvOpen, setBulkCsvOpen] = useState(false);
  const location = useLocation();
  const [jstOpen, setJstOpen] = useState(false);
  // Działka, dla której pokazujemy pochodzenie werdyktu odrolnienia (metoda + wycinki mapy).
  const [sourceParcel, setSourceParcel] = useState<Parcel | null>(null);
  const [ratesOpen, setRatesOpen] = useState(false);
  const [lcuTaskId, setLcuTaskId] = useState<string | null>(null);
  const lcuTask = useTaskPolling(lcuTaskId);
  const lcuStatus = lcuTask.data?.status;
  const lcuBusy = !!lcuTaskId && !["success", "failure", "cancelled"].includes(lcuStatus ?? "");
  const [ownTaskId, setOwnTaskId] = useState<string | null>(null);
  const ownTask = useTaskPolling(ownTaskId);
  const ownStatus = ownTask.data?.status;
  const ownBusy = !!ownTaskId && !["success", "failure", "cancelled"].includes(ownStatus ?? "");

  const [admTaskId, setAdmTaskId] = useState<string | null>(null);
  const admTask = useTaskPolling(admTaskId);
  const admStatus = admTask.data?.status;
  const admBusy = !!admTaskId && !["success", "failure", "cancelled"].includes(admStatus ?? "");

  const [lcTaskId, setLcTaskId] = useState<string | null>(null);
  const lcTask = useTaskPolling(lcTaskId);
  const lcStatus = lcTask.data?.status;
  const lcBusy = !!lcTaskId && !["success", "failure", "cancelled"].includes(lcStatus ?? "");

  // "Pobierz wszystkie brakujące kody własności" runs as a background task; when it
  // finishes, refresh the parcels so the freshly-fetched codes appear in the table.
  useEffect(() => {
    if (!ownTaskId || !ownStatus) return;
    if (["success", "failure", "cancelled"].includes(ownStatus)) {
      if (ownStatus === "success") qc.invalidateQueries({ queryKey: ["pp", "parcels", connection.id] });
      setOwnTaskId(null);
    }
  }, [ownStatus, ownTaskId, connection.id, qc]);

  // Same pattern for the bulk admin-data (województwo/powiat/gmina/obręb) re-fetch.
  useEffect(() => {
    if (!admTaskId || !admStatus) return;
    if (["success", "failure", "cancelled"].includes(admStatus)) {
      if (admStatus === "success") qc.invalidateQueries({ queryKey: ["pp", "parcels", connection.id] });
      setAdmTaskId(null);
    }
  }, [admStatus, admTaskId, connection.id, qc]);

  // Same pattern for the bulk klasoużytek (geoportal) fetch.
  useEffect(() => {
    if (!lcTaskId || !lcStatus) return;
    if (["success", "failure", "cancelled"].includes(lcStatus)) {
      if (lcStatus === "success") qc.invalidateQueries({ queryKey: ["pp", "parcels", connection.id] });
      setLcTaskId(null);
    }
  }, [lcStatus, lcTaskId, connection.id, qc]);

  // To samo dla ustalania klasy POD urządzeniami — po zakończeniu werdykty odrolnienia
  // przeliczają się z odczytów punktowych, więc lista musi się odświeżyć.
  useEffect(() => {
    if (!lcuTaskId || !lcuStatus) return;
    if (["success", "failure", "cancelled"].includes(lcuStatus)) {
      if (lcuStatus === "success") qc.invalidateQueries({ queryKey: ["pp", "parcels", connection.id] });
      setLcuTaskId(null);
    }
  }, [lcuStatus, lcuTaskId, connection.id, qc]);

  // Działki „w grze" (na trasie, dodane ręcznie albo ręcznie wykorzystywane —
  // utils.isProjectParcel) — mianownik dla „pokazano X z Y".
  const baseRows = useMemo(
    () => (parcels ?? []).filter(isProjectParcel),
    [parcels],
  );
  const rows = useMemo(() => {
    let list = baseRows;
    if (usedOnly) list = list.filter((p) => p.is_used);
    // Filtr trybu dostępu: cała lub pusta selekcja = bez ograniczenia, inaczej po kategorii.
    if (accessCats.size > 0 && accessCats.size < ACCESS_CATS.length)
      list = list.filter((p) => accessCats.has(accessCategory(p.access_mode)));
    if (statusFilter) list = list.filter((p) => p.status === statusFilter);
    const tq = terytFilter.trim().toLowerCase();
    if (tq) list = list.filter((p) => p.teryt.toLowerCase().includes(tq) || p.short_number.toLowerCase().includes(tq));
    const oq = ownerFilter.trim().toLowerCase();
    if (oq) list = list.filter((p) => p.owners.some((o) => o.full_name.toLowerCase().includes(oq)));
    if (mpzpFilter) {
      // Anulowane zamówienie się nie liczy — taka działka wraca do „bez wypisu".
      const ordered = (p: Parcel) => p.mpzp_orders.some((o) => o.status !== "cancelled");
      list = list.filter((p) => (mpzpFilter === "with" ? ordered(p) : !ordered(p)));
    }
    if (kwFilter) list = list.filter((p) => (kwFilter === "analyzed" ? kwAnalyzed(p) : !kwAnalyzed(p)));
    return list;
  }, [baseRows, usedOnly, accessCats, statusFilter, terytFilter, ownerFilter, mpzpFilter, kwFilter]);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const factor = sort.dir === "asc" ? 1 : -1;
    const val = (p: Parcel): string | number => {
      switch (sort.key) {
        case "teryt": return p.teryt || p.short_number;
        case "ownership_code": return p.ownership_code;
        // Badge order mirrors the column: zgoda → zawiadomienie → niewymagany.
        case "access_status": return ["consent", "notification", "not_required"].indexOf(p.access_mode);
        case "access_count": return p.owners_total ? p.owners_agreed / p.owners_total : -1;
        case "status": return STATUS_OPTIONS.indexOf(p.status);
        case "kw": return kwAnalyzed(p) ? 1 : 0;
        case "owner": return p.owners[0]?.full_name?.toLowerCase() ?? "";
      }
    };
    return [...rows].sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * factor;
      return String(va).localeCompare(String(vb), "pl", { numeric: true }) * factor;
    });
  }, [rows, sort]);

  // ── eksport CSV/XLSX widocznej listy (zadanie w tle, jak na stronie „Działki") ──
  // Filtrowanie działek odbywa się tu po stronie klienta, więc do backendu idą ID-ki
  // dokładnie tych wierszy, które zostały po filtrach — plik zawsze = widoczna tabela.
  const [exportTaskId, setExportTaskId] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const downloadedRef = useRef<string | null>(null);
  const exportMut = useMutation({
    mutationFn: (opts: { variant: "default" | "address"; file_format?: "xlsx" | "csv" }) =>
      startGlobalParcelsXlsx({
        filters: { parcel_ids: sortedRows.map((p) => p.id) },
        ...opts,
      }),
    onSuccess: (r) => setExportTaskId(r.task_id),
    onError: (e) => setExportError(apiErr(e)),
  });
  const exportTask = useTaskPolling(exportTaskId);
  const exportStatus = exportTask.data?.status;
  const exporting =
    exportMut.isPending ||
    (!!exportTaskId && !["success", "failure", "cancelled"].includes(exportStatus ?? ""));
  const exportResult =
    exportStatus === "success" ? (exportTask.data?.result as ParcelExportResult | null) : null;

  // Auto-download once the file is ready (each file url only once).
  useEffect(() => {
    if (exportResult?.file_url && downloadedRef.current !== exportResult.file_url) {
      downloadedRef.current = exportResult.file_url;
      triggerBrowserDownload(exportResult.file_url, exportResult.filename);
    }
  }, [exportResult]);

  const startExport = (
    variant: "default" | "address" = "default",
    file_format: "xlsx" | "csv" = "xlsx",
  ) => {
    setExportError(null);
    setExportTaskId(null);
    exportMut.mutate({ variant, file_format });
  };

  const accessIsDefault = accessCats.size === 1 && accessCats.has("consent");
  const anyFilter = !!statusFilter || !!terytFilter.trim() || !!ownerFilter.trim()
    || !!mpzpFilter || !!kwFilter || !accessIsDefault || !usedOnly;
  const clearFilters = () => {
    setStatusFilter(""); setTerytFilter(""); setOwnerFilter(""); setMpzpFilter(""); setKwFilter("");
    setUsedOnly(true);
    setAccessCats(new Set<AccessCat>(["consent"]));
  };
  // Chipy aktywnych filtrów w nagłówku panelu — także domyślnych (tryb dostępu,
  // wykorzystanie), bo to one tłumaczą, czemu nie widać wszystkich działek.
  const allAccessCats = () => new Set<AccessCat>(ACCESS_CATS.map((c) => c.key));
  const activeFilterChips: { key: string; label: string; clear: () => void }[] = [];
  if (accessCats.size < ACCESS_CATS.length) {
    activeFilterChips.push({
      key: "access",
      label: `tryb dostępu: ${
        accessCats.size === 0 ? "brak" : ACCESS_CATS.filter((c) => accessCats.has(c.key)).map((c) => c.label).join(", ")
      }`,
      clear: () => setAccessCats(allAccessCats()),
    });
  }
  if (usedOnly) activeFilterChips.push({ key: "used", label: "tylko wykorzystywane w projekcie", clear: () => setUsedOnly(false) });
  if (statusFilter) activeFilterChips.push({ key: "status", label: `status: ${STATUS_LABEL[statusFilter as ParcelStatus] ?? statusFilter}`, clear: () => setStatusFilter("") });
  if (mpzpFilter) activeFilterChips.push({ key: "mpzp", label: mpzpFilter === "with" ? "z wypisem MPZP" : "bez wypisu MPZP", clear: () => setMpzpFilter("") });
  if (kwFilter) activeFilterChips.push({ key: "kw", label: kwFilter === "analyzed" ? "analiza KW wykonana" : "bez analizy KW", clear: () => setKwFilter("") });
  if (terytFilter.trim()) activeFilterChips.push({ key: "teryt", label: `nr działki: ${terytFilter.trim()}`, clear: () => setTerytFilter("") });
  if (ownerFilter.trim()) activeFilterChips.push({ key: "owner", label: `właściciel: ${ownerFilter.trim()}`, clear: () => setOwnerFilter("") });

  // Persist the active filters for the session so they survive a tab switch (see
  // loadParcelFilters). Cleared/default filters are stored too — the state is remembered
  // verbatim until a full page reload.
  useEffect(() => {
    saveParcelFilters(connection.id, {
      statusFilter,
      terytFilter,
      ownerFilter,
      mpzpFilter,
      kwFilter,
      usedOnly,
      accessCats: [...accessCats],
      sort,
    });
  }, [connection.id, statusFilter, terytFilter, ownerFilter, mpzpFilter, kwFilter, usedOnly, accessCats, sort]);

  // Działki with geometry but no ownership code — what the bulk fetch can fill.
  const missingOwnership = useMemo(
    () => (parcels ?? []).filter(
      (p) => (p.in_current_route || p.is_manual) && !p.ownership_code && !!p.geometry,
    ).length,
    [parcels],
  );

  const runBulkOwnership = async () => {
    const r = await fetchOwnershipCodes(connection.id);
    setOwnTaskId(r.task_id);
  };

  // Działki with a number but missing part of their administrative location — what the
  // bulk ULDK re-fetch can fill (geometry not required; resolved by TERYT).
  const missingAdmin = useMemo(
    () => (parcels ?? []).filter(
      (p) => (p.in_current_route || p.is_manual) && !!p.teryt
        && (!p.voivodeship || !p.county || !p.commune),
    ).length,
    [parcels],
  );

  const runBulkAdmin = async () => {
    const r = await fetchAdminData(connection.id);
    setAdmTaskId(r.task_id);
  };

  // Działki z geometrią, bez klasoużytku z geoportalu — co może uzupełnić masowy odczyt KIEG.
  const missingLandClass = useMemo(
    () => (parcels ?? []).filter(
      (p) => (p.in_current_route || p.is_manual) && !p.geoportal_land_use && !!p.geometry,
    ).length,
    [parcels],
  );

  const runBulkLandClass = async () => {
    const r = await fetchLandClasses(connection.id);
    setLcTaskId(r.task_id);
  };

  // Działki z obiektem naziemnym, dla których werdykt odrolnienia wciąż opiera się na
  // wykazie całej działki — czyli te, które odczyt punktowy może dopiero rozstrzygnąć.
  const undecidedUnderDevice = useMemo(
    () => (parcels ?? []).filter((p) => {
      if (!(p.in_current_route || p.is_manual)) return false;
      const method = p.odrolnienie?.source?.method;
      return method === "wypis" || method === "geoportal_list" || method === "none";
    }).length,
    [parcels],
  );

  const runLandClassUnderDevices = async () => {
    const r = await checkLandClassUnderDevices(connection.id);
    setLcuTaskId(r.task_id);
  };

  // Tytuły pozyskane na innej wersji trasy niż bieżąca (⚠ na liście) — kandydaci do
  // porównania przebiegu z aktualną trasą (backend title_reconcile, tolerancja 0,7 m).
  const staleTitles = useMemo(
    () => (parcels ?? []).reduce((n, p) => n + (p.access_summary?.stale ?? 0), 0),
    [parcels],
  );
  const reconcileAll = pm.reconcileAllTitles.data;
  // Rozbicie werdyktów „zmiana" / „bez danych" po powodzie — po „Porównaj wszystkie" ma
  // być widać, CO stoi na przeszkodzie (np. stara wersja bez warstw typowanych), a nie
  // sama liczba. Powody wskazujące wersję grupują się po zdaniu z nazwą wersji.
  const reconcileBreakdown = useMemo(() => {
    if (!reconcileAll) return [];
    const labels: Record<string, string> = {
      route: "zmiana przebiegu trasy",
      line_types: "inny typ linii",
      objects: "inne obiekty",
      left: "trasa zeszła z działki",
      no_version: "tytuł bez wersji trasy",
      no_parcel_geometry: "działka bez geometrii",
    };
    const groups = new Map<string, { titles: number; parcels: Set<string> }>();
    for (const p of reconcileAll.parcels) {
      for (const t of p.titles) {
        if (t.verdict === "extended") continue;
        const key = labels[t.reason] ?? t.detail ?? t.reason;
        const g = groups.get(key) ?? { titles: 0, parcels: new Set<string>() };
        g.titles += 1;
        g.parcels.add(p.parcel);
        groups.set(key, g);
      }
    }
    return [...groups.entries()]
      .map(([label, g]) => ({ label, titles: g.titles, parcels: g.parcels.size }))
      .sort((a, b) => b.titles - a.titles);
  }, [reconcileAll]);

  // Działki z kodem własności instytucji (JST / Skarb Państwa) bez przypisanego
  // urzędu — kandydatki do automatycznego przydziału. Mirrors backend
  // jst_assign.build_proposals (grupy 1/2, 4/5, 11/12, 13/14) wraz z regułą użytkowania
  // wieczystego (PERPETUAL_USUFRUCT_GROUPS): przy grupie 2/5/12/14 działka czeka na
  // urząd-WŁAŚCICIELA gruntu także wtedy, gdy ma już przypisany urząd użytkownika
  // wieczystego — pismo idzie do jednego i drugiego. Urząd-właściciela poznajemy po
  // rodzaju rekordu (LAND_OWNER_OFFICE_KINDS), jak backendowe _has_office_for.
  const jstEligible = useMemo(
    () => (parcels ?? []).filter((p) => {
      if (!(p.in_current_route || p.is_manual)) return false;
      const main = (p.ownership_group || p.ownership_code || "").trim().split(".")[0];
      const ownerKinds = LAND_OWNER_OFFICE_KINDS[main];
      if (!ownerKinds) return false;
      if (isPerpetualUsufructCode(main)) {
        return !p.owners.some((o) => !!o.office_kind && ownerKinds.has(o.office_kind));
      }
      return !p.owners.some((o) => o.office);
    }).length,
    [parcels],
  );

  return (
    <div>
      {/* Rada: które działki TEGO projektu wymagają wypisu i wyrysu z MPZP. Milczy,
          gdy nie ma czego doradzić. */}
      <MpzpAdvicePanel connectionId={connection.id} />

      {/* Rozbieżności właścicieli KW vs wypis — porównanie liczone jest od dawna,
          ale widać je było wyłącznie w oknie pojedynczej księgi. */}
      <OwnerMismatchPanel connectionId={connection.id} />

      {/* ── Opcje: trzy nazwane grupy — dodawanie / uzupełnianie danych / eksport ── */}
      <div className="card mb-3 flex flex-wrap items-stretch">
        <ToolbarGroup label="Dodawanie">
          <Button size="sm" variant={creating ? "ghost" : "secondary"} onClick={() => setCreating((c) => !c)}>
            {creating ? "Anuluj" : "+ Działka ręcznie"}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setBulkCsvOpen(true)}
            title="Wgraj jeden CSV z kolumną Nr działki — właściciele trafią do wszystkich pasujących działek"
          >
            <FileUp size={13} /> Właściciele z CSV
          </Button>
        </ToolbarGroup>
        <ToolbarGroup label="Uzupełnianie danych">
          {(missingOwnership > 0 || ownBusy) && (
            <Button
              size="sm"
              variant="secondary"
              loading={ownBusy}
              disabled={ownBusy}
              onClick={runBulkOwnership}
              title="Pobierz z geoportalu kody własności (grupa rejestrowa) dla działek bez kodu"
            >
              {ownBusy ? "Pobieranie kodów…" : `Pobierz brakujące kody wł. (${missingOwnership})`}
            </Button>
          )}
          {(missingAdmin > 0 || admBusy) && (
            <Button
              size="sm"
              variant="secondary"
              loading={admBusy}
              disabled={admBusy}
              onClick={runBulkAdmin}
              title="Odczytaj ponownie z ULDK dane administracyjne (województwo, powiat, gmina, obręb) dla działek bez kompletu"
            >
              {admBusy ? "Pobieranie danych adm.…" : `Pobierz brakujące dane adm. (${missingAdmin})`}
            </Button>
          )}
          {(missingLandClass > 0 || lcBusy) && (
            <Button
              size="sm"
              variant="secondary"
              loading={lcBusy}
              disabled={lcBusy}
              onClick={runBulkLandClass}
              title="Odczytaj z i-geoportalu (KIEG) klasoużytek (użytek + klasa bonitacyjna) dla działek bez tej danej. Pokrycie częściowe — nie każdy powiat udostępnia warstwę klasoużytków."
            >
              {lcBusy ? "Pobieranie klasoużytków…" : `Klasoużytki z geoportalu (${missingLandClass})`}
            </Button>
          )}
          {(undecidedUnderDevice > 0 || lcuBusy) && (
            <Button
              size="sm"
              variant="secondary"
              loading={lcuBusy}
              disabled={lcuBusy}
              onClick={runLandClassUnderDevices}
              title="Ustal klasoużytek POD trafo/złączem/słupem, zamiast opierać werdykt na wykazie całej działki. Tam, gdzie powiat podaje oznaczenie konturu — odczyt punktowy z jego usługi; gdzie nie podaje — odczyt z rysunku mapy konturów (AI) z wycinkami do wglądu."
            >
              {lcuBusy ? "Sprawdzanie klasy pod urządzeniami…" : `Klasa pod urządzeniami (${undecidedUnderDevice})`}
            </Button>
          )}
          {jstEligible > 0 && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setJstOpen(true)}
              title="Zaproponuj urzędy (JST / GDDKiA / KOWR) po kodzie własności — propozycja do zatwierdzenia; brakujące urzędy można dociągnąć z internetu (AI)"
            >
              Przydziel urzędy (JST) ({jstEligible})
            </Button>
          )}
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setRatesOpen(true)}
            title="Ile gruntu zajmuje trwale słup, złącze, stacja — z tego liczy się powierzchnia do wyłączenia z produkcji rolniczej. Zmienia dyrektor techniczny."
          >
            Stawki powierzchni
          </Button>
        </ToolbarGroup>
        {/* Tytuły pozyskane na innej wersji trasy: porównanie przebiegu na działce z bieżącą
            wersją (0,7 m) — bez zmiany przebiegu wracają same, reszta zostaje do decyzji. */}
        <ToolbarGroup label="Tytuły dostępu">
          {/* title na opakowaniu — wyszarzony <button> nie pokazuje własnego tooltipa w Firefoksie */}
          <span
            className="inline-flex"
            title={
              "Dla każdej działki z tytułem pozyskanym na innej wersji trasy porównaj przebieg na działce (odcinek, typy linii i obiekty) z bieżącą wersją. Różnica do 0,7 m przy tych samych typach linii i obiektów = ten sam przebieg, tytuł wraca na bieżącą wersję; większa albo brak zapisanego przebiegu = tytuł zostaje do decyzji." +
              (staleTitles === 0 ? " NIEDOSTĘPNA (wszystkie pozyskane tytuły są na aktualnej wersji trasy — nie ma czego porównywać)" : "")
            }
          >
            <Button
              size="sm"
              variant="secondary"
              loading={pm.reconcileAllTitles.isPending}
              disabled={pm.reconcileAllTitles.isPending || staleTitles === 0}
              onClick={() => pm.reconcileAllTitles.mutate()}
            >
              Porównaj tytuły z aktualną trasą ({staleTitles})
            </Button>
          </span>
          {reconcileAll && (
            <div className="basis-full text-[10px] font-mono text-[var(--color-text-muted)]">
              <div>
                wersja {reconcileAll.current_version ?? "—"}: rozciągnięto {reconcileAll.titles_extended}, zmiana
                przebiegu/typu {reconcileAll.titles_changed} (do decyzji), bez danych {reconcileAll.titles_unverifiable}
              </div>
              {reconcileBreakdown.length > 0 && (
                <ul className="mt-0.5 pl-3 list-disc">
                  {reconcileBreakdown.map((r) => (
                    <li key={r.label}>
                      {r.label} — {r.titles} {r.titles === 1 ? "tytuł" : "tytułów"} ({r.parcels}{" "}
                      {r.parcels === 1 ? "działka" : "działek"})
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {pm.reconcileAllTitles.isError && (
            <span className="text-[10px] font-mono text-red-600">{apiErr(pm.reconcileAllTitles.error)}</span>
          )}
        </ToolbarGroup>
        {/* Eksport dokładnie tego, co widać po filtrach — te same opcje co strona „Działki". */}
        <ToolbarGroup label="Eksport — widoczne wiersze">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => startExport("default")}
            disabled={exporting || sortedRows.length === 0}
            loading={exporting}
            title="Pobierz widoczne (odfiltrowane) działki jako arkusz Excel — plik tworzy się w tle"
          >
            <FileSpreadsheet size={13} /> Działki (.xlsx)
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => startExport("default", "csv")}
            disabled={exporting || sortedRows.length === 0}
            loading={exporting}
            title="Te same kolumny jako CSV (średniki, UTF-8 z BOM — otwiera się poprawnie w Excelu)"
          >
            <FileSpreadsheet size={13} /> Działki (.csv)
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => startExport("address")}
            disabled={exporting || sortedRows.length === 0}
            loading={exporting}
            title="Wykaz działek z adresami: Identyfikator (TERYT), Obręb ewidencyjny, Numer działki ewidencyjnej, Województwo, Powiat, Gmina, Miejscowość, Ulica, Nr domu, Kod pocztowy"
          >
            <FileSpreadsheet size={13} /> Wykaz adresowy (.xlsx)
          </Button>
        </ToolbarGroup>
        {canSubmit && (
          <div className="ml-auto flex items-center self-center px-3 py-2">
            <Link
              to={`${location.pathname}?tab=wpw`}
              className="inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase tracking-wider text-[var(--color-primary)] hover:underline"
              title="Pakiety per działka + zapotrzebowanie WPW tworzy się w zakładce WPW"
            >
              Zgody prywatne (WPW) → zakładka WPW <ChevronRight size={12} />
            </Link>
          </div>
        )}
      </div>
      {creating && <ParcelCreateForm connectionId={connection.id} onDone={() => setCreating(false)} />}

      {/* Wejścia do masowej generacji pakietów WPW — te same kafelki co w zakładce WPW. */}
      <WpwMassLinks connectionId={connection.id} projectId={connection.project} />

      {/* ── Filtry: zwijany pasek; po rozwinięciu siatka na całą szerokość; „Wyczyść”
          w osobnej stopce, żeby nie mylił się z polami filtrów ── */}
      <div className="card mb-3">
        <div className="flex flex-wrap items-center gap-2 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2">
          <button
            type="button"
            onClick={() => setFiltersOpen(!filtersOpen)}
            className="inline-flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-primary)] hover:text-[var(--color-primary)] transition-colors"
            title={filtersOpen ? "Zwiń panel filtrów" : "Rozwiń panel filtrów"}
          >
            <Filter size={13} /> Filtry {filtersOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
          {activeFilterChips.length > 0 && (
            <>
              <span className="section-label">aktywne:</span>
              {activeFilterChips.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={c.clear}
                  title="Zdejmij ten filtr"
                  className="inline-flex items-center gap-1 border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-1.5 py-px font-mono text-[9.5px] uppercase tracking-wide text-[var(--color-text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-colors"
                >
                  {c.label} <X size={9} />
                </button>
              ))}
            </>
          )}
          <span className="ml-auto section-label">
            pokazano <b className="text-[var(--color-text-primary)]">{sortedRows.length}</b> z {baseRows.length} działek
          </span>
        </div>
        {filtersOpen && (
          <>
            <div className="grid grid-cols-2 gap-3 p-3 md:grid-cols-3 xl:grid-cols-6">
              <div className="block xl:col-span-2">
                <span className="section-label">Tryb dostępu</span>
                <div className="mt-1 flex h-8 flex-wrap items-center gap-3 border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3">
                  {ACCESS_CATS.map((c) => (
                    <label key={c.key} className="flex items-center gap-1.5 text-xs whitespace-nowrap cursor-pointer select-none">
                      <input
                        type="checkbox"
                        className="accent-[var(--color-primary)] cursor-pointer"
                        checked={accessCats.has(c.key)}
                        onChange={() => toggleAccessCat(c.key)}
                      />
                      {c.label}
                    </label>
                  ))}
                </div>
              </div>
              <div
                className="block"
                title="Działki, przez które faktycznie przebiega aktualna trasa albo stoi na nich obiekt — bez działek zamówionych w wypisach „na zapas”"
              >
                <span className="section-label">Wykorzystanie</span>
                <div className="mt-1 flex h-8 items-center border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3">
                  <label className="flex items-center gap-1.5 text-xs whitespace-nowrap cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="accent-[var(--color-primary)] cursor-pointer"
                      checked={usedOnly}
                      onChange={() => setUsedOnly((v) => !v)}
                    />
                    tylko wykorzystywane
                  </label>
                </div>
              </div>
              <label className="block">
                <span className="section-label">Status działki</span>
                <select className="input-base text-xs py-1.5 mt-1 w-full" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="">Wszystkie statusy</option>
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                </select>
              </label>
              <label
                className="block"
                title="Zamówiony wypis i wyrys z MPZP — działki z podpiętym nieanulowanym zamówieniem albo bez żadnego"
              >
                <span className="section-label">Wypis MPZP</span>
                <select className="input-base text-xs py-1.5 mt-1 w-full" value={mpzpFilter} onChange={(e) => setMpzpFilter(e.target.value)}>
                  <option value="">Wszystkie</option>
                  <option value="with">Z podpiętym wypisem MPZP</option>
                  <option value="without">Bez wypisu MPZP</option>
                </select>
              </label>
              <label className="block" title="Analiza księgi wieczystej (AI): wykonana albo jeszcze nie">
                <span className="section-label">Analiza KW</span>
                <select className="input-base text-xs py-1.5 mt-1 w-full" value={kwFilter} onChange={(e) => setKwFilter(e.target.value)}>
                  <option value="">Wszystkie</option>
                  <option value="analyzed">Analiza wykonana</option>
                  <option value="not_analyzed">Bez analizy</option>
                </select>
              </label>
              <label className="block xl:col-span-2">
                <span className="section-label">Nr działki</span>
                <input
                  className="input-base text-xs py-1.5 mt-1 w-full"
                  placeholder="np. 123/4"
                  value={terytFilter}
                  onChange={(e) => setTerytFilter(e.target.value)}
                />
              </label>
              <label className="block xl:col-span-2">
                <span className="section-label">Właściciel</span>
                <input
                  className="input-base text-xs py-1.5 mt-1 w-full"
                  placeholder="imię / nazwisko / nazwa"
                  value={ownerFilter}
                  onChange={(e) => setOwnerFilter(e.target.value)}
                />
              </label>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2">
              <span className="text-[10px] font-mono text-[var(--color-text-muted)]">
                Filtry i sortowanie zapamiętują się dla tego projektu na czas sesji.
              </span>
              <Button
                size="sm"
                variant="danger"
                onClick={clearFilters}
                disabled={!anyFilter}
                title="Przywróć domyślne filtry (tylko pozyskanie, tylko wykorzystywane w projekcie)"
              >
                <X size={12} /> Wyczyść filtry
              </Button>
            </div>
          </>
        )}
      </div>
      {/* ── stan eksportu ── */}
      {(exporting || exportError || exportStatus === "failure" || exportResult) && (
        <div className="mb-3 text-[11px] font-mono">
          {exportError || exportStatus === "failure" ? (
            <span className="text-[var(--color-primary)]">
              {exportError ?? "Eksport nieudany — spróbuj ponownie."}
            </span>
          ) : exportResult ? (
            exportResult.file_url && (
              <a
                href={exportResult.file_url}
                download={exportResult.filename}
                className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline"
              >
                <Download size={11} /> Plik gotowy — pobieranie rozpoczęte. Pobierz ponownie (
                {exportResult.count} działek)
              </a>
            )
          ) : (
            <span className="text-[var(--color-text-muted)]">
              Tworzenie pliku w tle — pobieranie rozpocznie się automatycznie…
            </span>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : rows.length === 0 ? (
        <div className="card">
          <EmptyState>
            {anyFilter
              ? "Brak działek pasujących do filtrów."
              : "Brak działek — wgraj DXF (zakładka Mapa) lub dodaj ręcznie."}
          </EmptyState>
        </div>
      ) : (
        <TableShell>
          <thead>
            <tr>
              <SortableHeader label="Nr działki" sortKey="teryt" sort={sort} onToggle={toggleSort} />
              <SortableHeader label="Kod wł." sortKey="ownership_code" sort={sort} onToggle={toggleSort} className="w-14" />
              <Th className="w-36">Klasoużytek</Th>
              <Th className="w-32">
                <div className="flex flex-col gap-0.5">
                  <span className="font-mono text-[0.65rem] font-bold uppercase tracking-widest">Tytuły dostępu</span>
                  <span className="inline-flex items-center gap-2">
                    <MiniSortButton label="status" sortKey="access_status" sort={sort} onToggle={toggleSort} />
                    <MiniSortButton label="liczba" sortKey="access_count" sort={sort} onToggle={toggleSort} />
                  </span>
                </div>
              </Th>
              <SortableHeader label="Status" sortKey="status" sort={sort} onToggle={toggleSort} className="w-40" />
              <SortableHeader label="Analiza KW" sortKey="kw" sort={sort} onToggle={toggleSort} className="w-24 text-center" />
              <SortableHeader label="Właściciel" sortKey="owner" sort={sort} onToggle={toggleSort} style={{ minWidth: 200 }} />
              <Th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((p: Parcel) => (
              <tr key={p.id} className="group border-t border-[var(--color-border-muted)] hover:bg-[var(--color-bg-elevated)] cursor-pointer transition-colors" onClick={() => setSelected(p.id)}>
                <td className="px-3 py-2.5">
                  <span className="inline-flex items-center gap-2">
                    <span className="w-2 h-2 flex-shrink-0" style={{ backgroundColor: parcelDotColor(p.status) }} />
                    <span className="font-mono text-[var(--color-text-primary)]">{p.teryt}</span>
                  </span>
                  {p.is_connected_entity && (
                    <span
                      className="ml-1 text-[10px] text-violet-700 font-mono"
                      title="Działka podmiotu przyłączanego (nie tranzytowa)"
                    >
                      [przyłączany]
                    </span>
                  )}
                  {p.is_manual && <span className="ml-1 text-[10px] text-amber-600 font-mono">[ręczna]</span>}
                  {!p.in_current_route && <span className="ml-1 text-[10px] text-[var(--color-text-muted)] font-mono">[poza trasą]</span>}
                  {!p.geometry && <span className="ml-1 text-[10px] text-amber-600 font-mono">[bez geom.]</span>}
                  {p.linked_orders.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {p.linked_orders.map((l) => (
                        <Link
                          key={l.link_id}
                          to={`/orders/${l.order_id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-colors"
                          title={`Zamówienie ${l.order_number ?? l.order_id} — ${l.order_type_display ?? l.order_type}`}
                        >
                          <ExternalLink size={9} />
                          {l.order_number ?? l.order_id?.slice(0, 8)}
                          {l.order_type && <span className="opacity-60">· {ORDER_TYPE_LABEL[l.order_type] ?? l.order_type_display}</span>}
                        </Link>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2.5 font-mono text-[var(--color-text-secondary)]">{p.ownership_code || "—"}</td>
                <td className="px-3 py-2.5">
                  <ParcelLandClassCell
                    parcel={p}
                    onShowSource={setSourceParcel}
                    onSetOdrolnienie={(value) => pm.setOdrolnienie.mutate({ id: p.id, value })}
                    odrolnieniePending={pm.setOdrolnienie.isPending}
                  />
                </td>
                <td className="px-3 py-2.5 font-mono text-[var(--color-text-secondary)]">
                  <div className="flex flex-col gap-0.5">
                    <span
                      className="self-start text-[9px] uppercase tracking-wide px-1 py-px border border-[var(--color-border)] text-[var(--color-text-muted)]"
                      title={p.access_mode_display}
                    >
                      {PARCEL_ACCESS_MODE_BADGE[p.access_mode]}
                    </span>
                    {p.access_mode === "not_required" ? null : p.access_mode === "notification" ? (
                      <span title="Poinformowano (zwrotki potwierdzone)">poinformowano {p.owners_agreed}/{p.owners_total}</span>
                    ) : (
                      <>
                        <span className="inline-flex items-center gap-1.5">
                          {p.owners_agreed}/{p.owners_total}
                          {p.access_summary.stale > 0 && <span className="text-amber-600" title="Tytuły na nieaktualnej wersji trasy">⚠{p.access_summary.stale}</span>}
                        </span>
                        <ParcelSharesMark summary={p.access_summary} />
                      </>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    <ParcelStatusBadge status={p.status} />
                    <OpenCasesLink cases={p.open_proceedings} pathname={location.pathname} />
                    <ParcelUsageMark
                      parcel={p}
                      pending={pm.setUsed.isPending}
                      onSet={(value) => pm.setUsed.mutate({ id: p.id, value })}
                    />

                    {p.status_changed_at && (
                      <span className="text-[9px] font-mono text-[var(--color-text-muted)] tabular-nums" title="Data statusu — ostatnia zmiana (lub dodanie działki, gdy brak zmian)">
                        {fmtDate(p.status_changed_at)}
                      </span>
                    )}
                    {!p.status_locked ? null : <span className="text-[9px] font-mono text-[var(--color-text-muted)]" title="Status ustawiony ręcznie">✎</span>}
                    {p.route_review_needed && (
                      <span
                        className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide px-1 py-px border border-amber-300 bg-amber-50 text-amber-700"
                        title="Zmiana trasy na działce z postępem pozyskania — do weryfikacji"
                      >
                        <AlertTriangle size={9} /> zmiana trasy
                      </span>
                    )}
                    {p.owners.some((o) => o.wpw_title_proposal) && (
                      <span
                        className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide px-1 py-px border border-amber-400 bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                        title="Zwrot WPW zaakceptowany — tytuł prawny czeka na potwierdzenie. Otwórz działkę i kliknij „Potwierdź”."
                      >
                        <CheckCircle2 size={9} /> tytuł do potwierdzenia
                      </span>
                    )}
                    {p.owners.some((o) => (o.pending_agreements ?? []).length > 0) && (
                      <span
                        className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide px-1 py-px border border-amber-400 bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                        title="Podwykonawca dodał porozumienie w zamówieniu WPW. Zatwierdza się razem ze zgodą („Potwierdź → pozyskaj tytuł” albo „Zatwierdź i przenieś” w zamówieniu); otwórz działkę, żeby je obejrzeć; odrzucenie zgody w zamówieniu odrzuca też ten plik."
                      >
                        <FileSignature size={9} /> porozumienie do zatwierdzenia
                      </span>
                    )}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-center">
                  <KwAnalysisCell parcel={p} />
                </td>
                <td className="px-3 py-2">
                  {p.owners.length === 0 ? (
                    <div className="flex flex-wrap items-center gap-1">
                      <span className="text-[var(--color-text-muted)]">—</span>
                      {(p.access_mode === "consent" || p.access_mode === "notification") && !coveredParcelIds.has(p.id) && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide px-1 py-px border border-amber-300 bg-amber-50 text-amber-700" title="Brak wypisu z rejestru gruntów">
                          <AlertTriangle size={9} /> brak wypisu
                        </span>
                      )}
                      <ParcelWypisMarks
                        parcel={p}
                        wypisy={wypisByParcel.get(p.id) ?? []}
                        onPreview={() => setWypisPreviewParcel(p)}
                        onSetWypisNeeded={(value) => pm.setWypisNeeded.mutate({ id: p.id, value })}
                        pending={pm.setWypisNeeded.isPending}
                      />
                    </div>
                  ) : (
                    <div className="grid items-start gap-x-2 gap-y-0.5 text-[11px]"
                      style={{ gridTemplateColumns: "minmax(0,1fr) auto auto" }}>
                      {groupOwnersForDisplay(p.owners).map((g) =>
                        g.owners.map((o, i) => {
                          const h = wpwByOwner.get(o.id);
                          const flagged = h && WPW_FLAG_STATUSES.has(h.declared_status);
                          // Pasek przy nazwisku niesie STAN TYTUŁU DOSTĘPU tego właściciela
                          // (zielony = pozyskany, czerwony = odmowa/problem, …) — wspólność
                          // małżeńską widać dalej przy udziale („małżeństwo").
                          const mark = ownerAccessMark(o.access, o.in_field, o.in_open_case, o.in_open_office_case);
                          const markTitle = g.married
                            ? `${mark.label} · wspólność małżeńska`
                            : mark.label;
                          return (
                            <Fragment key={o.id}>
                              <span
                                className="flex items-stretch gap-1.5 text-[var(--color-text-secondary)] leading-snug cursor-help"
                                title={markTitle}
                              >
                                <span
                                  aria-hidden
                                  className="w-[3px] shrink-0 rounded-sm"
                                  style={{ background: mark.color }}
                                />
                                <span className="min-w-0">
                                  {o.office && (
                                    <span className="mr-1 text-[9px] font-mono uppercase px-1 py-px bg-[var(--color-bg-elevated)] border border-[var(--color-border)] text-[var(--color-text-muted)]">
                                      Urząd
                                    </span>
                                  )}
                                  {o.full_name}
                                  {o.own_share && (
                                    // Udział trzymany samodzielnie OBOK wspólności — obok
                                    // nazwiska, bo komórka udziału grupy niesie wspólność.
                                    <span
                                      className="ml-1 font-mono text-[var(--color-text-muted)] whitespace-nowrap cursor-help"
                                      title="Udział trzymany samodzielnie, obok udziału we wspólności małżeńskiej — jeden pakiet z oboma udziałami"
                                    >
                                      ({o.own_share} samodzielnie)
                                    </span>
                                  )}
                                </span>
                              </span>
                              {g.married ? (
                                // Joint marital share: one cell spanning the spouses' rows.
                                i === 0 && (
                                  <span
                                    className="font-mono whitespace-nowrap self-center text-right"
                                    style={{ gridRow: `span ${g.owners.length}` }}
                                    title="Wspólność małżeńska według wypisu — wspólny udział małżonków"
                                  >
                                    <span className="text-rose-700 dark:text-rose-400 font-semibold">
                                      {o.ownership_share ? `(${o.ownership_share})` : ""}
                                    </span>
                                    <span className="block text-[8px] uppercase tracking-wide text-rose-600 dark:text-rose-400">
                                      małżeństwo
                                    </span>
                                  </span>
                                )
                              ) : (
                                <span className="text-[var(--color-text-muted)] font-mono whitespace-nowrap self-start pt-px">
                                  {o.ownership_share ? `(${o.ownership_share})` : ""}
                                </span>
                              )}
                              <span className="self-start whitespace-nowrap">
                                {flagged && (
                                  <button type="button" title="WPW — podgląd statusu i historii rozmowy"
                                    className="hover:opacity-80"
                                    onClick={(e) => { e.stopPropagation(); setWpwConv({ ownerId: o.id, label: `WPW — ${o.full_name}` }); }}>
                                    <WpwStatusBadge status={h!.declared_status} />
                                  </button>
                                )}
                              </span>
                            </Fragment>
                          );
                        }),
                      )}
                      <div style={{ gridColumn: "1 / -1" }} className="mt-1 empty:mt-0">
                        <ParcelWypisMarks
                          parcel={p}
                          wypisy={wypisByParcel.get(p.id) ?? []}
                          onPreview={() => setWypisPreviewParcel(p)}
                          onSetWypisNeeded={(value) => pm.setWypisNeeded.mutate({ id: p.id, value })}
                          pending={pm.setWypisNeeded.isPending}
                        />
                      </div>
                    </div>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => confirmParcelDelete(p) && pm.remove.mutate(p.id)}
                    disabled={pm.remove.isPending}
                    title="Usuń działkę"
                    className="inline-flex text-[var(--color-text-muted)] hover:text-[var(--color-primary)] disabled:opacity-50"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </TableShell>
      )}

      <WypisySection
        parcels={parcels ?? []}
        wypisy={wypisy ?? []}
        mutations={wm}
      />

      {selected && (
        <ParcelDrawer
          key={deepLinkSeq}
          parcelId={selected}
          connectionId={connection.id}
          projectId={connection.project}
          operator={connection.operator}
          initialTab={drawerTab}
          onClose={() => {
            setSelected(null);
            setDrawerTab(undefined);
          }}
        />
      )}

      {wpwConv && (
        <WpwConversationModal title={wpwConv.label} ownerId={wpwConv.ownerId} onClose={() => setWpwConv(null)} />
      )}

      {wypisPreviewParcel && (
        <WypisPreviewModal
          parcel={wypisPreviewParcel}
          wypisy={wypisy ?? []}
          mutations={wm}
          onClose={() => setWypisPreviewParcel(null)}
        />
      )}

      {bulkCsvOpen && (
        <OwnerCsvBulkImportModal
          connectionId={connection.id}
          onClose={() => setBulkCsvOpen(false)}
        />
      )}

      {ratesOpen && (
        <Modal open onClose={() => setRatesOpen(false)} title="Stawki powierzchni urządzeń" size="lg">
          <DeviceFootprintRatesPanel />
        </Modal>
      )}
      {sourceParcel && (
        <LandClassSourceModal
          parcel={sourceParcel}
          connectionId={connection.id}
          onClose={() => setSourceParcel(null)}
        />
      )}
      {jstOpen && (
        <JstAssignModal connectionId={connection.id} onClose={() => setJstOpen(false)} />
      )}
    </div>
  );
}
